import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fabric } from 'fabric';
import { useParams, useNavigate } from 'react-router-dom';
import carouselTemplates from '../../data/carouselTemplates';
import aiBackgroundRemover from '../../utils/aiBackgroundRemoval';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Box,
  Paper,
  Button,
  ButtonGroup,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Slider,
  Alert,
  Snackbar,
  styled,
  Collapse,
  Menu,
  CircularProgress
} from '@mui/material';
import CarouselToolbar from './CarouselToolbar';
import CarouselLeftSidebar from './CarouselLeftSidebar';
import CarouselRightProperties from './CarouselRightProperties';
import SaveTemplateDialog from './SaveTemplateDialog';
import {
  TextFields as TextIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  GroupWork as GroupIcon,
  CallSplit as UngroupIcon,
  Circle as CircleIcon,
  Stop as RectangleIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Layers as LayersIcon,
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  ContentCopy as DuplicateIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  FormatAlignLeft as AlignHorizontalLeft,
  FormatAlignCenter as AlignHorizontalCenter,
  FormatAlignRight as AlignHorizontalRight,
  VerticalAlignTop as AlignVerticalTop,
  VerticalAlignCenter as AlignVerticalCenter,
  VerticalAlignBottom as AlignVerticalBottom,
  ZoomIn,
  ZoomOut,
  ZoomOutMap,
  Palette,
  GridOn,
  AutoFixHigh,
  Settings,
  Add as AddIcon,
  KeyboardArrowDown,
  ExpandLess,
  ExpandMore,
  Category as ShapesIcon,
  Wallpaper as BackgroundIcon,
  PhotoLibrary as PhotosIcon,
  Dashboard as ElementsIcon,
  Star as StarIcon,
  Remove as LineIcon,
  ArrowForward,
  ArrowForward as ArrowIcon,
  ChangeHistory as PolygonIcon,
  PanoramaFishEye as EllipseIcon,
  PieChart as WedgeIcon,
  Timeline as ArcIcon,
  RadioButtonUnchecked as RingIcon,
  Upload as UploadIcon,
  FontSize as FontSizeIcon,
  Title as HeadingIcon,
  FormatAlignLeft as ParagraphIcon,
  Close as CloseIcon,
  Help as HelpIcon,
  Keyboard as KeyboardIcon,
  EmojiEmotions as EmojiIcon,
  Category as CategoryIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  VerticalAlignTop as BringToFrontIcon,
  VerticalAlignBottom as SendToBackIcon,
  ArrowUpward as BringForwardIcon,
  ArrowDownward as SendBackwardIcon
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import InstationLogo from '../layout/Instation-complete.svg';
import PexelsSearch from './panels/PexelsSearch';
import FontLoader from './FontLoader';
// 1. Add new icons for placeholders (if not already imported)
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';

// Social Media Image dimensions
const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1350;
const DEFAULT_ZOOM = 70; // Default zoom level

// Carousel specific constants
const PAGE_WIDTH = 1080;
const PAGE_HEIGHT = 1350;
const PAGE_SEPARATOR_WIDTH = 2; // Width of the page separator line

// Canvas sizes configuration
const CANVAS_SIZES = [
  { id: 'portrait', name: 'LinkedIn Portrait', width: 1080, height: 1350 },
  { id: 'square', name: 'LinkedIn Square', width: 1080, height: 1080 }
];

// Styled components moved to individual component files

// Template constants moved to CarouselLeftSidebar component









// Gradient and brand constants moved to CarouselLeftSidebar component

// Color and font constants moved to individual components

// GradientEditor component moved to CarouselLeftSidebar component

// Add isGradient check function
const isGradient = (fill) => {
  return typeof fill === 'object' && fill !== null && fill.type && fill.colorStops;
};

// Helper function for color conversion
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : {r: 0, g: 0, b: 0};
};

const FabricCarouselDesigner = () => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const { templateId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [selectedObject, setSelectedObject] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [activeSection, setActiveSection] = useState('elements');
  const [expandedPanels, setExpandedPanels] = useState({});
  // Removed zoom state - using fixed display sizing instead
  const [showGrid, setShowGrid] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#3B82F6');
  const [canvasObjects, setCanvasObjects] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [documentName, setDocumentName] = useState('Carousel Design');
  const [background, setBackground] = useState('#FFFFFF'); // Using uppercase for consistency
  const [fontSizeInput, setFontSizeInput] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [activeElementTab, setActiveElementTab] = useState(0);
  const [showPexels, setShowPexels] = useState(false);
  const [activeBackgroundTab, setActiveBackgroundTab] = useState(0);
  const [selectedBrandAccent, setSelectedBrandAccent] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isTemplateLoaded, setIsTemplateLoaded] = useState(false);
  const [showProfilePicture, setShowProfilePicture] = useState(true);
  const profilePictureRef = useRef(null);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [canvasSize, setCanvasSize] = useState(CANVAS_SIZES[0]); // Default to square
  
  // AI Design state
  const [showAIDesignDialog, setShowAIDesignDialog] = useState(false);
  const [designPrompt, setDesignPrompt] = useState('');
  const [isGeneratingDesign, setIsGeneratingDesign] = useState(false);
  const [generatedDesign, setGeneratedDesign] = useState(null);
  const [aiMenuAnchorEl, setAiMenuAnchorEl] = useState(null);
  const [aiMenuTab, setAiMenuTab] = useState('design'); // Add tab state: 'design' or 'content'
  const [contentPrompt, setContentPrompt] = useState(''); // Add content prompt state
  const [isGeneratingContent, setIsGeneratingContent] = useState(false); // Add content generation loading state
  const [contentMode, setContentMode] = useState('topic'); // 'topic' or 'text'

  // Carousel specific state
  const [pageCount, setPageCount] = useState(1);
  const [currentCanvasWidth, setCurrentCanvasWidth] = useState(PAGE_WIDTH);
  const [pageSeparators, setPageSeparators] = useState([]);

  // Zoom state for better viewing
  const [zoom, setZoom] = useState(0.5); // 50% default for optimal viewing
  const [zoomLocked, setZoomLocked] = useState(false); // Zoom lock toggle

  // Template saving state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  
  // Copy/paste state
  const [copiedObject, setCopiedObject] = useState(null);

  // Function to normalize template data while preserving EXACT coordinates
  const cleanupTemplateData = (templateData) => {
    if (!templateData || !templateData.pages) return templateData;
    
    const cleanedPages = [];
    
    templateData.pages.forEach((page, pageIndex) => {
      const cleanedElements = page.elements
        .map(element => ({
          ...element,
          // Only normalize element types, preserve ALL coordinates exactly
          type: element.type === 'rect' ? 'rectangle' : element.type
          // DO NOT modify x, y coordinates - preserve exactly as user created
        }));
      
      // Include ALL pages with elements, regardless of coordinate values
      if (cleanedElements.length > 0) {
        cleanedPages.push({
          ...page,
          id: `${cleanedPages.length + 1}`,
          name: `Page ${cleanedPages.length + 1}`,
          elements: cleanedElements
        });
      }
    });
    
    return {
      ...templateData,
      pages: cleanedPages
    };
  };

  // Function to restore proper layer order after all elements are loaded
  const restoreLayerOrder = useCallback((canvas, sortedElements) => {
    console.log('🎭 Restoring layer order for', sortedElements.length, 'elements...');
    
    // Get all objects currently on canvas
    const canvasObjects = canvas.getObjects().filter(obj => 
      !obj.id?.includes('separator') && !obj.id?.includes('page_border')
    );
    
    // Create a map of element IDs to their desired z-index
    const zIndexMap = {};
    sortedElements.forEach((element, index) => {
      zIndexMap[element.id] = index;
    });
    
    // Sort canvas objects by their desired z-index
    const objectsToReorder = canvasObjects
      .filter(obj => obj.id && zIndexMap.hasOwnProperty(obj.id))
      .sort((a, b) => (zIndexMap[a.id] || 0) - (zIndexMap[b.id] || 0));
    
    console.log('🔧 Reordering objects:', objectsToReorder.map(obj => ({ id: obj.id, type: obj.type, desiredIndex: zIndexMap[obj.id] })));
    
    // Move each object to its correct position in the stack
    objectsToReorder.forEach((obj, targetIndex) => {
      const currentIndex = canvas.getObjects().indexOf(obj);
      if (currentIndex !== targetIndex) {
        // Move object to correct position
        canvas.moveTo(obj, targetIndex);
        console.log(`📍 Moved ${obj.id} from index ${currentIndex} to ${targetIndex}`);
      }
    });
    
    canvas.renderAll();
    console.log('✅ Layer order restored successfully!');
  }, []);

  // Function to load template elements into the canvas
  const loadTemplateElement = useCallback((canvas, element) => {
    let fabricObject = null;

    // Helper function to convert gradient fills
    const convertFill = (fill, element) => {
      if (typeof fill === 'string') {
        return fill; // Simple color
      }
      
      if (fill && typeof fill === 'object' && fill.type) {
        // Convert gradient object to Fabric.js gradient with proper coordinate transformation
        const { type, coords, colorStops } = fill;
        
        // Transform absolute canvas coordinates to relative element coordinates
        const elementLeft = element.x || 0;
        const pageOffset = Math.floor(elementLeft / 1080) * 1080;
        
        if (type === 'linear') {
          // Transform linear gradient coordinates to be relative to the element
          const transformedCoords = {
            x1: (coords.x1 || 0) - pageOffset,
            y1: coords.y1 || 0,
            x2: (coords.x2 || element.width || 1080) - pageOffset,
            y2: coords.y2 || element.height || 1080
          };
          
          console.log(`🎨 Canvas gradient: transforming coords from`, coords, `to`, transformedCoords, `(pageOffset: ${pageOffset})`);
          
          return new fabric.Gradient({
            type: 'linear',
            coords: transformedCoords,
            colorStops: colorStops
          });
        } else if (type === 'radial') {
          // Transform radial gradient coordinates
          const transformedCoords = {
            x1: (coords.x1 || element.width/2) - pageOffset,
            y1: coords.y1 || element.height/2,
            x2: (coords.x2 || element.width/2) - pageOffset,  
            y2: coords.y2 || element.height/2,
            r1: coords.r1 || 0,
            r2: coords.r2 || Math.max(element.width || 1080, element.height || 1080)/2
          };
          
          console.log(`🎨 Canvas radial gradient: transforming coords from`, coords, `to`, transformedCoords);
          
          return new fabric.Gradient({
            type: 'radial',
            coords: transformedCoords,
            colorStops: colorStops
          });
        }
      }
      
      return fill; // Fallback
    };

    switch (element.type) {
      case 'rectangle':
      case 'rect': // Handle both rectangle and rect for backwards compatibility
        fabricObject = new fabric.Rect({
          left: element.x,
          top: element.y,
          width: element.width,
          height: element.height,
          fill: convertFill(element.fill, element),
          stroke: element.stroke,
          strokeWidth: element.strokeWidth,
          // RESTORE ALL TRANSFORMATION PROPERTIES
          opacity: element.opacity || 1,
          scaleX: element.scaleX || 1,
          scaleY: element.scaleY || 1,
          angle: element.angle || 0,
          skewX: element.skewX || 0,
          skewY: element.skewY || 0,
          draggable: element.draggable !== false,
          rx: element.rx || 0,
          ry: element.ry || 0,
          id: element.id
        });
        break;

      case 'circle':
        fabricObject = new fabric.Circle({
          left: element.x - (element.radius || 50),
          top: element.y - (element.radius || 50),
          radius: element.radius || 50,
          fill: convertFill(element.fill, element),
          stroke: element.stroke,
          strokeWidth: element.strokeWidth,
          // RESTORE ALL TRANSFORMATION PROPERTIES
          opacity: element.opacity || 1,
          scaleX: element.scaleX || 1,
          scaleY: element.scaleY || 1,
          angle: element.angle || 0,
          skewX: element.skewX || 0,
          skewY: element.skewY || 0,
          draggable: element.draggable !== false,
          id: element.id
        });
        break;

      case 'line':
        // Skip separator lines as they're handled separately
        if (element.id && element.id.includes('separator')) {
          console.log('⏭️ Skipping separator element:', element.id);
          return null;
        }
        
        fabricObject = new fabric.Line(element.points || [element.x, element.y, element.x + 100, element.y], {
          stroke: element.stroke || '#000000',
          strokeWidth: element.strokeWidth || 2,
          draggable: element.draggable !== false,
          id: element.id
        });
        break;

      case 'polygon':
        fabricObject = new fabric.Polygon(element.points, {
          left: element.x,
          top: element.y,
          fill: convertFill(element.fill),
          opacity: element.opacity || 1,
          draggable: element.draggable !== false,
          id: element.id
        });
        break;

      case 'heading':
        // For headings, use Textbox to match left panel behavior
        // Convert numeric font weights back to CSS keywords for Fabric.js
        let headingFontWeight = 'bold';
        if (element.fontWeight) {
          // Handle numeric weights
          if (typeof element.fontWeight === 'string' && !isNaN(element.fontWeight)) {
            const weightNum = parseInt(element.fontWeight, 10);
            if (weightNum >= 700) headingFontWeight = 'bold';
            else if (weightNum >= 500) headingFontWeight = '500';
            else headingFontWeight = 'normal';
          } else if (['bold', 'normal', '500', '600', '700'].includes(element.fontWeight)) {
            headingFontWeight = element.fontWeight;
          }
        }
        
        fabricObject = new fabric.Textbox(element.text, {
          left: element.x,
          top: element.y,
          width: element.width || 400,
          fontSize: element.fontSize || 48,
          fontFamily: element.fontFamily || 'Poppins',
          fontWeight: headingFontWeight,
          fill: element.fill || '#000000',
          textAlign: element.align || 'left',
          originX: element.align === 'center' ? 'center' : 'left',
          originY: 'top',
          // RESTORE ALL TRANSFORMATION PROPERTIES
          opacity: element.opacity || 1,
          scaleX: element.scaleX || 1,
          scaleY: element.scaleY || 1,
          angle: element.angle || 0,
          skewX: element.skewX || 0,
          skewY: element.skewY || 0,
          splitByGrapheme: false, // Use word-based wrapping
          editable: true,
          id: element.id
        });
        break;



      case 'paragraph':
        // For paragraphs, use Textbox with proper width and line height
        // Convert numeric font weights back to CSS keywords for Fabric.js
        let paragraphFontWeight = 'normal';
        if (element.fontWeight) {
          // Handle numeric weights
          if (typeof element.fontWeight === 'string' && !isNaN(element.fontWeight)) {
            const weightNum = parseInt(element.fontWeight, 10);
            if (weightNum >= 700) paragraphFontWeight = 'bold';
            else if (weightNum >= 500) paragraphFontWeight = '500';
            else paragraphFontWeight = 'normal';
          } else if (['bold', 'normal', '500', '600', '700'].includes(element.fontWeight)) {
            paragraphFontWeight = element.fontWeight;
          }
        }
        
        fabricObject = new fabric.Textbox(element.text, {
          left: element.x,
          top: element.y,
          width: element.width || 800,
          fontSize: element.fontSize || 20,
          fontFamily: element.fontFamily || 'Poppins',
          fontWeight: paragraphFontWeight,
          fill: element.fill || '#000000',
          textAlign: element.align || 'left',
          originX: element.align === 'center' ? 'center' : 'left',
          originY: 'top',
          // RESTORE ALL TRANSFORMATION PROPERTIES
          opacity: element.opacity || 1,
          scaleX: element.scaleX || 1,
          scaleY: element.scaleY || 1,
          angle: element.angle || 0,
          skewX: element.skewX || 0,
          skewY: element.skewY || 0,
          lineHeight: element.lineHeight || 1.2,
          splitByGrapheme: false, // Ensure word-based wrapping
          editable: true,
          id: element.id
        });
        break;

      case 'image-placeholder':
        // Create image placeholder
        fabricObject = new fabric.Rect({
          left: element.x,
          top: element.y,
          width: element.width || 100,
          height: element.height || 100,
          fill: '#F3F4F6',
          stroke: '#D1D5DB',
          strokeWidth: 2,
          strokeDashArray: [5, 5],
          rx: element.cornerRadius || 0,
          ry: element.cornerRadius || 0,
          id: element.id,
          placeholderType: 'image',
          placeholderLabel: element.label || 'Image'
        });
        
        // Add placeholder text
        const placeholderText = new fabric.Text(element.label || '🖼️ Image', {
          left: element.x + (element.width || 100) / 2,
          top: element.y + (element.height || 100) / 2,
          fontSize: Math.min(16, (element.width || 100) / 8),
          fontFamily: 'Inter',
          fontWeight: '500',
          fill: '#9CA3AF',
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: false,
          id: `${element.id}-label`
        });
        
        canvas.add(placeholderText);
        break;

      case 'image':
        // Create image element - PRESERVE EXACT COORDINATES AND ALL PROPERTIES
        if (element.src) {
          fabricObject = new fabric.Image.fromURL(element.src, (img) => {
            img.set({
              left: element.x, // Use EXACT coordinates from template
              top: element.y,  // Use EXACT coordinates from template
              width: element.originalWidth || element.width,
              height: element.originalHeight || element.height,
              // RESTORE ALL TRANSFORMATION PROPERTIES
              scaleX: element.scaleX || 1,
              scaleY: element.scaleY || 1,
              angle: element.angle || 0,
              skewX: element.skewX || 0,
              skewY: element.skewY || 0,
              opacity: element.opacity || 1,
              selectable: element.draggable !== false,
              crossOrigin: 'anonymous',
              id: element.id
            });
            canvas.add(img);
            canvas.renderAll();
            console.log('🖼️ Loaded image element:', element.id, `at EXACT position (${element.x}, ${element.y}) with opacity:`, element.opacity, 'transforms:', { scaleX: element.scaleX, scaleY: element.scaleY, angle: element.angle });
            
            // Notify that async element has loaded
            if (element._onLoaded) {
              element._onLoaded();
            }
          }, {
            crossOrigin: 'anonymous'
          });
          return null; // Return null since we handle adding to canvas in the callback
        }
        break;

      case 'replacementGroup':
        // Restore a replaced placeholder group (contains an image with clipping) - PRESERVE EXACT COORDINATES
        if (element.imageSrc) {
          fabric.Image.fromURL(element.imageSrc, (img) => {
            img.set({
              scaleX: element.imageScaleX || 1,
              scaleY: element.imageScaleY || 1,
              originX: 'center',
              originY: 'center',
              left: element.imageLeft || 0,
              top: element.imageTop || 0,
            });

            let clipPath = null;
            if (element.clipPath) {
              if (element.clipPath.type === 'circle') {
                clipPath = new fabric.Circle({
                  radius: element.clipPath.radius,
                  originX: 'center',
                  originY: 'center'
                });
              } else if (element.clipPath.type === 'rect') {
                clipPath = new fabric.Rect({
                  width: element.clipPath.width,
                  height: element.clipPath.height,
                  originX: 'center',
                  originY: 'center'
                });
              }
            }

            const restoredGroup = new fabric.Group([img], {
              left: element.x, // Use EXACT coordinates from template
              top: element.y,  // Use EXACT coordinates from template
              originX: 'center',
              originY: 'center',
              clipPath: clipPath,
              // RESTORE ALL TRANSFORMATION PROPERTIES
              opacity: element.opacity || 1,
              scaleX: element.scaleX || 1,
              scaleY: element.scaleY || 1,
              angle: element.angle || 0,
              skewX: element.skewX || 0,
              skewY: element.skewY || 0,
              isPlaceholderReplacement: true,
              placeholderShape: element.placeholderShape,
              id: element.id
            });

            canvas.add(restoredGroup);
            canvas.renderAll();
            console.log('🖼️ Restored replacement group:', element.id, `at EXACT position (${element.x}, ${element.y})`);
            
            // Notify that async element has loaded
            if (element._onLoaded) {
              element._onLoaded();
            }
          }, {
            crossOrigin: 'anonymous'
          });
          return null; // Return null since we handle adding to canvas in the callback
        }
        break;

      case 'placeholderGroup':
        // Restore an original placeholder group structure
        const groupObjects = [];
        
        if (element.groupObjects && element.groupObjects.length > 0) {
          element.groupObjects.forEach(subObj => {
            let subElement = null;
            
            if (subObj.type === 'circle') {
              subElement = new fabric.Circle({
                radius: subObj.radius,
                originX: subObj.originX || 'center',
                originY: subObj.originY || 'center',
                left: subObj.left,
                top: subObj.top,
                fill: subObj.fill,
                stroke: subObj.stroke,
                strokeWidth: subObj.strokeWidth
              });
            } else if (subObj.type === 'text') {
              subElement = new fabric.Text(subObj.text, {
                fontSize: subObj.fontSize,
                originX: subObj.originX || 'center',
                originY: subObj.originY || 'center',
                left: subObj.left,
                top: subObj.top,
                fontFamily: subObj.fontFamily,
                fill: subObj.fill
              });
            } else if (subObj.type === 'rect') {
              subElement = new fabric.Rect({
                width: subObj.width,
                height: subObj.height,
                originX: subObj.originX || 'center',
                originY: subObj.originY || 'center',
                left: subObj.left,
                top: subObj.top,
                fill: subObj.fill,
                stroke: subObj.stroke,
                strokeWidth: subObj.strokeWidth
              });
            }
            
            if (subElement) {
              groupObjects.push(subElement);
            }
          });
        }
        
        if (groupObjects.length > 0) {
          fabricObject = new fabric.Group(groupObjects, {
            left: element.x,
            top: element.y,
            id: element.id,
            placeholderType: element.placeholderType,
            placeholderLabel: element.placeholderLabel
          });
          console.log('🔄 Restored placeholder group with', groupObjects.length, 'sub-objects:', element.id);
        } else {
          // Fallback: Create a placeholder rectangle if no groupObjects found
          console.warn('⚠️ PlaceholderGroup has no sub-objects, creating fallback placeholder:', element.id);
          fabricObject = new fabric.Rect({
            left: element.x,
            top: element.y,
            width: element.width || 100,
            height: element.height || 100,
            fill: 'transparent',
            stroke: '#D1D5DB',
            strokeWidth: 2,
            strokeDashArray: [5, 5],
            rx: 10,
            ry: 10,
            id: element.id,
            placeholderType: element.placeholderType || 'placeholder',
            placeholderLabel: element.placeholderLabel || 'Placeholder'
          });
        }
        break;

      case 'group':
        // Fallback for simple groups (legacy support)
        fabricObject = new fabric.Rect({
          left: element.x,
          top: element.y,
          width: element.width || 100,
          height: element.height || 100,
          fill: 'transparent',
          stroke: '#E5E7EB',
          strokeWidth: 1,
          strokeDashArray: [3, 3],
          rx: element.rx || 5,
          ry: element.ry || 5,
          id: element.id,
          groupType: true,
          opacity: element.opacity || 0.5
        });
        
        // Add a subtle label for group elements
        const groupLabel = new fabric.Text('Group', {
          left: element.x + (element.width || 100) / 2,
          top: element.y + (element.height || 100) / 2,
          fontSize: Math.min(12, (element.width || 100) / 10),
          fontFamily: 'Inter',
          fontWeight: '400',
          fill: '#9CA3AF',
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: false,
          id: `${element.id}-label`
        });
        
        canvas.add(groupLabel);
        break;

      case 'rect':
        // Handle 'rect' type (sometimes used instead of 'rectangle')
        // Skip page borders as they're handled separately
        if (element.id && element.id.includes('page_border')) {
          console.log('⏭️ Skipping page border element:', element.id);
          return null;
        }
        
        fabricObject = new fabric.Rect({
          left: element.x,
          top: element.y,
          width: element.width,
          height: element.height,
          fill: convertFill(element.fill, element),
          opacity: element.opacity || 1,
          draggable: element.draggable !== false,
          rx: element.rx || 0,
          ry: element.ry || 0,
          stroke: element.stroke,
          strokeWidth: element.strokeWidth || 0,
          id: element.id
        });
        break;

      default:
        console.log('🚨 Unknown element type:', element.type, element);
        // Try to handle unknown types gracefully
        if (element.src) {
          console.log('🔄 Attempting to load unknown type as image:', element.type);
          return loadTemplateElement(canvas, { ...element, type: 'image' });
        }
        return null;
    }

    if (fabricObject) {
      // Common object properties
      fabricObject.set({
        selectable: element.draggable !== false,
        hasControls: element.draggable !== false,
        lockMovementX: element.draggable === false,
        lockMovementY: element.draggable === false,
        hoverCursor: element.draggable === false ? 'default' : 'move'
      });

      canvas.add(fabricObject);
    }

    return fabricObject;
  }, []);

  // Navigation handler
  const handleBackToTemplates = () => {
    navigate('/carousel-templates');
  };



  // Profile picture feature removed - functionality disabled

  // Profile picture toggle removed

  // Replace placeholder with image - REWRITTEN FOR PAN/ZOOM/REPLACE
  const replacePlaceholder = useCallback((placeholderObject, imageUrl) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    fabric.Image.fromURL(imageUrl, (newImage) => {
        const isOriginalPlaceholderGroup = placeholderObject.type === 'group' && !placeholderObject.isPlaceholderReplacement;
        const isAlreadyReplacedGroup = placeholderObject.isPlaceholderReplacement;

        let groupToUpdate = isAlreadyReplacedGroup ? placeholderObject : null;
        let placeholderShape, radius, width, height, left, top;
        let id = placeholderObject.id || `placeholder_${Date.now()}`;

        if (isOriginalPlaceholderGroup) {
            const shape = placeholderObject._objects.find(o => o.type === 'circle' || o.type === 'rect');
            if (!shape) return; // Should not happen
            placeholderShape = shape.type;
            left = placeholderObject.left;
            top = placeholderObject.top;
            if (placeholderShape === 'circle') {
                radius = shape.radius * (placeholderObject.scaleX || 1);
            } else {
                width = shape.width * (placeholderObject.scaleX || 1);
                height = shape.height * (placeholderObject.scaleY || 1);
            }
        } else if (isAlreadyReplacedGroup) {
            placeholderShape = placeholderObject.placeholderShape;
            left = placeholderObject.left;
            top = placeholderObject.top;
            if (placeholderShape === 'circle') {
                radius = placeholderObject.clipPath.radius;
            } else {
                width = placeholderObject.getScaledWidth();
                height = placeholderObject.getScaledHeight();
            }
            const oldImage = placeholderObject.getObjects('image')[0];
            if (oldImage) {
                placeholderObject.remove(oldImage);
            }
        } else {
            return; // Not a valid placeholder object to replace
        }

        const targetWidth = placeholderShape === 'circle' ? radius * 2 : width;
        const targetHeight = placeholderShape === 'circle' ? radius * 2 : height;
        const scaleMode = placeholderShape === 'circle' ? 'cover' : 'contain';

        const scale = (scaleMode === 'cover')
            ? Math.max(targetWidth / newImage.width, targetHeight / newImage.height)
            : Math.min(targetWidth / newImage.width, targetHeight / newImage.height);

        newImage.set({
            scaleX: scale,
            scaleY: scale,
            originX: 'center',
            originY: 'center',
            left: 0, // Set initial position to 0,0 relative to group center
            top: 0,
        });

        if (groupToUpdate) {
            groupToUpdate.addWithUpdate(newImage);
            groupToUpdate.setCoords();
            canvas.renderAll();
        } else {
            let clipPath = null;
            if (placeholderShape === 'circle') {
                clipPath = new fabric.Circle({ radius, originX: 'center', originY: 'center' });
            }

            const newGroup = new fabric.Group([newImage], {
                left,
                top,
                originX: 'center',
                originY: 'center',
                clipPath,
                isPlaceholderReplacement: true,
                placeholderShape,
                id,
            });
            
            canvas.remove(placeholderObject);
            canvas.add(newGroup);
            canvas.setActiveObject(newGroup);
        }
        
        setSnackbar({ open: true, message: 'Image updated successfully!', severity: 'success' });

    }, { crossOrigin: 'anonymous' });
  }, []);

  // Handle file upload for placeholder replacement
  const handleImageUpload = useCallback((file, placeholderObject) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        replacePlaceholder(placeholderObject, e.target.result);
      };
      reader.readAsDataURL(file);
    }
  }, [replacePlaceholder]);

  // Remove background from image using client-side processing
  const removeBackground = useCallback(async (imageObject) => {
    if (!imageObject || imageObject.type !== 'image') {
      setSnackbar({ open: true, message: 'Please select an image first.', severity: 'warning' });
      return;
    }

    setIsRemovingBackground(true);
    
    try {
      // Get the image element
      const canvas = fabricCanvasRef.current;
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      // Set canvas size to match image
      tempCanvas.width = imageObject.width * imageObject.scaleX;
      tempCanvas.height = imageObject.height * imageObject.scaleY;
      
      // Draw the image on temporary canvas
      tempCtx.drawImage(
        imageObject.getElement(),
        0, 0,
        tempCanvas.width,
        tempCanvas.height
      );
      
      // Convert to blob for API processing
      tempCanvas.toBlob(async (blob) => {
        try {
          // Option 1: Using remove.bg API (requires API key)
          const removeWithAPI = async (imageBlob) => {
            const formData = new FormData();
            formData.append('image_file', imageBlob);
            formData.append('size', 'auto');
            
            const response = await fetch('https://api.remove.bg/v1.0/removebg', {
              method: 'POST',
              headers: {
                'X-Api-Key': process.env.REACT_APP_REMOVE_BG_API_KEY
              },
              body: formData
            });
            
            if (!response.ok) throw new Error('API request failed');
            return await response.blob();
          };

          // Option 2: Simple edge detection fallback (basic background removal)
          const removeWithCanvas = async (imageBlob) => {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                const bgCanvas = document.createElement('canvas');
                const bgCtx = bgCanvas.getContext('2d');
                bgCanvas.width = img.width;
                bgCanvas.height = img.height;
                
                bgCtx.drawImage(img, 0, 0);
                const imageData = bgCtx.getImageData(0, 0, bgCanvas.width, bgCanvas.height);
                const data = imageData.data;
                
                // Simple background removal: make white/light pixels transparent
                for (let i = 0; i < data.length; i += 4) {
                  const r = data[i];
                  const g = data[i + 1];
                  const b = data[i + 2];
                  
                  // If pixel is close to white/light gray, make it transparent
                  const brightness = (r + g + b) / 3;
                  if (brightness > 200 && Math.abs(r - g) < 30 && Math.abs(g - b) < 30) {
                    data[i + 3] = 0; // Make transparent
                  }
                }
                
                bgCtx.putImageData(imageData, 0, 0);
                bgCanvas.toBlob(resolve, 'image/png');
              };
              img.src = URL.createObjectURL(imageBlob);
            });
          };

          // Try API first, fallback to canvas processing
          let processedBlob;
          try {
            // For demo purposes, we'll use the canvas method
            // In production, you'd implement the API call with user's API key
            processedBlob = await removeWithCanvas(blob);
          } catch (apiError) {
            console.log('API failed, using fallback method');
            processedBlob = await removeWithCanvas(blob);
          }
          
          // Create new image URL
          const processedUrl = URL.createObjectURL(processedBlob);
          
          // Replace the original image with processed one
          fabric.Image.fromURL(processedUrl, (newImg) => {
            // Copy properties from original image
            newImg.set({
              left: imageObject.left,
              top: imageObject.top,
              scaleX: imageObject.scaleX,
              scaleY: imageObject.scaleY,
              angle: imageObject.angle,
              id: imageObject.id
            });
            
            // Remove old image and add new one
            canvas.remove(imageObject);
            canvas.add(newImg);
            canvas.setActiveObject(newImg);
            canvas.renderAll();
            
            setSnackbar({ 
              open: true, 
              message: 'Background removed successfully!', 
              severity: 'success' 
            });
          }, { crossOrigin: 'anonymous' });
          
        } catch (error) {
          console.error('Background removal failed:', error);
          setSnackbar({ 
            open: true, 
            message: 'Background removal failed. Please try again.', 
            severity: 'error' 
          });
        }
      }, 'image/png');
      
    } catch (error) {
      console.error('Background removal error:', error);
      setSnackbar({ 
        open: true, 
        message: 'Background removal failed. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setIsRemovingBackground(false);
    }
  }, []);

  // AI-powered background removal
  const removeBackgroundAI = useCallback(async (imageObject) => {
    if (!imageObject || imageObject.type !== 'image') {
      setSnackbar({ 
        open: true, 
        message: 'Please select an image to remove background.', 
        severity: 'warning' 
      });
      return;
    }

    setIsRemovingBackground(true);
    
    try {
      setSnackbar({ 
        open: true, 
        message: 'Loading AI model and processing image...', 
        severity: 'info' 
      });

      // Get image element from Fabric.js object
      const imageElement = imageObject.getElement();
      
      // Check if AI model is available and get info
      const modelInfo = aiBackgroundRemover.getModelInfo();
      console.log('AI Model Info:', modelInfo);
      
      // Try AI background removal for people first
      let processedBlob;
      let usedMethod = 'unknown';
      
      try {
        processedBlob = await aiBackgroundRemover.removeBackground(imageElement, {
          edgeBlur: 2
        });
        
        // Get the actual method used
        const finalModelInfo = aiBackgroundRemover.getModelInfo();
        usedMethod = finalModelInfo.modelType;
        
        if (usedMethod === 'MediaPipe') {
        setSnackbar({ 
          open: true, 
            message: 'AI background removal completed successfully! (MediaPipe)', 
          severity: 'success' 
        });
        } else if (usedMethod === 'BodyPix') {
          setSnackbar({ 
            open: true, 
            message: 'AI background removal completed successfully! (BodyPix)', 
            severity: 'success' 
          });
        } else if (usedMethod === 'basic') {
          setSnackbar({ 
            open: true, 
            message: 'Background removed using enhanced color detection.', 
            severity: 'success' 
          });
        }
        
      } catch (error) {
        console.log('Person detection failed, trying general object removal:', error.message);
        
        // Fallback to general object removal
        try {
          processedBlob = await aiBackgroundRemover.removeBackgroundGeneral(imageElement);
          usedMethod = 'general';
          
          setSnackbar({ 
            open: true, 
            message: 'Background removal completed! (General object detection)', 
            severity: 'success' 
          });
          
        } catch (generalError) {
          console.log('General AI removal failed, using enhanced simple method');
          
          // Final fallback to enhanced simple method
          processedBlob = await enhancedSimpleRemoval(imageElement);
          usedMethod = 'enhanced_simple';
          
          setSnackbar({ 
            open: true, 
            message: 'Background removed using enhanced algorithm.', 
            severity: 'success' 
          });
        }
      }
      
      // Create new image URL
      const processedUrl = URL.createObjectURL(processedBlob);
      
      // Replace the original image with processed one
      fabric.Image.fromURL(processedUrl, (newImg) => {
        // Copy properties from original image
        newImg.set({
          left: imageObject.left,
          top: imageObject.top,
          scaleX: imageObject.scaleX,
          scaleY: imageObject.scaleY,
          angle: imageObject.angle,
          id: imageObject.id
        });
        
        // Remove old image and add new one
        fabricCanvasRef.current.remove(imageObject);
        fabricCanvasRef.current.add(newImg);
        fabricCanvasRef.current.setActiveObject(newImg);
        fabricCanvasRef.current.renderAll();
        
        // Add to history for undo functionality
        setTimeout(() => {
          const canvas = fabricCanvasRef.current;
          if (canvas) {
            const state = JSON.stringify(canvas.toJSON());
            setHistory(prev => {
              const newHistory = prev.slice(0, historyIndex + 1);
              newHistory.push(state);
              setHistoryIndex(newHistory.length - 1);
              return newHistory;
            });
          }
        }, 100);
        
        console.log(`Background removal completed using method: ${usedMethod}`);
      }, { crossOrigin: 'anonymous' });
      
    } catch (error) {
      console.error('AI background removal failed:', error);
      setSnackbar({ 
        open: true, 
        message: `Background removal failed: ${error.message}. Please try the simple method.`, 
        severity: 'error' 
      });
    } finally {
      setIsRemovingBackground(false);
    }
  }, [historyIndex]);

  // Enhanced simple background removal method
  const enhancedSimpleRemoval = useCallback(async (imageElement) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = imageElement.width || imageElement.naturalWidth;
      canvas.height = imageElement.height || imageElement.naturalHeight;
      
      ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Multi-algorithm approach with improved sampling
      const edgePixels = [];
      const step = Math.max(1, Math.floor(Math.min(canvas.width, canvas.height) / 50));
      
      // Sample edge pixels more comprehensively
      for (let x = 0; x < canvas.width; x += step) {
        // Top edge
        const topIndex = x * 4;
        edgePixels.push([data[topIndex], data[topIndex + 1], data[topIndex + 2]]);
        
        // Bottom edge
        const bottomIndex = ((canvas.height - 1) * canvas.width + x) * 4;
        edgePixels.push([data[bottomIndex], data[bottomIndex + 1], data[bottomIndex + 2]]);
      }
      
      for (let y = 0; y < canvas.height; y += step) {
        // Left edge
        const leftIndex = (y * canvas.width) * 4;
        edgePixels.push([data[leftIndex], data[leftIndex + 1], data[leftIndex + 2]]);
        
        // Right edge
        const rightIndex = (y * canvas.width + canvas.width - 1) * 4;
        edgePixels.push([data[rightIndex], data[rightIndex + 1], data[rightIndex + 2]]);
      }
      
      // Calculate dominant background color
      const avgBg = [0, 0, 0];
      edgePixels.forEach(([r, g, b]) => {
        avgBg[0] += r; 
        avgBg[1] += g; 
        avgBg[2] += b;
      });
      avgBg[0] = Math.round(avgBg[0] / edgePixels.length);
      avgBg[1] = Math.round(avgBg[1] / edgePixels.length);
      avgBg[2] = Math.round(avgBg[2] / edgePixels.length);
      
      // Process pixels with improved algorithm
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2];
        
        // Calculate color distance
        const distance = Math.sqrt(
          Math.pow(r - avgBg[0], 2) +
          Math.pow(g - avgBg[1], 2) +
          Math.pow(b - avgBg[2], 2)
        );
        
        // Check if pixel is similar to white/light colors (common backgrounds)
        const brightness = (r + g + b) / 3;
        const isWhiteish = brightness > 200 && 
          Math.abs(r - g) < 40 && Math.abs(g - b) < 40 && Math.abs(r - b) < 40;
        
        // Adaptive threshold based on background characteristics
        const baseThreshold = 70;
        const threshold = isWhiteish ? baseThreshold * 0.8 : baseThreshold;
        const fadeThreshold = threshold * 1.6;
        
        if (distance < threshold || isWhiteish) {
          data[i + 3] = 0; // Make transparent
        } else if (distance < fadeThreshold) {
          // Smooth edges with better alpha blending
          const alpha = (distance - threshold) / (fadeThreshold - threshold);
          data[i + 3] = Math.round(data[i + 3] * Math.min(alpha, 1));
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob(resolve, 'image/png');
    });
  }, []);



  // Load template from URL parameter
  useEffect(() => {
    console.log('🚀 Template loading useEffect started with templateId:', templateId);
    
    const loadTemplate = async () => {
      console.log('🔍 Template loading - templateId:', templateId);
    if (templateId) {
        // First, check static templates
        let template = carouselTemplates.find(t => t.id === templateId);
        console.log('📁 Static template found:', template ? template.name : 'Not found');
        
        // If not found in static templates, fetch from API
        if (!template) {
          console.log('🌐 Fetching template from API...');
          try {
            const response = await fetch(`http://localhost:5000/api/carousel-templates/${templateId}`);
            const data = await response.json();
            console.log('📡 API response received for template:', data.template?.name);
            
            if (data.success && data.template) {
              // Backend already parses template_data and returns it as 'template' field
              let templateData = data.template.template;
              
              // Clean up any invalid data in the template
              templateData = cleanupTemplateData(templateData);
              
              template = {
                id: data.template.id,
                name: data.template.name,
                category: data.template.category,
                thumbnail: data.template.thumbnail || '/images/templates/default.png',
                template: templateData
              };
              console.log('✅ Template loaded from API:', template.name, 'with', templateData?.pages?.length || 0, 'pages');
            }
          } catch (error) {
            console.error('❌ Error fetching template:', error);
          }
        }
        
      if (template) {
        // Clean up template data before setting it
        if (template.template) {
          template.template = cleanupTemplateData(template.template);
        }
        
        setSelectedTemplate(template);
        console.log('✅ setSelectedTemplate called for:', template.name);
          
        setDocumentName(template.name);
        if (template.template && template.template.canvasSettings) {
          setBackground(template.template.canvasSettings.background || '#ffffff');
        }
        setSnackbar({ 
          open: true, 
          message: `Template "${template.name}" loaded successfully!`, 
          severity: 'success' 
        });
      } else {
          console.log('❌ Template not found, starting with blank canvas');
        setSnackbar({ 
          open: true, 
          message: 'Template not found. Starting with blank canvas.', 
          severity: 'warning' 
        });
      }
      } else {
        console.log('📝 No templateId provided, starting with blank canvas');
    }
    setIsTemplateLoaded(true);
    };

    loadTemplate();
  }, [templateId]);

  // Track selectedTemplate state changes
  useEffect(() => {
    if (selectedTemplate) {
      console.log('🔄 selectedTemplate updated:', selectedTemplate.name, 'pages:', selectedTemplate.template?.pages?.length || 0);
    }
  }, [selectedTemplate]);

  // Removed zoom calculation - using fixed display sizing instead

  // Initialize Fabric.js canvas
  useEffect(() => {
    console.log('🎯 Canvas useEffect triggered for:', selectedTemplate?.name || 'blank canvas');
    
    if (!isTemplateLoaded) {
      console.log('⏸️ Canvas init skipped - template not loaded yet');
      return;
    }

    // Clean up existing canvas if it exists
    if (fabricCanvasRef.current) {
      console.log('🧹 Disposing existing canvas before creating new one');
      try {
        // Check if canvas is still properly attached to DOM
        if (fabricCanvasRef.current.upperCanvasEl && fabricCanvasRef.current.upperCanvasEl.parentNode) {
          fabricCanvasRef.current.dispose();
        }
      } catch (error) {
        console.warn('⚠️ Error disposing canvas:', error);
      }
      fabricCanvasRef.current = null;
    }

    console.log(`🎨 Initializing canvas with background color: ${background}`);
    const canvas = new fabric.Canvas(canvasRef.current, {
      width: PAGE_WIDTH, // Start with single page, expansion handled by useEffect
      height: PAGE_HEIGHT,
      backgroundColor: background || '#FFFFFF', // Ensure we always have a valid background color
      selection: true,
      preserveObjectStacking: true
    });

    // Removed zoom initialization - using fixed display sizing instead

    fabricCanvasRef.current = canvas;

    // Enhanced event handlers
    canvas.on('mouse:dblclick', function(e) {
          if (e.target && (e.target.type === 'textbox' || e.target.type === 'i-text')) {
        e.target.enterEditing();
        e.target.selectAll();
      }
    });

    canvas.on('selection:created', function(e) {
      const obj = e.selected[0];
      setSelectedObject(obj);
      if (obj && obj.type === 'textbox') {
        setFontSizeInput(obj.fontSize || 20);
      }
      updateObjectsList();
      // Auto-close left panel when selecting an element
      setActiveSection(null);
    });

    canvas.on('selection:updated', function(e) {
      const obj = e.selected[0];
      setSelectedObject(obj);
      if (obj && obj.type === 'textbox') {
        setFontSizeInput(obj.fontSize || 20);
      }
      updateObjectsList();
      // Auto-close left panel when selecting an element
      setActiveSection(null);
    });

    canvas.on('selection:cleared', function() {
      setSelectedObject(null);
      setFontSizeInput('');
    });

    // Handle text scaling - convert scale transformations to font size changes
    canvas.on('object:modified', function(e) {
      const obj = e.target;
      console.log('🔍 Object modified:', obj?.type, 'scaleX:', obj?.scaleX, 'scaleY:', obj?.scaleY);
      
      if (obj && (obj.type === 'textbox' || obj.type === 'text')) {
        const scaleX = obj.scaleX || 1;
        const scaleY = obj.scaleY || 1;
        
        // If the text has been scaled, convert to font size
        if (Math.abs(scaleX - 1) > 0.01 || Math.abs(scaleY - 1) > 0.01) {
          // Use the average scale factor to determine new font size
          const scaleFactor = Math.max(scaleX, scaleY);
          const currentFontSize = obj.fontSize || 20;
          const newFontSize = Math.max(8, Math.round(currentFontSize * scaleFactor));
          
          console.log(`📝 Converting text scale: ${currentFontSize}px * ${scaleFactor.toFixed(2)} = ${newFontSize}px`);
          
          // For textboxes, adjust width to prevent unwanted wrapping
          if (obj.type === 'textbox') {
            const currentWidth = obj.width || 400;
            // Scale the width proportionally to accommodate larger text
            const newWidth = Math.max(100, Math.round(currentWidth * scaleFactor));
            
            console.log(`📏 Textbox width: ${currentWidth}px → ${newWidth}px`);
            
            // Update font size, width, and reset scale
            obj.set({
              fontSize: newFontSize,
              width: newWidth,
              scaleX: 1,
              scaleY: 1
            });
          } else {
            // For regular text, just update font size and reset scale
            obj.set({
              fontSize: newFontSize,
              scaleX: 1,
              scaleY: 1
            });
          }
          
          // Recalculate dimensions based on new font size
          if (obj.type === 'textbox' || obj.type === 'text') {
            // Let text objects recalculate their dimensions naturally
            obj.initDimensions();
          }
          
          // Update font size input if this is the selected object
          if (obj === canvas.getActiveObject()) {
            setFontSizeInput(newFontSize);
          }
          
          // Re-render to apply changes
          obj.setCoords();
          canvas.renderAll();
          
          console.log(`📏 Final dimensions - width: ${obj.width}, height: ${obj.height}`);
        }
      }
    });

    // ENSURE ALL OBJECTS HAVE IDs - Global safety net
    canvas.on('object:added', function(e) {
      const obj = e.target;
      if (!obj.id) {
        // Generate a meaningful ID based on object type and position
        const timestamp = Date.now();
        const x = Math.round(obj.left || 0);
        const y = Math.round(obj.top || 0);
        obj.id = `${obj.type}_${timestamp}_${x}_${y}`;
        console.log('🔧 Auto-assigned ID to object without ID:', obj.type, 'at', obj.left, obj.top, '→', obj.id);
      }
      saveState();
      updateObjectsList();
    });

    canvas.on('object:removed', function() {
      saveState();
      updateObjectsList();
    });

        // Load template elements if template is selected
    console.log('🔍 Canvas Init - checking template:', selectedTemplate?.name || 'none');
    
    if (selectedTemplate && selectedTemplate.template && selectedTemplate.template.pages) {
      const templatePages = selectedTemplate.template.pages;
      
      // Set page count based on template
      const templatePageCount = templatePages.length;
      console.log(`📄 Loading template with ${templatePageCount} pages`);
      
      // FIRST: Resize canvas to accommodate all pages BEFORE loading elements
      const expectedWidth = templatePageCount * PAGE_WIDTH;
      if (canvas.getWidth() !== expectedWidth) {
        console.log(`🔧 Pre-sizing canvas for template: ${canvas.getWidth()}px → ${expectedWidth}px`);
        canvas.setDimensions({ width: expectedWidth, height: PAGE_HEIGHT });
        canvas.renderAll();
      }
      
      setPageCount(templatePageCount);
      
      // THEN: Load all pages with proper layer ordering
      const allElements = [];
      
      templatePages.forEach((templatePage, pageIndex) => {
        console.log(`📄 Loading page ${pageIndex + 1} with ${templatePage.elements.length} elements`);
        allElements.push(...templatePage.elements);
      });
      
      // Sort ALL elements by zIndex first to maintain order
      const sortedElements = [...allElements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
      console.log('🎭 Loading elements in layer order:', sortedElements.map(el => ({ id: el.id, zIndex: el.zIndex })));
      
      // Count async elements for layer order restoration
      const asyncElementCount = sortedElements.filter(el => el.type === 'image' || el.type === 'replacementGroup').length;
      let loadedAsyncCount = 0;
      
      const checkAndRestoreOrder = () => {
        loadedAsyncCount++;
        console.log(`📊 Async element loaded: ${loadedAsyncCount}/${asyncElementCount}`);
        if (loadedAsyncCount >= asyncElementCount) {
          setTimeout(() => {
            restoreLayerOrder(canvas, sortedElements);
          }, 100); // Small delay to ensure all elements are fully rendered
        }
      };
      
      // Load all elements
      sortedElements.forEach((element, elementIndex) => {
        console.log(`🔧 Loading element ${elementIndex + 1}:`, element.type, element.id, `at EXACT position (${element.x}, ${element.y}) with zIndex:`, element.zIndex);
        
        // For async elements, pass callback to notify when loaded
        if (element.type === 'image' || element.type === 'replacementGroup') {
          element._onLoaded = checkAndRestoreOrder;
        }
        
        const fabricObj = loadTemplateElement(canvas, element);
        if (!fabricObj && element.type !== 'image' && element.type !== 'replacementGroup') {
          console.warn(`⚠️ Failed to load element:`, element);
        }
      });
      
      // If no async elements, apply order immediately
      if (asyncElementCount === 0) {
        setTimeout(() => {
          restoreLayerOrder(canvas, sortedElements);
        }, 50);
      }
      
      // Add page separators for multi-page templates
      if (templatePageCount > 1) {
        const separators = [];
        for (let i = 1; i < templatePageCount; i++) {
          const separator = new fabric.Line(
            [i * PAGE_WIDTH, 0, i * PAGE_WIDTH, PAGE_HEIGHT],
            {
              stroke: '#cbd5e1',
              strokeWidth: PAGE_SEPARATOR_WIDTH,
              strokeDashArray: [10, 10],
              selectable: false,
              evented: false,
              excludeFromExport: true,
              id: `separator_${i}`
            }
          );
          canvas.add(separator);
          separators.push({ id: `separator_${i}`, position: i * PAGE_WIDTH });
        }
        setPageSeparators(separators);
        
        // Add page borders for all pages
        for (let i = 1; i <= templatePageCount; i++) {
          const pageBorder = new fabric.Rect({
            left: (i - 1) * PAGE_WIDTH,
            top: 0,
            width: PAGE_WIDTH,
            height: PAGE_HEIGHT,
            fill: 'transparent',
            stroke: '#cbd5e1',
            strokeWidth: 1,
            strokeDashArray: [5, 5],
            selectable: false,
            evented: false,
            excludeFromExport: true,
            id: `page_border_${i}`
          });
          canvas.add(pageBorder);
        }
      }
      
      canvas.renderAll();
      
      // Force a canvas refresh after template loading to ensure visibility
      setTimeout(() => {
        canvas.renderAll();
        canvas.calcOffset();
        console.log('🔄 Force canvas refresh after template load');
      }, 100);
      
      console.log(`✅ Template loading complete! ${canvas.getObjects().length} objects, ${templatePageCount} pages, canvas: ${canvas.getWidth()}x${canvas.getHeight()}`);
    } else {
      console.log('ℹ️ No template to load - starting with blank canvas');
    }

    // Profile picture overlay removed



    // Page borders are now handled in template loading section above

    // Save initial state and ensure canvas is properly rendered
    setTimeout(() => {
      if (canvas && !canvas.disposed) {
        saveState();
        updateObjectsList();
        
        // Final canvas refresh to ensure everything is visible
        canvas.renderAll();
        canvas.calcOffset();
        console.log('🔄 Final canvas refresh and state save');
      }
    }, 150);

    // Handler to suppress browser context menu, now consolidated and safe
    const contextMenuHandler = (e) => {
      e.preventDefault();
      const pointer = canvas.getPointer(e);
      const target = canvas.findTarget(e, false);
      if (target) {
        canvas.setActiveObject(target);
        canvas.renderAll();
        // Use a small timeout to prevent other click events from immediately closing the menu
        setTimeout(() => {
          setContextMenu({ x: e.clientX, y: e.clientY });
        }, 50);
      } else {
        setContextMenu(null); // Clear menu if clicking on empty canvas space
      }
    };

    // Safely get the wrapper element
    const canvasWrapper = canvas.wrapperEl;
    if (canvasWrapper) {
      canvasWrapper.addEventListener('contextmenu', contextMenuHandler);
    }

    return () => {
      // Cleanup the single event listener from the captured wrapper element
      if (canvasWrapper) {
        canvasWrapper.removeEventListener('contextmenu', contextMenuHandler);
      }
      // Other canvas cleanup
      if (canvas && !canvas.disposed) {
        console.log('🧹 Cleaning up canvas on dependency change');
        try {
          canvas.off('mouse:down');
          canvas.off('selection:created');
          canvas.off('selection:updated');
          canvas.off('selection:cleared');
          canvas.off('object:modified');
          canvas.off('object:added');
          canvas.off('object:removed');
          
          // Check if canvas is still properly attached to DOM before disposing
          if (canvas.upperCanvasEl && canvas.upperCanvasEl.parentNode) {
            canvas.dispose();
          }
        } catch (error) {
          console.warn('⚠️ Error cleaning up canvas:', error);
        }
      }
    };
  }, [isTemplateLoaded, selectedTemplate, canvasSize]); // Removed background from dependencies to prevent reinit during AI design

  // Handle page count changes without reinitializing canvas
  useEffect(() => {
    console.log(`🔄 useEffect triggered - pageCount: ${pageCount}, isTemplateLoaded: ${isTemplateLoaded}`);
    
    const canvas = fabricCanvasRef.current;
    if (canvas && isTemplateLoaded) {
      const expectedWidth = pageCount * PAGE_WIDTH;
      const currentWidth = canvas.getWidth();
      
      console.log(`🔍 Canvas Dimensions: ${currentWidth}px → ${expectedWidth}px (${pageCount} pages)`);
      
      if (currentWidth !== expectedWidth) {
        console.log(`🔧 Resizing canvas from ${currentWidth}px to ${expectedWidth}px`);
        
        // Try to resize canvas
        canvas.setDimensions({ width: expectedWidth, height: PAGE_HEIGHT });
        
        // Reset any internal scaling
        canvas.setZoom(1);
        canvas.absolutePan(new fabric.Point(0, 0));
        
        setCurrentCanvasWidth(expectedWidth);
        
        // Check if resize was successful
        const newWidth = canvas.getWidth();
        console.log(`✅ Canvas resize attempt - Expected: ${expectedWidth}px, Actual: ${newWidth}px`);
        
        // Force re-render to show the changes
        canvas.renderAll();
        
        // Additional render call after a brief delay to ensure visibility
        setTimeout(() => {
          canvas.renderAll();
          canvas.calcOffset();
          console.log('🔄 Force render after canvas resize');
        }, 50);
        
        if (newWidth !== expectedWidth) {
          console.error(`❌ Canvas resize failed! Expected ${expectedWidth}px but got ${newWidth}px`);
        } else {
          // Check HTML canvas element dimensions
          const canvasElement = canvas.getElement();
          console.log(`🔍 Canvas Element: ${canvasElement.width}x${canvasElement.height} (${canvasElement.offsetWidth}x${canvasElement.offsetHeight} display)`);
          
          // Auto-adjust zoom for optimal viewing
          const viewportWidth = 1200; // Increased viewport assumption for less zoom out
          const optimalZoom = Math.min(viewportWidth / expectedWidth, 0.9); // Increased max zoom to 90%
          const finalZoom = Math.max(optimalZoom, 0.5); // Increased min zoom to 50%
          
          if (Math.abs(zoom - finalZoom) > 0.05 && !zoomLocked) { // Only update if significant change and zoom not locked
            console.log(`🔧 Auto-adjusting zoom from ${zoom.toFixed(2)} to ${finalZoom.toFixed(2)}`);
            setZoom(finalZoom);
            
            // Force render after zoom change
            setTimeout(() => {
              canvas.renderAll();
              console.log('🔄 Force render after zoom adjustment');
            }, 100);
          }
        }
      }
    }
  }, [pageCount, isTemplateLoaded, zoom, zoomLocked]); // Added zoom and zoomLocked dependencies

  // Profile picture overlay functionality removed

  // Update objects list for layers panel
  const updateObjectsList = useCallback((customObjects = null) => {
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      if (customObjects) {
        // If custom objects are provided (for reordering), use them
        setCanvasObjects(customObjects);
        
        // Also update the actual canvas objects order
        const reorderedObjects = [];
        customObjects.forEach(obj => {
          if (obj.object) {
            reorderedObjects.push(obj.object);
          }
        });
        
        // Reorder canvas objects to match the new order
        if (reorderedObjects.length > 0) {
          // Remove all objects and add them back in the new order
          const allCanvasObjects = canvas.getObjects();
          const nonReorderedObjects = allCanvasObjects.filter(obj => 
            !reorderedObjects.includes(obj)
          );
          
          canvas.clear();
          
          // Add non-reordered objects first (like page separators)
          nonReorderedObjects.forEach(obj => canvas.add(obj));
          
          // Then add reordered objects in their new order
          reorderedObjects.forEach(obj => canvas.add(obj));
          
          canvas.renderAll();
          // We'll save state after the component is fully defined
          setTimeout(() => {
            if (canvas) canvas.fire('object:modified');
          }, 100);
        }
      } else {
        // Normal update - map all canvas objects to our layer objects
        const objects = canvas.getObjects().map((obj, index) => {
          // Prevent negative page indices that create "Slide 0"
          const pageIndex = Math.max(0, Math.floor(obj.left / PAGE_WIDTH));
          let objectName = obj.type;
          
          if (obj.type === 'textbox') {
            objectName = obj.text?.slice(0, 20) + (obj.text?.length > 20 ? '...' : '');
          } else if (obj.type === 'image') {
            objectName = 'Image';
          } else if (obj.type === 'rect') {
            objectName = 'Rectangle';
          } else if (obj.type === 'circle') {
            objectName = 'Circle';
          } else if (obj.type === 'group') {
            objectName = 'Group';
          } else {
            objectName = obj.type.charAt(0).toUpperCase() + obj.type.slice(1);
          }
          
          return {
            id: obj.id || index,
            type: obj.type,
            name: objectName,
            visible: obj.visible !== false,
            locked: obj.lockMovementX && obj.lockMovementY,
            pageIndex: pageIndex,
            pageId: pageIndex,
            object: obj
          };
        });
        setCanvasObjects(objects);
      }
    }
  }, []);

  // Toggle object lock/unlock
  const toggleObjectLock = useCallback((obj) => {
    if (!obj) return;
    
    const isLocked = obj.lockMovementX || obj.lockMovementY || obj.selectable === false;
    
    if (isLocked) {
      // Unlock object
      obj.set({
        lockMovementX: false,
        lockMovementY: false,
        lockScalingX: false,
        lockScalingY: false,
        lockRotation: false,
        selectable: true,
        evented: true
      });
    } else {
      // Lock object
      obj.set({
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
        selectable: false,
        evented: false
      });
    }
    
    fabricCanvasRef.current.renderAll();
    updateObjectsList();
    setSnackbar({ 
      open: true, 
      message: isLocked ? 'Object unlocked' : 'Object locked', 
      severity: 'success' 
    });
  }, [updateObjectsList, setSnackbar]);

  // Toggle object visibility
  const toggleObjectVisibility = useCallback((obj) => {
    if (!obj) return;
    
    const isVisible = obj.visible !== false;
    obj.set('visible', !isVisible);
    
    fabricCanvasRef.current.renderAll();
    updateObjectsList();
    setSnackbar({ 
      open: true, 
      message: isVisible ? 'Object hidden' : 'Object shown', 
      severity: 'success' 
    });
  }, [updateObjectsList, setSnackbar]);

  // Get current page index based on canvas viewport
  const getCurrentPageIndex = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return 0;
    
    const viewportTransform = canvas.viewportTransform;
    const currentViewX = -viewportTransform[4] / viewportTransform[0];
    return Math.floor(currentViewX / PAGE_WIDTH);
  }, []);

  // Helper function to convert blob URL to base64
  const blobToBase64 = (url) => {
    return new Promise((resolve, reject) => {
      if (!url.startsWith('blob:')) {
        resolve(url); // Already a data URL or regular URL
        return;
      }
      
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
        .catch(reject);
    });
  };

  // Save template functionality
  const handleSaveTemplate = async (templateInfo) => {
    setIsSavingTemplate(true);
    try {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      // Get current canvas state
      const canvasJson = canvas.toJSON();
      
      // Extract pages data based on current canvas content
      const pages = [];
      const objects = canvas.getObjects();
      
      console.log('💾 Processing', objects.length, 'canvas objects for template save...');
      console.log('🔍 Canvas objects preview:', objects.map(obj => ({ type: obj.type, id: obj.id, left: obj.left, top: obj.top })));
      const imageCount = objects.filter(obj => obj.type === 'image').length;
      const groupImageCount = objects.filter(obj => obj.type === 'group' && obj.isPlaceholderReplacement && obj.getObjects('image').length > 0).length;
      const totalImages = imageCount + groupImageCount;
      if (totalImages > 0) {
        console.log('🖼️ Found', totalImages, 'images to process for saving (', imageCount, 'standalone,', groupImageCount, 'in groups)...');
      }
      
      // Group objects by page based on their x position
      const objectsByPage = {};
      
            // Process images first to convert blob URLs to base64
      const imageProcessingPromises = [];
      objects.forEach(obj => {
        if (obj.type === 'image') {
          // Get image source from various possible locations
          let imageSrc = obj.src || obj.getSrc?.() || obj._originalElement?.src || obj._element?.src;
          
          // If still no src, try to get it as data URL
          if (!imageSrc && obj._element) {
            try {
              // Create a canvas to extract the image data
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = obj._element.width || obj.width;
              canvas.height = obj._element.height || obj.height;
              ctx.drawImage(obj._element, 0, 0);
              imageSrc = canvas.toDataURL('image/png');
              console.log('🔄 Extracted image data from canvas element');
            } catch (err) {
              console.error('❌ Failed to extract image from canvas element:', err);
            }
          }
          
          if (imageSrc && imageSrc.startsWith('blob:')) {
            imageProcessingPromises.push(
              blobToBase64(imageSrc).then(base64Src => {
                obj._base64Src = base64Src;
                console.log('🖼️ Converted image blob to base64:', imageSrc.substring(0, 30), '→', base64Src.substring(0, 50));
              }).catch(err => {
                console.error('❌ Failed to convert image blob:', imageSrc, err);
              })
            );
          } else if (imageSrc) {
            // Store the src directly if it's not a blob
            obj._base64Src = imageSrc;
            console.log('🖼️ Using existing image src:', imageSrc.substring(0, 50));
          }
        } else if (obj.type === 'group' && obj.isPlaceholderReplacement) {
          // Process images inside replacement groups
          const imageInGroup = obj.getObjects('image')[0];
          if (imageInGroup) {
            let imageSrc = imageInGroup.src || imageInGroup.getSrc?.() || imageInGroup._originalElement?.src || imageInGroup._element?.src;
            
            if (!imageSrc && imageInGroup._element) {
              try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = imageInGroup._element.width || imageInGroup.width;
                canvas.height = imageInGroup._element.height || imageInGroup.height;
                ctx.drawImage(imageInGroup._element, 0, 0);
                imageSrc = canvas.toDataURL('image/png');
                console.log('🔄 Extracted group image data from canvas element');
              } catch (err) {
                console.error('❌ Failed to extract group image from canvas element:', err);
              }
            }
            
            if (imageSrc && imageSrc.startsWith('blob:')) {
              imageProcessingPromises.push(
                blobToBase64(imageSrc).then(base64Src => {
                  imageInGroup._base64Src = base64Src;
                  console.log('🖼️ Converted group image blob to base64:', imageSrc.substring(0, 30), '→', base64Src.substring(0, 50));
                }).catch(err => {
                  console.error('❌ Failed to convert group image blob:', imageSrc, err);
                })
              );
            } else if (imageSrc) {
              imageInGroup._base64Src = imageSrc;
              console.log('🖼️ Using existing group image src:', imageSrc.substring(0, 50));
            }
          }
        }
      });
      
      // Wait for all image conversions to complete
      await Promise.all(imageProcessingPromises);
      
      objects.forEach(obj => {
        // Calculate page index, allowing negative coordinates for exact preservation
        const pageIndex = Math.max(0, Math.floor(obj.left / PAGE_WIDTH));
        
        if (!objectsByPage[pageIndex]) {
          objectsByPage[pageIndex] = [];
        }
        
        // Create element data compatible with template format - PRESERVE EXACT COORDINATES
        // NEVER generate random IDs - preserve original IDs or create meaningful ones
        let elementId = obj.id;
        if (!elementId) {
          console.error('❌ Object missing ID during save:', obj.type, 'at', obj.left, obj.top);
          // Use a meaningful ID based on object type and properties
          elementId = `${obj.type}_${Date.now()}_${Math.round(obj.left)}_${Math.round(obj.top)}`;
          console.warn('🔧 Generated fallback ID:', elementId);
        }
        
        const element = {
          id: elementId,
          type: obj.type === 'textbox' ? (obj.fontSize > 40 ? 'heading' : 'paragraph') : 
                obj.type === 'rect' ? 'rectangle' : obj.type, // Normalize rect to rectangle
          x: Math.round(obj.left * 1000) / 1000, // Save EXACT coordinates with clean precision
          y: Math.round(obj.top * 1000) / 1000,  // Save EXACT coordinates with clean precision
          width: Math.round(obj.width * 1000) / 1000,
          height: Math.round(obj.height * 1000) / 1000,
          draggable: true,
          // SAVE ALL TRANSFORMATION PROPERTIES
          opacity: Math.round((obj.opacity || 1) * 1000) / 1000,
          scaleX: Math.round((obj.scaleX || 1) * 1000) / 1000,
          scaleY: Math.round((obj.scaleY || 1) * 1000) / 1000,
          angle: Math.round((obj.angle || 0) * 1000) / 1000,
          skewX: Math.round((obj.skewX || 0) * 1000) / 1000,
          skewY: Math.round((obj.skewY || 0) * 1000) / 1000,
          // SAVE LAYER ORDER (z-index) - Canvas object order represents stacking
          zIndex: objects.indexOf(obj) // Preserve stacking order (0 = bottom, higher = top)
        };

        // Add type-specific properties
        if (obj.type === 'textbox') {
          element.text = obj.text;
          element.fontSize = obj.fontSize;
          element.fontFamily = obj.fontFamily;
          // Convert font weight to numeric values for MUI compatibility
          element.fontWeight = obj.fontWeight === 'bold' ? '700' : obj.fontWeight === 'normal' ? '400' : obj.fontWeight;
          element.fill = obj.fill;
          element.align = obj.textAlign;
          element.lineHeight = obj.lineHeight;
        } else if (obj.type === 'rect') {
          element.fill = obj.fill;
          element.stroke = obj.stroke;
          element.strokeWidth = obj.strokeWidth;
          element.rx = obj.rx;
          element.ry = obj.ry;
        } else if (obj.type === 'circle') {
          element.radius = obj.radius;
          element.fill = obj.fill;
          element.stroke = obj.stroke;
          element.strokeWidth = obj.strokeWidth;
        } else if (obj.type === 'image') {
          // Use converted base64 src if available, otherwise try various sources
          let imageSrc = obj._base64Src || obj.src || obj.getSrc?.() || obj._originalElement?.src || obj._element?.src;
          
          // If still no src, try to extract from canvas element
          if (!imageSrc && obj._element) {
            try {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = obj._element.width || obj.width;
              canvas.height = obj._element.height || obj.height;
              ctx.drawImage(obj._element, 0, 0);
              imageSrc = canvas.toDataURL('image/png');
              console.log('🔄 Extracted image data for saving');
            } catch (err) {
              console.error('❌ Failed to extract image for saving:', err);
            }
          }
          
          // Only save image if it has a valid src
          if (imageSrc) {
            element.src = imageSrc;
            element.crossOrigin = 'anonymous';
            
            // Store original dimensions and scaling with clean precision
            element.originalWidth = Math.round(obj.width * 1000) / 1000;
            element.originalHeight = Math.round(obj.height * 1000) / 1000;
            // Note: scaleX, scaleY, opacity, angle, skewX, skewY already saved in common properties above
            
            console.log('💾 Saving image element with opacity:', obj.opacity, 'transforms:', { scaleX: obj.scaleX, scaleY: obj.scaleY, angle: obj.angle }, imageSrc.substring(0, 50));
          } else {
            console.warn('⚠️ Skipping image element without src - could not extract image data');
            return; // Skip this element
          }
        } else if (obj.type === 'group') {
          // Handle different types of groups
          if (obj.isPlaceholderReplacement) {
            // This is a replaced placeholder group (contains an image)
            element.type = 'replacementGroup';
            element.isPlaceholderReplacement = true;
            element.placeholderShape = obj.placeholderShape;
            
            // Save clipping information with clean precision
            if (obj.clipPath) {
              element.clipPath = {
                type: obj.clipPath.type,
                radius: obj.clipPath.radius ? Math.round(obj.clipPath.radius * 1000) / 1000 : undefined,
                width: obj.clipPath.width ? Math.round(obj.clipPath.width * 1000) / 1000 : undefined,
                height: obj.clipPath.height ? Math.round(obj.clipPath.height * 1000) / 1000 : undefined
              };
            }
            
            // Note: opacity, scaleX, scaleY, angle, skewX, skewY already saved in common properties above
            
            // Save the image inside the group
            const imageInGroup = obj.getObjects('image')[0];
            if (imageInGroup) {
              let imageSrc = imageInGroup._base64Src || imageInGroup.src || imageInGroup.getSrc?.() || 
                           imageInGroup._originalElement?.src || imageInGroup._element?.src;
              
              if (!imageSrc && imageInGroup._element) {
                try {
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  canvas.width = imageInGroup._element.width || imageInGroup.width;
                  canvas.height = imageInGroup._element.height || imageInGroup.height;
                  ctx.drawImage(imageInGroup._element, 0, 0);
                  imageSrc = canvas.toDataURL('image/png');
                } catch (err) {
                  console.error('❌ Failed to extract group image:', err);
                }
              }
              
              if (imageSrc) {
                element.imageSrc = imageSrc;
                element.imageScaleX = Math.round((imageInGroup.scaleX || 1) * 1000) / 1000;
                element.imageScaleY = Math.round((imageInGroup.scaleY || 1) * 1000) / 1000;
                element.imageLeft = Math.round((imageInGroup.left || 0) * 1000) / 1000;
                element.imageTop = Math.round((imageInGroup.top || 0) * 1000) / 1000;
                console.log('💾 Saving replacement group with image:', imageSrc.substring(0, 50));
              }
            }
          } else {
            // This is an original placeholder group
            element.type = 'placeholderGroup';
            
            // Save placeholder information
            if (obj.placeholderType) {
              element.placeholderType = obj.placeholderType;
              element.placeholderLabel = obj.placeholderLabel;
            }
            
            // Save the structure of objects in the group
            element.groupObjects = obj._objects?.map(subObj => ({
              type: subObj.type,
              left: subObj.left,
              top: subObj.top,
              width: subObj.width,
              height: subObj.height,
              radius: subObj.radius,
              fill: subObj.fill,
              stroke: subObj.stroke,
              strokeWidth: subObj.strokeWidth,
              text: subObj.text,
              fontSize: subObj.fontSize,
              fontFamily: subObj.fontFamily,
              originX: subObj.originX,
              originY: subObj.originY
            })) || [];
            
            console.log('💾 Saving placeholder group with', element.groupObjects.length, 'sub-objects');
          }
        }

        objectsByPage[pageIndex].push(element);
        console.log(`💾 Saved ${element.type} "${element.id}" with zIndex: ${element.zIndex} (${element.zIndex === 0 ? 'bottom layer' : element.zIndex === objects.length - 1 ? 'top layer' : 'middle layer'})`);
      });

      // Create pages array (only include valid pages starting from index 0)
      Object.keys(objectsByPage)
        .map(key => parseInt(key))
        .filter(pageIndex => pageIndex >= 0) // Only include valid page indices
        .sort((a, b) => a - b) // Sort by page index
        .forEach(pageIndex => {
          pages.push({
            id: `${pageIndex + 1}`,
            name: `Page ${pageIndex + 1}`,
            elements: objectsByPage[pageIndex]
          });
        });

      const templateData = {
        name: templateInfo.name,
        pages: pages,
        canvasSettings: {
          width: pageCount * PAGE_WIDTH,
          height: PAGE_HEIGHT,
          background: background
        }
      };

      // Save to database
      const response = await fetch('http://localhost:5000/api/carousel-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: templateInfo.name,
          description: templateInfo.description,
          category: templateInfo.category,
          templateData: templateData,
          canvasSettings: templateData.canvasSettings,
          isPublic: templateInfo.isPublic,
          tags: templateInfo.tags
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Template saved successfully!',
          severity: 'success'
        });
        setShowSaveDialog(false);
      } else {
        throw new Error(result.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save template. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSavingTemplate(false);
    }
  };

  // Save canvas state for undo/redo
  const saveState = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      const state = JSON.stringify(canvas.toJSON());
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(state);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [history, historyIndex]);

  // Undo/Redo functions
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const canvas = fabricCanvasRef.current;
      const previousState = history[historyIndex - 1];
      canvas.loadFromJSON(previousState, () => {
        canvas.renderAll();
        setHistoryIndex(historyIndex - 1);
        setSelectedObject(null);
        updateObjectsList();
      });
    }
  }, [history, historyIndex, updateObjectsList]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const canvas = fabricCanvasRef.current;
      const nextState = history[historyIndex + 1];
      canvas.loadFromJSON(nextState, () => {
        canvas.renderAll();
        setHistoryIndex(historyIndex + 1);
        setSelectedObject(null);
        updateObjectsList();
      });
    }
  }, [history, historyIndex, updateObjectsList]);

  // Carousel specific functions
  // Add multiple pages at once - used for AI design generation
  const addMultiplePages = useCallback((count) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || count <= 0) return;

    const currentPageCount = pageCount;
    const newPageCount = pageCount + count;
    
    console.log(`📄 Adding ${count} new pages: ${currentPageCount} → ${newPageCount}`);
    
    const newSeparators = [];
    
    // Add all pages at once
    for (let i = 0; i < count; i++) {
      const pageIndex = currentPageCount + i;
      const separatorX = pageIndex * PAGE_WIDTH;
      
      // Add page separator
      const separator = new fabric.Line([separatorX, 0, separatorX, PAGE_HEIGHT], {
        stroke: '#e2e8f0',
        strokeWidth: PAGE_SEPARATOR_WIDTH,
        strokeDashArray: [10, 5],
        selectable: false,
        evented: false,
        excludeFromExport: true,
        id: `separator_${pageIndex}`
      });
      
      canvas.add(separator);
      newSeparators.push(separator);
      
      // Add page border
      const pageBorderX = pageIndex * PAGE_WIDTH;
      const pageBorder = new fabric.Rect({
        left: pageBorderX,
        top: 0,
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        fill: 'transparent',
        stroke: '#cbd5e1',
        strokeWidth: 1,
        strokeDashArray: [5, 5],
        selectable: false,
        evented: false,
        excludeFromExport: true,
        id: `page_border_${pageIndex + 1}`
      });
      
      canvas.add(pageBorder);
    }
    
    // Update state all at once
    setPageCount(newPageCount);
    setPageSeparators(prev => [...prev, ...newSeparators]);
    
    // Update canvas dimensions
    const expectedWidth = newPageCount * PAGE_WIDTH;
    canvas.setDimensions({ width: expectedWidth, height: PAGE_HEIGHT });
    setCurrentCanvasWidth(expectedWidth);
    
    // Update canvas background - ensure we have a valid color
    const bgColor = background || '#FFFFFF';
    console.log(`🎨 Setting background color for multiple pages: ${bgColor}`);
    canvas.setBackgroundColor(bgColor, () => {
      canvas.renderAll();
      console.log(`✅ Background color applied to multiple pages: ${bgColor}`);
    });
    
    // Force re-render to ensure all elements are visible
    canvas.renderAll();
    canvas.calcOffset();
    
    // Only show snackbar if adding a small number of pages
    if (count <= 3) {
      setSnackbar({ 
        open: true, 
        message: `${count} new pages added successfully!`, 
        severity: 'success' 
      });
    }
    
    // Save state for undo/redo after a delay to ensure everything is properly rendered
    setTimeout(() => {
      // Safety check to ensure canvas is still valid
      if (canvas && canvas.getContext()) {
        console.log('🔄 Rendering canvas after adding multiple pages');
        canvas.renderAll();
        saveState();
      } else {
        console.warn('⚠️ Canvas context not available for rendering after adding pages');
      }
    }, 200);
  }, [pageCount, background, saveState]);

  const addNewPage = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const currentPageCount = pageCount;
    const newPageCount = pageCount + 1;
    
    console.log(`📄 Adding new page: ${currentPageCount} → ${newPageCount}`);
    console.log(`📐 Current canvas dimensions: ${canvas.getWidth()}x${canvas.getHeight()}`);
    
    // Add page separator line at the end of current pages
    const separatorX = pageCount * PAGE_WIDTH;
    const separator = new fabric.Line([separatorX, 0, separatorX, PAGE_HEIGHT], {
      stroke: '#e2e8f0',
      strokeWidth: PAGE_SEPARATOR_WIDTH,
      strokeDashArray: [10, 5],
      selectable: false,
      evented: false,
      excludeFromExport: true,
      id: `separator_${pageCount}`
    });
    
    canvas.add(separator);
    
    // Add page border for the new page to visualize the 1080x1080 area
    const pageBorderX = pageCount * PAGE_WIDTH;
    const pageBorder = new fabric.Rect({
      left: pageBorderX,
      top: 0,
      width: PAGE_WIDTH,
      height: PAGE_HEIGHT,
      fill: 'transparent',
      stroke: '#cbd5e1',
      strokeWidth: 1,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      excludeFromExport: true,
      id: `page_border_${newPageCount}`
    });
    
    canvas.add(pageBorder);
    
    // Update state (canvas width will be updated by useEffect)
    setPageCount(newPageCount);
    setPageSeparators(prev => [...prev, separator]);
    
    // Update canvas background if needed
    canvas.setBackgroundColor(background, canvas.renderAll.bind(canvas));
    
    setSnackbar({ 
      open: true, 
      message: `Page ${newPageCount} added successfully!`, 
      severity: 'success' 
    });
    
    // Save state for undo/redo
    setTimeout(() => saveState(), 100);
  }, [pageCount, background, saveState]);

  const getPageNumber = useCallback((x) => {
    return Math.floor(x / PAGE_WIDTH) + 1;
  }, []);

  const getPageBounds = useCallback((pageNum) => {
    return {
      left: (pageNum - 1) * PAGE_WIDTH,
      right: pageNum * PAGE_WIDTH,
      top: 0,
      bottom: PAGE_HEIGHT
    };
  }, []);

  const exportAllPages = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const pages = [];
    
    // Hide separators and page borders during export
    const separatorObjects = canvas.getObjects().filter(obj => obj.id && obj.id.startsWith('separator_'));
    const pageBorderObjects = canvas.getObjects().filter(obj => obj.id && obj.id.startsWith('page_border_'));
    
    separatorObjects.forEach(sep => sep.set('visible', false));
    pageBorderObjects.forEach(border => border.set('visible', false));
    canvas.renderAll();
    
    for (let i = 1; i <= pageCount; i++) {
      const pageBounds = getPageBounds(i);
      
      // Create a temporary canvas for this page
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = PAGE_WIDTH;
      tempCanvas.height = PAGE_HEIGHT;
      const tempCtx = tempCanvas.getContext('2d');
      
      // Fill background
      tempCtx.fillStyle = background;
      tempCtx.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);
      
      // Get the main canvas as image and crop the page area
      const mainCanvasDataURL = canvas.toDataURL({
        left: pageBounds.left,
        top: pageBounds.top,
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        format: 'png'
      });
      
      pages.push({
        pageNumber: i,
        dataURL: mainCanvasDataURL,
        bounds: pageBounds
      });
    }
    
    // Show separators and page borders again
    separatorObjects.forEach(sep => sep.set('visible', true));
    pageBorderObjects.forEach(border => border.set('visible', true));
    canvas.renderAll();
    
    // Download each page
    pages.forEach((page, index) => {
      const link = document.createElement('a');
      link.download = `${documentName}_page_${page.pageNumber}.png`;
      link.href = page.dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
    
    setSnackbar({ 
      open: true, 
      message: `All ${pageCount} pages exported successfully!`, 
      severity: 'success' 
    });
  }, [pageCount, getPageBounds, background, documentName]);

  // Enhanced PDF export with visual accuracy
  const exportToPDF = useCallback(async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [PAGE_WIDTH, PAGE_HEIGHT],
        compress: true
      });

      // Hide separators and page borders during export
      const separatorObjects = canvas.getObjects().filter(obj => obj.id && obj.id.startsWith('separator_'));
      const pageBorderObjects = canvas.getObjects().filter(obj => obj.id && obj.id.startsWith('page_border_'));
      
      separatorObjects.forEach(sep => sep.set('visible', false));
      pageBorderObjects.forEach(border => border.set('visible', false));
      canvas.renderAll();

      let isFirstPage = true;

      // Process each page
      for (let i = 1; i <= pageCount; i++) {
        if (!isFirstPage) {
          pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        }
        isFirstPage = false;

        const pageBounds = getPageBounds(i);
        
        // Create a high-quality temporary canvas for this specific page
        const scaleFactor = 2; // 2x resolution for crisp text
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = PAGE_WIDTH * scaleFactor;
        tempCanvas.height = PAGE_HEIGHT * scaleFactor;
        const tempCtx = tempCanvas.getContext('2d');
        
        // Scale the context for high-DPI rendering
        tempCtx.scale(scaleFactor, scaleFactor);
        
        // Set maximum quality rendering
        tempCtx.imageSmoothingEnabled = true;
        tempCtx.imageSmoothingQuality = 'high';
        
        // Handle background - improved gradient support
        if (background && background !== 'transparent') {
          if (typeof background === 'string' && background.startsWith('#')) {
            tempCtx.fillStyle = background;
            tempCtx.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);
          } else if (typeof background === 'object' && background.type === 'linear') {
            // Handle gradient backgrounds
            const gradient = tempCtx.createLinearGradient(0, 0, PAGE_WIDTH, PAGE_HEIGHT);
            background.colorStops.forEach(stop => {
              gradient.addColorStop(stop.offset, stop.color);
            });
            tempCtx.fillStyle = gradient;
            tempCtx.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);
          }
        }

        // IMPROVED APPROACH: Use Fabric's built-in toDataURL for the specific page area
        // This ensures all transformations, rotations, and complex objects are rendered correctly
        const pageImageData = canvas.toDataURL({
          format: 'png',
          quality: 1.0,
          multiplier: 2, // Higher resolution
          left: pageBounds.left,
          top: pageBounds.top,
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT
        });

        // Add the rendered image to PDF
        pdf.addImage(pageImageData, 'PNG', 0, 0, PAGE_WIDTH, PAGE_HEIGHT, undefined, 'FAST');
      }

      // Restore visibility
      separatorObjects.forEach(sep => sep.set('visible', true));
      pageBorderObjects.forEach(border => border.set('visible', true));
      canvas.renderAll();

      // Save the PDF
      pdf.save(`${documentName}_carousel.pdf`);
      
      setSnackbar({ 
        open: true, 
        message: `High-quality PDF exported successfully!`, 
        severity: 'success' 
      });

    } catch (error) {
      console.error('PDF export error:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error exporting PDF. Please try again.', 
        severity: 'error' 
      });
    }
  }, [pageCount, getPageBounds, background, documentName]);

  // Helper function to render text to canvas for high-quality PDF
  const renderTextToCanvas = async (ctx, textObj, x, y) => {
    const fontSize = (textObj.fontSize || 16) * (textObj.scaleY || 1);
    const fontFamily = textObj.fontFamily || 'Poppins';
    const fontWeight = textObj.fontWeight || 'normal';
    const textColor = textObj.fill || '#000000';
    const textContent = textObj.text || '';
    const opacity = textObj.opacity || 1;

    if (!textContent || textContent.trim() === '') return;

    // Set high-quality text rendering
    ctx.textRenderingOptimization = 'optimizeQuality';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Handle text shadow first
    if (textObj.shadow && textObj.shadow.color) {
      ctx.save();
      ctx.globalAlpha = opacity * 0.5; // Shadow with reduced opacity
      ctx.fillStyle = textObj.shadow.color;
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      ctx.textBaseline = 'top';
      
      const shadowX = x + (textObj.shadow.offsetX || 2);
      const shadowY = y + (textObj.shadow.offsetY || 2);
      
      // Render shadow text with same alignment logic
      renderTextContent(ctx, textObj, textContent, shadowX, shadowY, fontSize);
      ctx.restore();
    }

    // Set main text properties with opacity
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.fillStyle = textColor;
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textBaseline = 'top';
    
    // Render main text
    renderTextContent(ctx, textObj, textContent, x, y, fontSize);
    ctx.restore();
  };

  // Helper function to render text content with proper alignment
  const renderTextContent = (ctx, textObj, textContent, x, y, fontSize) => {
    const textAlign = textObj.textAlign || textObj.align || 'left';
    const objectWidth = (textObj.width || 0) * (textObj.scaleX || 1);
    
    // Calculate actual X position based on alignment and origin
    let actualX = x;
    if (textAlign === 'center') {
      if (textObj.originX === 'center') {
        actualX = x; // X is already the center point
        ctx.textAlign = 'center';
      } else {
        actualX = x + objectWidth / 2;
        ctx.textAlign = 'center';
      }
    } else if (textAlign === 'right') {
      actualX = x + objectWidth;
      ctx.textAlign = 'right';
    } else {
      ctx.textAlign = 'left';
    }

    // Handle multi-line text
    if (textContent.includes('\n')) {
      const lines = textContent.split('\n');
      const lineHeight = fontSize * (textObj.lineHeight || 1.2);
      
      lines.forEach((line, index) => {
        if (line.trim()) {
          const yPos = y + (index * lineHeight);
          ctx.fillText(line, actualX, yPos);
        }
      });
    } else {
      // Handle text wrapping for Textbox elements
      if (textObj.type === 'textbox' && objectWidth > 0) {
        const words = textContent.split(' ');
        const maxWidth = objectWidth;
        let line = '';
        let yPos = y;
        const lineHeight = fontSize * (textObj.lineHeight || 1.2);

        words.forEach((word, index) => {
          const testLine = line + word + ' ';
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > maxWidth && index > 0) {
            if (line.trim()) {
              ctx.fillText(line.trim(), actualX, yPos);
            }
            line = word + ' ';
            yPos += lineHeight;
          } else {
            line = testLine;
          }
        });
        
        if (line.trim()) {
          ctx.fillText(line.trim(), actualX, yPos);
        }
      } else {
        ctx.fillText(textContent, actualX, y);
      }
    }
  };

  // Helper function to render text with better quality
  const renderTextToPDF = async (pdf, textObj, x, y) => {
    const fontSize = (textObj.fontSize || 16) * (textObj.scaleY || 1);
    const fontFamily = textObj.fontFamily || 'Poppins';
    const fontWeight = textObj.fontWeight || 'normal';
    const textColor = textObj.fill || '#000000';
    const textContent = textObj.text || '';

    if (!textContent || textContent.trim() === '') return;

    // Handle opacity and color
    const rgb = hexToRgb(textColor);
    const opacity = textObj.opacity || 1;
    
    // Set text color with opacity (jsPDF doesn't support direct text opacity)
    if (opacity < 1) {
      // Blend with white background for opacity effect
      const blendedRgb = {
        r: Math.round(rgb.r * opacity + 255 * (1 - opacity)),
        g: Math.round(rgb.g * opacity + 255 * (1 - opacity)),
        b: Math.round(rgb.b * opacity + 255 * (1 - opacity))
      };
      pdf.setTextColor(blendedRgb.r, blendedRgb.g, blendedRgb.b);
    } else {
      pdf.setTextColor(rgb.r, rgb.g, rgb.b);
    }

    // Handle text shadow (simple implementation)
    const hasShadow = textObj.shadow && textObj.shadow.color;
    let shadowRendered = false;

    // Enhanced font mapping for better typography
    let jsPDFFont = 'helvetica';
    if (fontFamily.toLowerCase().includes('poppins')) {
      jsPDFFont = 'helvetica'; // Poppins fallback to Helvetica (similar sans-serif)
    } else if (fontFamily.toLowerCase().includes('times') || fontFamily.toLowerCase().includes('serif')) {
      jsPDFFont = 'times';
    } else if (fontFamily.toLowerCase().includes('courier') || fontFamily.toLowerCase().includes('mono')) {
      jsPDFFont = 'courier';
    } else if (fontFamily.toLowerCase().includes('georgia')) {
      jsPDFFont = 'times'; // Georgia fallback to Times
    }

    // Enhanced font weight handling
    let style = 'normal';
    const weightNum = parseInt(fontWeight);
    if (fontWeight === 'bold' || fontWeight === '700' || fontWeight === '900' || weightNum >= 700) {
      style = 'bold';
    }
    
    pdf.setFont(jsPDFFont, style);
    pdf.setFontSize(fontSize);

    // Calculate proper positioning based on Fabric.js object properties
    const objectWidth = (textObj.width || 0) * (textObj.scaleX || 1);
    const objectHeight = (textObj.height || fontSize) * (textObj.scaleY || 1);
    const textAlign = textObj.textAlign || textObj.align || 'left';

    // Get the actual bounding box from Fabric.js object
    let textX = x;
    let textY = y;

    // Handle different text alignment scenarios
    // For center-aligned text, the X coordinate in templates is the center point
    if (textAlign === 'center') {
      // Templates define center-aligned text with X as center point
      // When loaded, Fabric.js sets originX: 'center', so textObj.left IS the center
      textX = x; // Use the position as-is for center alignment
    } else if (textAlign === 'right') {
      if (textObj.originX === 'right') {
        textX = x;
      } else {
        textX = x + objectWidth;
      }
    } else {
      // Left alignment
      if (textObj.originX === 'center') {
        textX = x - objectWidth / 2;
      } else if (textObj.originX === 'right') {
        textX = x - objectWidth;
      } else {
        textX = x;
      }
    }

    // Handle Y position based on originY
    if (textObj.originY === 'center') {
      textY = y - objectHeight / 2 + fontSize * 0.3;
    } else if (textObj.originY === 'bottom') {
      textY = y - objectHeight + fontSize * 0.3;
    } else {
      // Default top origin
      textY = y + fontSize * 0.8;
    }

    // Handle multi-line text
    if (textContent.includes('\n')) {
      const lines = textContent.split('\n');
      const lineHeight = fontSize * (textObj.lineHeight || 1.2);
      
      lines.forEach((line, index) => {
        if (line.trim()) {
          const yPos = textY + (index * lineHeight);
          
          try {
            // Render shadow first if it exists
            if (hasShadow && !shadowRendered) {
              const shadowRgb = hexToRgb(textObj.shadow.color);
              pdf.setTextColor(shadowRgb.r, shadowRgb.g, shadowRgb.b);
              const shadowX = textX + (textObj.shadow.offsetX || 0);
              const shadowY = yPos + (textObj.shadow.offsetY || 0);
              pdf.text(line, shadowX, shadowY, { 
                align: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'right' : 'left'
              });
              // Reset to original color
              if (opacity < 1) {
                const blendedRgb = {
                  r: Math.round(rgb.r * opacity + 255 * (1 - opacity)),
                  g: Math.round(rgb.g * opacity + 255 * (1 - opacity)),
                  b: Math.round(rgb.b * opacity + 255 * (1 - opacity))
                };
                pdf.setTextColor(blendedRgb.r, blendedRgb.g, blendedRgb.b);
              } else {
                pdf.setTextColor(rgb.r, rgb.g, rgb.b);
              }
            }
            
            // Render main text
            pdf.text(line, textX, yPos, { 
              align: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'right' : 'left'
            });
          } catch (error) {
            console.warn('Error rendering line:', line, error);
          }
        }
      });
    } else {
      // Handle text wrapping for Textbox elements
      if (textObj.type === 'textbox' && objectWidth > 0) {
        const words = textContent.split(' ');
        const maxWidth = objectWidth;
        let line = '';
        let yPos = textY;
        const lineHeight = fontSize * (textObj.lineHeight || 1.2);

        words.forEach((word, index) => {
          const testLine = line + word + ' ';
          const textWidth = pdf.getTextWidth(testLine);
          
          if (textWidth > maxWidth && index > 0) {
                         if (line.trim()) {
               try {
                 // Render shadow for wrapped text
                 if (hasShadow && !shadowRendered) {
                   const shadowRgb = hexToRgb(textObj.shadow.color);
                   pdf.setTextColor(shadowRgb.r, shadowRgb.g, shadowRgb.b);
                   const shadowX = textX + (textObj.shadow.offsetX || 0);
                   const shadowY = yPos + (textObj.shadow.offsetY || 0);
                   pdf.text(line.trim(), shadowX, shadowY, { 
                     align: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'right' : 'left'
                   });
                   // Reset to original color
                   if (opacity < 1) {
                     const blendedRgb = {
                       r: Math.round(rgb.r * opacity + 255 * (1 - opacity)),
                       g: Math.round(rgb.g * opacity + 255 * (1 - opacity)),
                       b: Math.round(rgb.b * opacity + 255 * (1 - opacity))
                     };
                     pdf.setTextColor(blendedRgb.r, blendedRgb.g, blendedRgb.b);
                   } else {
                     pdf.setTextColor(rgb.r, rgb.g, rgb.b);
                   }
                   shadowRendered = true; // Only render shadow once per text object
                 }
                 
                 pdf.text(line.trim(), textX, yPos, { 
                   align: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'right' : 'left'
                 });
               } catch (error) {
                 console.warn('Error rendering wrapped line:', line, error);
               }
             }
            line = word + ' ';
            yPos += lineHeight;
          } else {
            line = testLine;
          }
        });
        
        if (line.trim()) {
          try {
            pdf.text(line.trim(), textX, yPos, { 
              align: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'right' : 'left'
            });
          } catch (error) {
            console.warn('Error rendering final line:', line, error);
          }
        }
      } else {
                 try {
           // Render shadow for single line text
           if (hasShadow) {
             const shadowRgb = hexToRgb(textObj.shadow.color);
             pdf.setTextColor(shadowRgb.r, shadowRgb.g, shadowRgb.b);
             const shadowX = textX + (textObj.shadow.offsetX || 0);
             const shadowY = textY + (textObj.shadow.offsetY || 0);
             pdf.text(textContent, shadowX, shadowY, { 
               align: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'right' : 'left'
             });
             // Reset to original color
             if (opacity < 1) {
               const blendedRgb = {
                 r: Math.round(rgb.r * opacity + 255 * (1 - opacity)),
                 g: Math.round(rgb.g * opacity + 255 * (1 - opacity)),
                 b: Math.round(rgb.b * opacity + 255 * (1 - opacity))
               };
               pdf.setTextColor(blendedRgb.r, blendedRgb.g, blendedRgb.b);
             } else {
               pdf.setTextColor(rgb.r, rgb.g, rgb.b);
             }
           }
           
           pdf.text(textContent, textX, textY, { 
             align: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'right' : 'left'
           });
         } catch (error) {
           console.warn('Error rendering single line text:', textContent, error);
         }
      }
    }
  };

  // Helper function to render rectangles
  const renderRectangleToPDF = async (ctx, rectObj, x, y) => {
    const width = (rectObj.width || 100) * (rectObj.scaleX || 1);
    const height = (rectObj.height || 100) * (rectObj.scaleY || 1);
    const opacity = rectObj.opacity || 1;

    // Save context for opacity
    ctx.save();
    ctx.globalAlpha = opacity;

    // Handle fill
    if (rectObj.fill && rectObj.fill !== 'transparent') {
      if (typeof rectObj.fill === 'string') {
        ctx.fillStyle = rectObj.fill;
      } else if (rectObj.fill && typeof rectObj.fill === 'object') {
        console.log(`🎨 Processing gradient object:`, rectObj.fill);
        
        // Handle Fabric.js gradient objects - check multiple possible structures
        let gradient;
        let gradientData = rectObj.fill;
        
        // Check if it's a Fabric.js Gradient object with internal structure
        if (rectObj.fill.colorStops || rectObj.fill.gradientUnits) {
          gradientData = rectObj.fill;
        } else if (rectObj.fill.source && rectObj.fill.source.colorStops) {
          gradientData = rectObj.fill.source;
        }
        
        console.log(`🎨 Gradient data:`, gradientData);
        
                 if (gradientData.type === 'linear' || (gradientData.gradientUnits && gradientData.x1 !== undefined)) {
           // Handle linear gradient with proper coordinate transformation
           const coords = gradientData.coords || {
             x1: gradientData.x1 || 0,
             y1: gradientData.y1 || 0,
             x2: gradientData.x2 || width,
             y2: gradientData.y2 || height
           };
           
           // Transform absolute canvas coordinates to relative page coordinates
           // The original object position (rectObj.left) tells us which page this belongs to
           const pageOffset = Math.floor((rectObj.left || 0) / 1080) * 1080;
           
           const relativeCoords = {
             x1: (coords.x1 || 0) - pageOffset,
             y1: coords.y1 || 0,
             x2: (coords.x2 || width) - pageOffset,
             y2: coords.y2 || height
           };
           
           gradient = ctx.createLinearGradient(
             relativeCoords.x1, 
             relativeCoords.y1, 
             relativeCoords.x2, 
             relativeCoords.y2
           );
           
           console.log(`🎨 Transformed coords from`, coords, `to`, relativeCoords, `(pageOffset: ${pageOffset})`);
          
                 } else if (gradientData.type === 'radial') {
           // Handle radial gradient with proper coordinate transformation
           const coords = gradientData.coords || { 
             x1: width/2, y1: height/2, r1: 0, 
             x2: width/2, y2: height/2, r2: Math.max(width, height)/2 
           };
           
           // Transform absolute canvas coordinates to relative page coordinates
           const pageOffset = Math.floor((rectObj.left || 0) / 1080) * 1080;
           
           const relativeCoords = {
             x1: (coords.x1 || width/2) - pageOffset,
             y1: coords.y1 || height/2,
             r1: coords.r1 || 0,
             x2: (coords.x2 || width/2) - pageOffset,
             y2: coords.y2 || height/2,
             r2: coords.r2 || Math.max(width, height)/2
           };
           
           gradient = ctx.createRadialGradient(
             relativeCoords.x1, 
             relativeCoords.y1, 
             relativeCoords.r1,
             relativeCoords.x2, 
             relativeCoords.y2, 
             relativeCoords.r2
           );
           
           console.log(`🎨 Radial gradient transformed from`, coords, `to`, relativeCoords);
        }
        
        // Add color stops
        const colorStops = gradientData.colorStops || gradientData.stops;
        if (gradient && colorStops) {
          console.log(`🎨 Adding color stops:`, colorStops);
          colorStops.forEach(stop => {
            const offset = stop.offset !== undefined ? stop.offset : stop.position || 0;
            const color = stop.color || stop.stop || '#000000';
            gradient.addColorStop(offset, color);
          });
          ctx.fillStyle = gradient;
        } else {
          console.log(`❌ No gradient or color stops found, trying fabric.js direct render`);
          
          // Fallback: Try to render the object using Fabric.js
          try {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempFabric = new fabric.Canvas(tempCanvas);
            
            // Clone and render just this object
            const clonedObj = fabric.util.object.clone(rectObj);
            clonedObj.set({
              left: 0,
              top: 0,
              width: width,
              height: height
            });
            
            tempFabric.add(clonedObj);
            tempFabric.renderAll();
            
            // Copy the rendered result
            ctx.drawImage(tempCanvas, x, y);
            tempFabric.dispose();
            return; // Skip the manual drawing
          } catch (error) {
            console.log(`❌ Fabric.js render failed:`, error);
            ctx.fillStyle = '#cccccc'; // Final fallback color
          }
        }
      }

      // Draw rectangle with rounded corners if specified
      if (rectObj.rx || rectObj.ry) {
        const radius = rectObj.rx || rectObj.ry || 0;
        ctx.beginPath();
        ctx.roundRect(x, y, width, height, radius);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, width, height);
      }
    }

    // Handle stroke
    if (rectObj.stroke && rectObj.strokeWidth > 0) {
      ctx.strokeStyle = rectObj.stroke;
      ctx.lineWidth = rectObj.strokeWidth;
      if (rectObj.rx || rectObj.ry) {
        const radius = rectObj.rx || rectObj.ry || 0;
        ctx.beginPath();
        ctx.roundRect(x, y, width, height, radius);
        ctx.stroke();
      } else {
        ctx.strokeRect(x, y, width, height);
      }
    }

    ctx.restore();
  };

  // Helper function to render circles
  const renderCircleToPDF = async (ctx, circleObj, x, y) => {
    const radius = (circleObj.radius || 50) * Math.max(circleObj.scaleX || 1, circleObj.scaleY || 1);
    const centerX = x + radius;
    const centerY = y + radius;
    const opacity = circleObj.opacity || 1;

    // Save context for opacity
    ctx.save();
    ctx.globalAlpha = opacity;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);

    // Handle fill
    if (circleObj.fill && circleObj.fill !== 'transparent') {
      if (typeof circleObj.fill === 'string') {
        ctx.fillStyle = circleObj.fill;
      } else if (circleObj.fill && typeof circleObj.fill === 'object' && circleObj.fill.colorStops) {
        // Handle gradient fills for circles
        let gradient;
        if (circleObj.fill.type === 'radial') {
          gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
        } else {
          gradient = ctx.createLinearGradient(centerX - radius, centerY - radius, centerX + radius, centerY + radius);
        }
        
        circleObj.fill.colorStops.forEach(stop => {
          gradient.addColorStop(stop.offset, stop.color);
        });
        ctx.fillStyle = gradient;
      }
      ctx.fill();
    }

    // Handle stroke
    if (circleObj.stroke && circleObj.strokeWidth > 0) {
      ctx.strokeStyle = circleObj.stroke;
      ctx.lineWidth = circleObj.strokeWidth;
      ctx.stroke();
    }

    ctx.restore();
  };

  // Helper function to render images
  const renderImageToPDF = async (ctx, imageObj, x, y) => {
    if (imageObj.getSrc && imageObj.getSrc()) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const width = (imageObj.width || img.width) * (imageObj.scaleX || 1);
          const height = (imageObj.height || img.height) * (imageObj.scaleY || 1);
          ctx.drawImage(img, x, y, width, height);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = imageObj.getSrc();
      });
    }
  };

  // Helper function to render complex objects
  const renderComplexObjectToPDF = async (ctx, obj, x, y, canvas) => {
    // For complex objects, we'll render them individually
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 200;
    tempCanvas.height = 200;
    const tempFabric = new fabric.Canvas(tempCanvas);
    
    // Clone the object and render it
    const clonedObj = fabric.util.object.clone(obj);
    clonedObj.set({
      left: 100,
      top: 100
    });
    
    tempFabric.add(clonedObj);
    tempFabric.renderAll();
    
    // Draw the rendered object
    ctx.drawImage(tempCanvas, x, y);
    tempFabric.dispose();
  };

  // Element creation functions

  const addHeading = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const text = new fabric.Textbox('Your Professional Title', {
      left: canvasSize.width / 2 - 200,
      top: canvasSize.height / 2 - 30,
      width: 400,
      fontSize: 48,
      fontFamily: 'Poppins',
      fontWeight: 'bold', // Using CSS keyword instead of numeric value
      fill: selectedColor,
      textAlign: 'left',
      splitByGrapheme: false, // Set to false for word-based wrapping
      id: 'heading_' + Date.now()
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    setSelectedObject(text);
    setFontSizeInput(text.fontSize);
    setSnackbar({ open: true, message: 'Heading added successfully!', severity: 'success' });
  }, [selectedColor, canvasSize]);

  const addParagraph = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const text = new fabric.Textbox('Add your professional description or value proposition here. Make it compelling and engaging.', {
      left: canvasSize.width / 2 - 200,
      top: canvasSize.height / 2 - 15,
      width: 400,
      fontSize: 18,
      fontFamily: 'Poppins',
      fontWeight: 'normal', // Using CSS keyword instead of numeric value
      fill: selectedColor,
      textAlign: 'left',
      splitByGrapheme: false, // Set to false for word-based wrapping
      id: 'paragraph_' + Date.now()
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    setSelectedObject(text);
    setFontSizeInput(text.fontSize);
    setSnackbar({ open: true, message: 'Paragraph added successfully!', severity: 'success' });
  }, [selectedColor, canvasSize]);

  const addShape = useCallback((shapeType) => {
    const canvas = fabricCanvasRef.current;
    let shape;

    switch (shapeType) {
      case 'rectangle':
        shape = new fabric.Rect({
          left: canvasSize.width / 2 - 60,
          top: canvasSize.height / 2 - 40,
          width: 120,
          height: 80,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          id: 'rect_' + Date.now()
        });
        break;
      case 'circle':
        shape = new fabric.Circle({
          left: canvasSize.width / 2 - 50,
          top: canvasSize.height / 2 - 50,
          radius: 50,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          id: 'circle_' + Date.now()
        });
        break;
      case 'line':
        shape = new fabric.Line([0, 0, 100, 0], {
          left: canvasSize.width / 2 - 50,
          top: canvasSize.height / 2,
          stroke: selectedColor,
          strokeWidth: 3,
          fill: '',
          id: 'line_' + Date.now()
        });
        break;
      case 'arrow':
        // Create arrow using path
        const arrowPath = 'M 0 10 L 80 10 L 70 0 L 100 15 L 70 30 L 80 20 L 0 20 Z';
        shape = new fabric.Path(arrowPath, {
          left: canvasSize.width / 2 - 50,
          top: canvasSize.height / 2 - 15,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 1,
          scaleX: 1,
          scaleY: 1,
          id: 'arrow_' + Date.now()
        });
        break;
      case 'star':
        // Create a star path
        const starPath = 'M 50 0 L 61.8 35 L 100 35 L 69.1 57.5 L 80.9 92.5 L 50 70 L 19.1 92.5 L 30.9 57.5 L 0 35 L 38.2 35 Z';
        shape = new fabric.Path(starPath, {
          left: canvasSize.width / 2 - 50,
          top: canvasSize.height / 2 - 50,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 1,
          scaleX: 0.8,
          scaleY: 0.8,
          id: 'star_' + Date.now()
        });
        break;
      case 'polygon':
        // Create hexagon
        const hexPoints = [];
        const hexRadius = 50;
        for (let i = 0; i < 6; i++) {
          const angle = (i * Math.PI) / 3;
          hexPoints.push({
            x: Math.cos(angle) * hexRadius,
            y: Math.sin(angle) * hexRadius
          });
        }
        shape = new fabric.Polygon(hexPoints, {
          left: CANVAS_WIDTH / 2,
          top: CANVAS_HEIGHT / 2,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          originX: 'center',
          originY: 'center',
          id: 'polygon_' + Date.now()
        });
        break;
      case 'ellipse':
        shape = new fabric.Ellipse({
          left: CANVAS_WIDTH / 2 - 60,
          top: CANVAS_HEIGHT / 2 - 40,
          rx: 60,
          ry: 40,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          id: 'ellipse_' + Date.now()
        });
        break;
      case 'wedge':
        // Create wedge using path (pie slice)
        const wedgePath = 'M 50 50 L 50 0 A 50 50 0 0 1 85.36 64.64 Z';
        shape = new fabric.Path(wedgePath, {
          left: CANVAS_WIDTH / 2 - 50,
          top: CANVAS_HEIGHT / 2 - 50,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          id: 'wedge_' + Date.now()
        });
        break;
      case 'arc':
        // Create arc using path
        const arcPath = 'M 10 50 A 40 40 0 0 1 90 50';
        shape = new fabric.Path(arcPath, {
          left: CANVAS_WIDTH / 2 - 50,
          top: CANVAS_HEIGHT / 2 - 25,
          fill: '',
          stroke: selectedColor,
          strokeWidth: 4,
          id: 'arc_' + Date.now()
        });
        break;
      case 'ring':
        // Create ring using two circles
        const outerCircle = new fabric.Circle({
          radius: 50,
          fill: selectedColor,
          originX: 'center',
          originY: 'center'
        });
        const innerCircle = new fabric.Circle({
          radius: 30,
          fill: 'white',
          originX: 'center',
          originY: 'center'
        });
        shape = new fabric.Group([outerCircle, innerCircle], {
          left: CANVAS_WIDTH / 2,
          top: CANVAS_HEIGHT / 2,
          originX: 'center',
          originY: 'center',
          id: 'ring_' + Date.now()
        });
        break;
      default:
        return;
    }

    canvas.add(shape);
    canvas.setActiveObject(shape);
    canvas.renderAll();
    setSelectedObject(shape);
    setSnackbar({ open: true, message: `${shapeType} added successfully!`, severity: 'success' });
  }, [selectedColor]);

  const addImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          fabric.Image.fromURL(event.target.result, (img) => {
            const canvas = fabricCanvasRef.current;
            
            // Smart scaling
            const maxWidth = 400;
            const maxHeight = 300;
            const scaleX = maxWidth / img.width;
            const scaleY = maxHeight / img.height;
            const scale = Math.min(scaleX, scaleY, 1);
            
            img.set({
              left: CANVAS_WIDTH / 2 - (img.width * scale) / 2,
              top: CANVAS_HEIGHT / 2 - (img.height * scale) / 2,
              scaleX: scale,
              scaleY: scale,
              id: 'image_' + Date.now()
            });

            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();
            setSnackbar({ open: true, message: 'Image added successfully!', severity: 'success' });
          });
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }, []);

  // Template application removed - now handled by CarouselLeftSidebar component

  // Delete selected object
  const deleteSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
      canvas.renderAll();
      setSelectedObject(null);
      setSnackbar({ open: true, message: 'Object deleted!', severity: 'info' });
    }
  }, []);

  // Group selected elements
  const groupSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const activeObjects = canvas.getActiveObjects();
    
    if (activeObjects.length > 1) {
      // Get the current selection's properties
      const selection = canvas.getActiveObject();
      
      // Clone objects to preserve their states
      const clonedObjects = activeObjects.map(obj => {
        const clone = fabric.util.object.clone(obj);
        // Ensure the clone has all the necessary properties
        clone.set({
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX || 1,
          scaleY: obj.scaleY || 1,
          angle: obj.angle || 0,
          opacity: obj.opacity || 1,
          originX: obj.originX || 'left',
          originY: obj.originY || 'top'
        });
        return clone;
      });

      // Create a group from cloned objects
      const group = new fabric.Group(clonedObjects, {
        id: 'group_' + Date.now(),
        left: selection.left,
        top: selection.top,
        width: selection.width,
        height: selection.height,
        scaleX: selection.scaleX || 1,
        scaleY: selection.scaleY || 1,
        angle: selection.angle || 0,
        opacity: selection.opacity || 1,
        originX: 'left',
        originY: 'top',
        selectable: true,
        hasControls: true
      });
      
      // Remove original objects
      activeObjects.forEach(obj => {
        canvas.remove(obj);
      });
      
      // Add the group
      canvas.add(group);
      canvas.setActiveObject(group);
      canvas.renderAll();
      
      setSnackbar({ open: true, message: 'Elements grouped successfully!', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Please select multiple elements to group', severity: 'warning' });
    }
  }, []);

  // Ungroup selected group
  const ungroupSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const activeObject = canvas.getActiveObject();
    
    if (activeObject && activeObject.type === 'group') {
      // Get all objects in the group
      const items = activeObject.getObjects();
      
      // Get group's properties
      const { left, top, width, height, scaleX, scaleY, angle } = activeObject;
      
      // Calculate group's bounds
      const boundingRect = activeObject.getBoundingRect(true);
      const groupLeft = boundingRect.left;
      const groupTop = boundingRect.top;
      
      // Remove the group but keep objects
      const ungroupedObjects = [];
      items.forEach(obj => {
        // Calculate the absolute position of the object
        const matrix = activeObject.calcTransformMatrix();
        const newPoint = fabric.util.transformPoint({
          x: obj.left,
          y: obj.top
        }, matrix);
        
        console.log(`🔍 Ungrouping ${obj.type}:`, {
          originalPos: { left: obj.left, top: obj.top },
          newPos: newPoint,
          radius: obj.radius,
          fill: obj.fill,
          stroke: obj.stroke
        });
        
        let newObj;
        
        // Create new objects instead of cloning to avoid property loss
        if (obj.type === 'circle') {
          newObj = new fabric.Circle({
            left: newPoint.x,
            top: newPoint.y,
            radius: obj.radius || 50,
            fill: obj.fill || '#000000',
            stroke: obj.stroke || null,
            strokeWidth: obj.strokeWidth || 0,
            opacity: obj.opacity || 1,
            scaleX: (obj.scaleX || 1) * (scaleX || 1),
            scaleY: (obj.scaleY || 1) * (scaleY || 1),
            angle: (obj.angle || 0) + (angle || 0),
            originX: obj.originX || 'left',
            originY: obj.originY || 'top',
            id: obj.id || (`circle_${Date.now()}_${Math.round(newPoint.x)}_${Math.round(newPoint.y)}`),
            selectable: true,
            evented: true
          });
        } else if (obj.type === 'rect') {
          newObj = new fabric.Rect({
            left: newPoint.x,
            top: newPoint.y,
            width: obj.width || 100,
            height: obj.height || 100,
            fill: obj.fill || '#000000',
            stroke: obj.stroke || null,
            strokeWidth: obj.strokeWidth || 0,
            opacity: obj.opacity || 1,
            scaleX: (obj.scaleX || 1) * (scaleX || 1),
            scaleY: (obj.scaleY || 1) * (scaleY || 1),
            angle: (obj.angle || 0) + (angle || 0),
            rx: obj.rx || 0,
            ry: obj.ry || 0,
            id: obj.id || (`rect_${Date.now()}_${Math.round(newPoint.x)}_${Math.round(newPoint.y)}`),
            selectable: true,
            evented: true
          });
        } else if (obj.type === 'textbox') {
          newObj = new fabric.Textbox(obj.text || 'Text', {
            left: newPoint.x,
            top: newPoint.y,
            width: obj.width || 200,
            fontSize: obj.fontSize || 20,
            fontFamily: obj.fontFamily || 'Arial',
            fontWeight: obj.fontWeight || 'normal',
            fill: obj.fill || '#000000',
            textAlign: obj.textAlign || 'left',
            lineHeight: obj.lineHeight || 1.2,
            opacity: obj.opacity || 1,
            scaleX: (obj.scaleX || 1) * (scaleX || 1),
            scaleY: (obj.scaleY || 1) * (scaleY || 1),
            angle: (obj.angle || 0) + (angle || 0),
            id: obj.id || (`textbox_${Date.now()}_${Math.round(newPoint.x)}_${Math.round(newPoint.y)}`),
            selectable: true,
            evented: true
          });
        } else if (obj.type === 'text') {
          newObj = new fabric.Text(obj.text || 'Text', {
            left: newPoint.x,
            top: newPoint.y,
            fontSize: obj.fontSize || 20,
            fontFamily: obj.fontFamily || 'Arial',
            fontWeight: obj.fontWeight || 'normal',
            fill: obj.fill || '#000000',
            textAlign: obj.textAlign || 'left',
            opacity: obj.opacity || 1,
            scaleX: (obj.scaleX || 1) * (scaleX || 1),
            scaleY: (obj.scaleY || 1) * (scaleY || 1),
            angle: (obj.angle || 0) + (angle || 0),
            id: obj.id || (`text_${Date.now()}_${Math.round(newPoint.x)}_${Math.round(newPoint.y)}`),
            selectable: true,
            evented: true
          });
        } else if (obj.type === 'image') {
          // For images, we need to handle them specially
          newObj = new fabric.Image(obj.getElement(), {
            left: newPoint.x,
            top: newPoint.y,
            scaleX: (obj.scaleX || 1) * (scaleX || 1),
            scaleY: (obj.scaleY || 1) * (scaleY || 1),
            angle: (obj.angle || 0) + (angle || 0),
            opacity: obj.opacity || 1,
            cropX: obj.cropX || 0,
            cropY: obj.cropY || 0,
            id: obj.id || (`image_${Date.now()}_${Math.round(newPoint.x)}_${Math.round(newPoint.y)}`),
            selectable: true,
            evented: true
          });
        } else {
          // Fallback: try to clone other object types
          newObj = fabric.util.object.clone(obj);
          newObj.set({
            left: newPoint.x,
            top: newPoint.y,
            scaleX: (obj.scaleX || 1) * (scaleX || 1),
            scaleY: (obj.scaleY || 1) * (scaleY || 1),
            angle: (obj.angle || 0) + (angle || 0),
            id: obj.id || (`${obj.type}_${Date.now()}_${Math.round(newPoint.x)}_${Math.round(newPoint.y)}`),
            selectable: true,
            evented: true
          });
        }
        
        console.log(`✅ Created new ${newObj.type}:`, newObj.type === 'circle' ? `radius=${newObj.radius}` : `${newObj.width || 'N/A'}x${newObj.height || 'N/A'}`);
        
        ungroupedObjects.push(newObj);
      });
      
      // Remove the group
      canvas.remove(activeObject);
      
      // Add objects back to canvas
      ungroupedObjects.forEach(obj => {
        canvas.add(obj);
      });
      
      // Select all ungrouped objects
      const newSelection = new fabric.ActiveSelection(ungroupedObjects, {
        canvas: canvas
      });
      canvas.setActiveObject(newSelection);
      canvas.requestRenderAll();
      
      setSnackbar({ open: true, message: 'Group ungrouped successfully!', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Please select a group to ungroup', severity: 'warning' });
    }
  }, []);

  // Duplicate selected object
  const duplicateSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      activeObject.clone((cloned) => {
        cloned.set({
          left: cloned.left + 20,
          top: cloned.top + 20,
          id: activeObject.type + '_' + Date.now()
        });
        canvas.add(cloned);
        canvas.setActiveObject(cloned);
        canvas.renderAll();
        setSnackbar({ open: true, message: 'Object duplicated!', severity: 'success' });
      });
    }
  }, []);

  // Alignment functions
  const alignObjects = useCallback((alignment) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length === 0) return;
    
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    
    activeObjects.forEach((obj) => {
      switch (alignment) {
        case 'left':
          obj.set('left', 0);
          break;
        case 'center':
          obj.set('left', (canvasWidth - obj.getScaledWidth()) / 2);
          break;
        case 'right':
          obj.set('left', canvasWidth - obj.getScaledWidth());
          break;
        case 'top':
          obj.set('top', 0);
          break;
        case 'middle':
          obj.set('top', (canvasHeight - obj.getScaledHeight()) / 2);
          break;
        case 'bottom':
          obj.set('top', canvasHeight - obj.getScaledHeight());
          break;
      }
      obj.setCoords();
    });
    
    canvas.renderAll();
    saveState();
    console.log(`🎯 Aligned objects: ${alignment}`);
  }, [saveState]);

  // Copy and paste functions
  const copySelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;
    
    activeObject.clone((cloned) => {
      setCopiedObject(cloned);
      setSnackbar({ open: true, message: 'Object copied!', severity: 'success' });
      console.log('📋 Object copied');
    });
  }, []);

  const pasteObject = useCallback(() => {
    if (!copiedObject || !fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    
    copiedObject.clone((cloned) => {
      canvas.discardActiveObject();
      cloned.set({
        left: cloned.left + 10,
        top: cloned.top + 10,
        id: cloned.type + '_' + Date.now()
      });
      
      if (cloned.type === 'activeSelection') {
        // Handle multiple objects
        cloned.canvas = canvas;
        cloned.forEachObject((obj) => {
          // Ensure each object in the selection has an ID
          if (!obj.id) {
            obj.id = `${obj.type}_${Date.now()}_${Math.round(obj.left || 0)}_${Math.round(obj.top || 0)}`;
          }
          canvas.add(obj);
        });
        cloned.setCoords();
      } else {
        canvas.add(cloned);
      }
      
      canvas.setActiveObject(cloned);
      canvas.renderAll();
      saveState();
      setSnackbar({ open: true, message: 'Object pasted!', severity: 'success' });
      console.log('📋 Object pasted');
    });
  }, [copiedObject, saveState]);

  // Update object property
  const updateObjectProperty = useCallback((property, value) => {
    if (selectedObject && fabricCanvasRef.current) {
      selectedObject.set(property, value);
      
      // Special handling for text objects
      if (selectedObject.type === 'textbox') {
        // For textbox objects, we need to trigger a re-render of the text
        selectedObject.initDimensions();
        selectedObject.setCoords();
      }
      
      fabricCanvasRef.current.renderAll();
      
      // Force component re-render to update the properties panel
      setSelectedObject(fabricCanvasRef.current.getActiveObject());
      updateObjectsList();
      
      // Save state for undo/redo
      saveState();
    }
  }, [selectedObject, updateObjectsList, saveState]);

  // Download carousel pages
const downloadImage = useCallback((format = 'png') => {
    exportAllPages();
  }, [exportAllPages]);

  // Add emoji
  const addEmoji = useCallback((emoji) => {
    const canvas = fabricCanvasRef.current;
    
    const text = new fabric.Text(emoji, {
      left: CANVAS_WIDTH / 2,
      top: CANVAS_HEIGHT / 2,
      fontSize: 64,
      fontFamily: 'Arial',
      originX: 'center',
      originY: 'center',
      id: 'emoji_' + Date.now()
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    setSnackbar({ open: true, message: 'Emoji added successfully!', severity: 'success' });
  }, []);

  // Add grouped elements
  const addGroupedElement = useCallback((elementType) => {
    const canvas = fabricCanvasRef.current;
    let elements = [];

    switch (elementType) {
      case 'profile-header':
        // Create a replaceable profile placeholder group
        const placeholderSize = 60; // Original radius was 30
        const placeholderCircle = new fabric.Circle({
            radius: placeholderSize / 2,
            originX: 'center',
            originY: 'center',
            left: placeholderSize / 2,
            top: placeholderSize / 2,
            fill: '#F3F4F6',
            stroke: '#D1D5DB',
            strokeWidth: 2,
        });
        const placeholderText = new fabric.Text('Profile', {
            fontSize: 10,
            originX: 'center',
            originY: 'center',
            left: placeholderSize / 2,
            top: placeholderSize / 2,
            fontFamily: 'Inter',
        });
        const profilePicPlaceholder = new fabric.Group([placeholderCircle, placeholderText], {
          left: 100,
            top: 85, // Align with the top of the name text
            id: 'profile_placeholder_' + Date.now(),
            placeholderType: 'image',
            placeholderLabel: 'Profile Image',
        });

        // Create name text with wider box
        const nameText = new fabric.Textbox('Abdul Subhan Fahad', {
          left: 170,
          top: 85,
          fontSize: 20,
          fontFamily: 'Poppins',
          fontWeight: 'bold',
          fill: '#000000',
          width: 300,
          id: 'profile_name_' + Date.now()
        });

        // Create handle text with wider box
        const handleText = new fabric.Textbox('@abdulsubhanf', {
          left: 170,
          top: 110,
          fontSize: 14,
          fontFamily: 'Poppins',
          fontWeight: 'normal',
          fill: '#666666',
          width: 300,
          id: 'profile_handle_' + Date.now()
        });

        elements = [profilePicPlaceholder, nameText, handleText];
        break;

      case 'swipe-left':
        // Create swipe background
        const swipeBg = new fabric.Rect({
          left: CANVAS_WIDTH / 2 - 80,
          top: CANVAS_HEIGHT - 120,
          width: 160,
          height: 40,
          fill: 'rgba(0, 0, 0, 0.7)',
          rx: 20,
          ry: 20,
          id: 'swipe_bg_' + Date.now()
        });

        // Create swipe arrow
        const swipeArrow = new fabric.IText('←', {
          left: CANVAS_WIDTH / 2 - 60,
          top: CANVAS_HEIGHT - 110,
          fontSize: 20,
          fontFamily: 'Arial',
          fill: '#ffffff',
          originY: 'center',
          id: 'swipe_arrow_' + Date.now()
        });

        // Create swipe text
        const swipeText = new fabric.IText('Swipe Left', {
          left: CANVAS_WIDTH / 2 - 30,
          top: CANVAS_HEIGHT - 110,
          fontSize: 14,
          fontFamily: 'Poppins',
          fontWeight: '500',
          fill: '#ffffff',
          originY: 'center',
          id: 'swipe_text_' + Date.now()
        });

        elements = [swipeBg, swipeArrow, swipeText];
        break;

      case 'call-to-action':
        // Create CTA background
        const ctaBg = new fabric.Rect({
          left: CANVAS_WIDTH / 2 - 150,
          top: CANVAS_HEIGHT - 150,
          width: 300,
          height: 60,
          fill: '#4890fe',
          rx: 30,
          ry: 30,
          id: 'cta_bg_' + Date.now()
        });

        // Create CTA text
        const ctaText = new fabric.IText('Follow for more tips!', {
          left: CANVAS_WIDTH / 2,
          top: CANVAS_HEIGHT - 120,
          fontSize: 18,
          fontFamily: 'Poppins',
          fontWeight: 'bold',
          fill: '#ffffff',
          originX: 'center',
          originY: 'center',
          id: 'cta_text_' + Date.now()
        });

        elements = [ctaBg, ctaText];
        break;

      case 'social-proof':
        // Create heart icon
        const heartIcon = new fabric.Text('❤️', {
          left: 100,
          top: CANVAS_HEIGHT - 100,
          fontSize: 24,
          fontFamily: 'Arial',
          id: 'heart_icon_' + Date.now()
        });

        // Create likes text
        const likesText = new fabric.IText('1.2K likes', {
          left: 135,
          top: CANVAS_HEIGHT - 92,
          fontSize: 14,
          fontFamily: 'Poppins',
          fontWeight: '500',
          fill: '#333333',
          id: 'likes_text_' + Date.now()
        });

        // Create comment icon
        const commentIcon = new fabric.Text('💬', {
          left: 220,
          top: CANVAS_HEIGHT - 100,
          fontSize: 24,
          fontFamily: 'Arial',
          id: 'comment_icon_' + Date.now()
        });

        // Create comments text
        const commentsText = new fabric.IText('86 comments', {
          left: 255,
          top: CANVAS_HEIGHT - 92,
          fontSize: 14,
          fontFamily: 'Poppins',
          fontWeight: '500',
          fill: '#333333',
          id: 'comments_text_' + Date.now()
        });

        elements = [heartIcon, likesText, commentIcon, commentsText];
        break;

      case 'quote-attribution':
        // Create quote background
        const quoteBg = new fabric.Rect({
          left: 80,
          top: CANVAS_HEIGHT / 2 - 100,
          width: CANVAS_WIDTH - 160,
          height: 200,
          fill: 'rgba(255, 255, 255, 0.9)',
          rx: 15,
          ry: 15,
          stroke: '#e0e0e0',
          strokeWidth: 1,
          id: 'quote_bg_' + Date.now()
        });

        // Create quote text
        const quoteText = new fabric.Textbox('"The best time to plant a tree was 20 years ago. The second best time is now."', {
          left: 100,
          top: CANVAS_HEIGHT / 2 - 80,
          width: CANVAS_WIDTH - 200,
          fontSize: 18,
          fontFamily: 'Poppins',
          fontStyle: 'italic',
          fill: '#333333',
          textAlign: 'center',
          lineHeight: 1.4,
          id: 'quote_text_' + Date.now()
        });

        // Create quote author
        const quoteAuthor = new fabric.IText('- Chinese Proverb', {
          left: CANVAS_WIDTH / 2,
          top: CANVAS_HEIGHT / 2 + 60,
          fontSize: 14,
          fontFamily: 'Poppins',
          fontWeight: '600',
          fill: '#666666',
          originX: 'center',
          id: 'quote_author_' + Date.now()
        });

        elements = [quoteBg, quoteText, quoteAuthor];
        break;

      case 'badge-label':
        // Create badge background
        const badgeBg = new fabric.Rect({
          left: 50,
          top: 50,
          width: 120,
          height: 30,
          fill: '#ff6b6b',
          rx: 15,
          ry: 15,
          id: 'badge_bg_' + Date.now()
        });

        // Create badge text
        const badgeText = new fabric.IText('NEW', {
          left: 110,
          top: 65,
          fontSize: 12,
          fontFamily: 'Poppins',
          fontWeight: 'bold',
          fill: '#ffffff',
          originX: 'center',
          originY: 'center',
          id: 'badge_text_' + Date.now()
        });

        elements = [badgeBg, badgeText];
        break;
        
      // New element types
      case 'testimonial-card':
        // Create testimonial card background
        const testimonialBg = new fabric.Rect({
          left: 100,
          top: 100,
          width: 300,
          height: 200,
          fill: 'white',
          rx: 10,
          ry: 10,
          stroke: '#e0e0e0',
          strokeWidth: 1,
          id: 'testimonial_bg_' + Date.now()
        });
        
        // Create star rating
        const starRating = new fabric.Text('★★★★★', {
          left: 120,
          top: 120,
          fontSize: 16,
          fontFamily: 'Arial',
          fill: '#ffc107',
          id: 'star_rating_' + Date.now()
        });
        
        // Create testimonial text
        const testimonialText = new fabric.Textbox('"This product changed my life! Highly recommend to everyone."', {
          left: 120,
          top: 150,
          width: 260,
          fontSize: 14,
          fontFamily: 'Poppins',
          fill: '#333333',
          lineHeight: 1.4,
          id: 'testimonial_text_' + Date.now()
        });
        
        // Create author name
        const testimonialAuthor = new fabric.Text('- Sarah Johnson', {
          left: 120,
          top: 240,
          fontSize: 12,
          fontFamily: 'Poppins',
          fontWeight: '600',
          fill: '#666666',
          id: 'testimonial_author_' + Date.now()
        });
        
        elements = [testimonialBg, starRating, testimonialText, testimonialAuthor];
        break;
        
      case 'step-indicator':
        // Create step circle
        const stepCircle = new fabric.Circle({
          left: 100,
          top: 100,
          radius: 20,
          fill: '#8b5cf6',
          id: 'step_circle_' + Date.now()
        });
        
        // Create step number
        const stepNumber = new fabric.Text('1', {
          left: 100,
          top: 100,
          fontSize: 18,
          fontFamily: 'Poppins',
          fontWeight: 'bold',
          fill: 'white',
          originX: 'center',
          originY: 'center',
          id: 'step_number_' + Date.now()
        });
        
        // Create step description
        const stepDescription = new fabric.Textbox('First step description', {
          left: 140,
          top: 100,
          width: 200,
          fontSize: 14,
          fontFamily: 'Poppins',
          fontWeight: '500',
          fill: '#333333',
          originY: 'center',
          id: 'step_description_' + Date.now()
        });
        
        elements = [stepCircle, stepNumber, stepDescription];
        break;
        
      case 'stat-card':
        // Create stat card background
        const statBg = new fabric.Rect({
          left: 100,
          top: 100,
          width: 150,
          height: 100,
          fill: 'white',
          rx: 8,
          ry: 8,
          stroke: '#e0e0e0',
          strokeWidth: 1,
          id: 'stat_bg_' + Date.now()
        });
        
        // Create stat value
        const statValue = new fabric.Text('+27%', {
          left: 120,
          top: 130,
          fontSize: 24,
          fontFamily: 'Poppins',
          fontWeight: 'bold',
          fill: '#10b981',
          id: 'stat_value_' + Date.now()
        });
        
        // Create stat label
        const statLabel = new fabric.Text('Engagement Rate', {
          left: 120,
          top: 165,
          fontSize: 12,
          fontFamily: 'Poppins',
          fill: '#666666',
          id: 'stat_label_' + Date.now()
        });
        
        elements = [statBg, statValue, statLabel];
        break;
        
      case 'feature-list':
        // Create feature items
        const featureCheck1 = new fabric.Text('✓', {
          left: 100,
          top: 100,
          fontSize: 16,
          fontFamily: 'Arial',
          fill: '#10b981',
          id: 'feature_check1_' + Date.now()
        });
        
        const featureText1 = new fabric.Text('Feature one', {
          left: 125,
          top: 100,
          fontSize: 14,
          fontFamily: 'Poppins',
          fill: '#333333',
          id: 'feature_text1_' + Date.now()
        });
        
        const featureCheck2 = new fabric.Text('✓', {
          left: 100,
          top: 130,
          fontSize: 16,
          fontFamily: 'Arial',
          fill: '#10b981',
          id: 'feature_check2_' + Date.now()
        });
        
        const featureText2 = new fabric.Text('Feature two', {
          left: 125,
          top: 130,
          fontSize: 14,
          fontFamily: 'Poppins',
          fill: '#333333',
          id: 'feature_text2_' + Date.now()
        });
        
        const featureCheck3 = new fabric.Text('✓', {
          left: 100,
          top: 160,
          fontSize: 16,
          fontFamily: 'Arial',
          fill: '#10b981',
          id: 'feature_check3_' + Date.now()
        });
        
        const featureText3 = new fabric.Text('Feature three', {
          left: 125,
          top: 160,
          fontSize: 14,
          fontFamily: 'Poppins',
          fill: '#333333',
          id: 'feature_text3_' + Date.now()
        });
        
        elements = [featureCheck1, featureText1, featureCheck2, featureText2, featureCheck3, featureText3];
        break;
        
      case 'author-bio':
        // Create author bio container
        const bioBg = new fabric.Rect({
          left: 100,
          top: 100,
          width: 250,
          height: 70,
          fill: 'white',
          rx: 8,
          ry: 8,
          stroke: '#e0e0e0',
          strokeWidth: 1,
          id: 'bio_bg_' + Date.now()
        });
        
        // Create author avatar placeholder
        const avatarCircle = new fabric.Circle({
          left: 115,
          top: 135,
          radius: 25,
          fill: '#e0e0e0',
          id: 'author_avatar_' + Date.now()
        });
        
        // Create author name
        const authorName = new fabric.Text('Jane Smith', {
          left: 170,
          top: 120,
          fontSize: 14,
          fontFamily: 'Poppins',
          fontWeight: 'bold',
          fill: '#000000',
          id: 'author_name_' + Date.now()
        });
        
        // Create author title
        const authorTitle = new fabric.Text('Marketing Director | Speaker', {
          left: 170,
          top: 145,
          fontSize: 12,
          fontFamily: 'Poppins',
          fill: '#666666',
          id: 'author_title_' + Date.now()
        });
        
        elements = [bioBg, avatarCircle, authorName, authorTitle];
        break;

      default:
        return;
    }

    // Add all elements to canvas
    elements.forEach(element => {
      canvas.add(element);
    });

    // Create a group from all elements
    const group = new fabric.Group(elements, {
      id: `${elementType}_group_` + Date.now()
    });

    // Remove individual elements and add the group
    elements.forEach(element => {
      canvas.remove(element);
    });
    
    canvas.add(group);
    canvas.setActiveObject(group);
    canvas.renderAll();
    
    setSnackbar({ open: true, message: 'Grouped element added successfully! Use ungroup to edit individual elements.', severity: 'success' });
  }, []);

  // Handle Pexels image selection
  const handlePexelsImage = useCallback((image) => {
    const canvas = fabricCanvasRef.current;

    fabric.Image.fromURL(image.src.large, (img) => {
      // Check if we're replacing a placeholder
      const activeObject = canvas.getActiveObject();
      let targetLeft, targetTop, targetScaleX, targetScaleY;
      
      if (activeObject && (activeObject.placeholderType === 'image' || activeObject.placeholderType === 'logo')) {
        // Replace placeholder
        targetLeft = activeObject.left;
        targetTop = activeObject.top;
        const targetWidth = activeObject.width * activeObject.scaleX;
        const targetHeight = activeObject.height * activeObject.scaleY;
        const scaleX = targetWidth / img.width;
        const scaleY = targetHeight / img.height;
        const scale = Math.min(scaleX, scaleY);
        targetScaleX = targetScaleY = scale;
        
        // Remove placeholder and its text
        const placeholderText = canvas.getObjects().find(obj => obj.id === activeObject.id + '_text');
        if (placeholderText) canvas.remove(placeholderText);
        canvas.remove(activeObject);
      } else {
        // Add as new image
      const maxWidth = CANVAS_WIDTH * 0.8;
      const maxHeight = CANVAS_HEIGHT * 0.8;
      const scaleX = maxWidth / img.width;
      const scaleY = maxHeight / img.height;
      const scale = Math.min(scaleX, scaleY);
        
        targetLeft = CANVAS_WIDTH / 2;
        targetTop = CANVAS_HEIGHT / 2;
        targetScaleX = targetScaleY = scale;
      }
      
      img.set({
        left: targetLeft,
        top: targetTop,
        originX: activeObject && activeObject.placeholderType ? 'left' : 'center',
        originY: activeObject && activeObject.placeholderType ? 'top' : 'center',
        scaleX: targetScaleX,
        scaleY: targetScaleY,
        id: 'pexels_' + Date.now()
      });

      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      setSnackbar({ open: true, message: 'Stock image added successfully!', severity: 'success' });
    }, { crossOrigin: 'anonymous' });
  }, []);

  // Apply gradient background
  const applyGradient = useCallback((gradientPreset) => {
    const canvas = fabricCanvasRef.current;
    
    // Create gradient with proper structure for the editor
    const gradient = new fabric.Gradient({
      type: 'linear',
      coords: {
        x1: 0,
        y1: 0,
        x2: 1,
        y2: 1
      },
      colorStops: [
        { offset: 0, color: gradientPreset.colors[0] },
        { offset: 1, color: gradientPreset.colors[1] }
      ],
      gradientUnits: 'percentage'
    });

    canvas.setBackgroundColor(gradient, canvas.renderAll.bind(canvas));
    setBackground(gradient);
    setSnackbar({ open: true, message: `${gradientPreset.name} gradient applied!`, severity: 'success' });
  }, []);

  // Apply brand accent colors
  const applyBrandAccent = useCallback((brandAccent) => {
    setSelectedBrandAccent(brandAccent);
    setSelectedColor(brandAccent.primary);
    setSnackbar({ open: true, message: `${brandAccent.name} brand colors selected!`, severity: 'success' });
  }, []);

  // Handle section clicks
  const handleSectionClick = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const togglePanel = (panel) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: !prev[panel]
    }));
  };



  // Zoom functions
  const handleZoomChange = (event, newValue) => {
    // Simple zoom control for display only
    if (!zoomLocked) {
    setZoom(newValue);
    }
  };

  const toggleZoomLock = () => {
    setZoomLocked(prev => !prev);
  };

  // Sidebar sections
  const sidebarSections = [
    { id: 'elements', label: 'Elements', icon: <ElementsIcon /> },
    { id: 'text', label: 'Text', icon: <TextIcon /> },
    { id: 'photos', label: 'Photos', icon: <PhotosIcon /> },
    { id: 'background', label: 'Background', icon: <BackgroundIcon /> },
    { id: 'branding', label: 'Branding', icon: <Palette /> },
    { id: 'layers', label: 'Layers', icon: <LayersIcon /> }
  ];

  // Keyboard shortcuts - moved after all function definitions
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent shortcuts when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      switch (e.key.toLowerCase()) {
        case 'z':
          if (ctrlKey && !e.shiftKey) {
            e.preventDefault();
            undo();
          }
          break;
        case 'y':
          if (ctrlKey) {
            e.preventDefault();
            redo();
          }
          break;
        case 'z':
          if (ctrlKey && e.shiftKey) {
            e.preventDefault();
            redo();
          }
          break;
        case 'd':
          if (ctrlKey) {
            e.preventDefault();
            duplicateSelected();
          }
          break;
        case 'c':
          if (ctrlKey) {
            e.preventDefault();
            copySelected();
          }
          break;
        case 'v':
          if (ctrlKey) {
            e.preventDefault();
            pasteObject();
          }
          break;
        case 'g':
          if (ctrlKey && !e.shiftKey) {
            e.preventDefault();
            groupSelected();
          } else if (ctrlKey && e.shiftKey) {
            e.preventDefault();
            ungroupSelected();
          }
          break;
        case 's':
          if (ctrlKey) {
            e.preventDefault();
            downloadImage('png');
          }
          break;
        case 'delete':
        case 'backspace':
          if (!ctrlKey && !e.shiftKey) {
            e.preventDefault();
            deleteSelected();
          }
          break;
        case 'escape':
          if (fabricCanvasRef.current) {
            fabricCanvasRef.current.discardActiveObject();
            fabricCanvasRef.current.renderAll();
            setSelectedObject(null);
          }
          break;

        case 'r':
          if (!ctrlKey) {
            e.preventDefault();
            addShape('rectangle');
          }
          break;
        case 'a':
          if (ctrlKey && fabricCanvasRef.current) {
            e.preventDefault();
            const objects = fabricCanvasRef.current.getObjects();
            if (objects.length > 0) {
              fabricCanvasRef.current.setActiveObject(objects[0]);
              for (let i = 1; i < objects.length; i++) {
                fabricCanvasRef.current.setActiveObject(objects[i]);
              }
              fabricCanvasRef.current.renderAll();
            }
          }
          break;
        case '?':
        case '/':
          if (!ctrlKey) {
            e.preventDefault();
            setShowShortcuts(true);
          }
          break;
        case 'arrowleft':
          if (fabricCanvasRef.current && fabricCanvasRef.current.getActiveObject()) {
            e.preventDefault();
            const obj = fabricCanvasRef.current.getActiveObject();
            const moveStep = e.shiftKey ? 10 : 1;
            obj.set('left', obj.left - moveStep);
            obj.setCoords();
            fabricCanvasRef.current.renderAll();
            saveState();
          }
          break;
        case 'arrowright':
          if (fabricCanvasRef.current && fabricCanvasRef.current.getActiveObject()) {
            e.preventDefault();
            const obj = fabricCanvasRef.current.getActiveObject();
            const moveStep = e.shiftKey ? 10 : 1;
            obj.set('left', obj.left + moveStep);
            obj.setCoords();
            fabricCanvasRef.current.renderAll();
            saveState();
          }
          break;
        case 'arrowup':
          if (fabricCanvasRef.current && fabricCanvasRef.current.getActiveObject()) {
            e.preventDefault();
            const obj = fabricCanvasRef.current.getActiveObject();
            const moveStep = e.shiftKey ? 10 : 1;
            obj.set('top', obj.top - moveStep);
            obj.setCoords();
            fabricCanvasRef.current.renderAll();
            saveState();
          }
          break;
        case 'arrowdown':
          if (fabricCanvasRef.current && fabricCanvasRef.current.getActiveObject()) {
            e.preventDefault();
            const obj = fabricCanvasRef.current.getActiveObject();
            const moveStep = e.shiftKey ? 10 : 1;
            obj.set('top', obj.top + moveStep);
            obj.setCoords();
            fabricCanvasRef.current.renderAll();
            saveState();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, duplicateSelected, deleteSelected, downloadImage, addShape, groupSelected, ungroupSelected, copySelected, pasteObject, saveState]);



            // Sidebar render function removed - now handled by CarouselLeftSidebar component

    // Properties panel content is now handled by CarouselRightProperties component

  // Text properties panel removed - now handled by CarouselRightProperties component

  const updateHistory = () => {
    if (!fabricCanvasRef.current) return;
    
    const json = fabricCanvasRef.current.toJSON();
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(json);
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Handle context menu
  const handleContextMenu = useCallback((e) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    e.preventDefault();
    const pointer = canvas.getPointer(e);
    const target = canvas.findTarget(e);
    
    if (target) {
      canvas.setActiveObject(target);
      canvas.renderAll();
      setContextMenu({ x: e.clientX, y: e.clientY });
    }
  }, []);

  // Close context menu handler - close on any reason except during menu interaction
  const handleCloseContextMenu = useCallback((reason) => {
    setContextMenu(null);
  }, []);

  // Close menu on left click anywhere
  const handleCanvasClick = useCallback((e) => {
    // Close context menu on any left click, unless clicking on the menu itself
    if (e.button === 0 && !e.target.closest('.MuiMenu-root')) {
      setContextMenu(null);
    }
  }, []);

  // Add click handler to close menu on left click
  useEffect(() => {
    document.addEventListener('click', handleCanvasClick);
    return () => {
      document.removeEventListener('click', handleCanvasClick);
    };
  }, [handleCanvasClick]);

  // Handle context menu actions
  const handleContextMenuAction = useCallback((action) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Close context menu after action except for nested menu items
    const shouldCloseMenu = !['align', 'arrange'].includes(action);

    switch (action) {
      case 'group':
        groupSelected();
        break;
      case 'ungroup':
        ungroupSelected();
        break;
      case 'duplicate':
        duplicateSelected();
        break;
      case 'delete':
        deleteSelected();
        break;
      case 'lock':
        if (canvas.getActiveObject()) {
          toggleObjectLock(canvas.getActiveObject());
        }
        break;
      case 'unlock':
        if (canvas.getActiveObject()) {
          toggleObjectLock(canvas.getActiveObject());
        }
        break;
      case 'toggleVisibility':
        if (canvas.getActiveObject()) {
          toggleObjectVisibility(canvas.getActiveObject());
        }
        break;
      case 'bringToFront':
        if (canvas.getActiveObject()) {
          canvas.bringToFront(canvas.getActiveObject());
          canvas.renderAll();
        }
        break;
      case 'sendToBack':
        if (canvas.getActiveObject()) {
          canvas.sendToBack(canvas.getActiveObject());
          canvas.renderAll();
        }
        break;
      case 'bringForward':
        if (canvas.getActiveObject()) {
          canvas.bringForward(canvas.getActiveObject());
          canvas.renderAll();
        }
        break;
      case 'sendBackward':
        if (canvas.getActiveObject()) {
          canvas.sendBackwards(canvas.getActiveObject());
          canvas.renderAll();
        }
        break;
      case 'copy':
        copySelected();
        break;
      case 'paste':
        pasteObject();
        break;
      case 'alignLeft':
        alignObjects('left');
        break;
      case 'alignCenter':
        alignObjects('center');
        break;
      case 'alignRight':
        alignObjects('right');
        break;
      case 'alignTop':
        alignObjects('top');
        break;
      case 'alignMiddle':
        alignObjects('middle');
        break;
      case 'alignBottom':
        alignObjects('bottom');
        break;
    }
    
    // Close menu after action only if it's not a nested menu parent
    if (shouldCloseMenu) {
      setContextMenu(null);
    }
  }, [groupSelected, ungroupSelected, duplicateSelected, deleteSelected, toggleObjectLock, toggleObjectVisibility, copySelected, pasteObject, alignObjects]);

  // Add context menu event listener
  // THIS IS NOW REMOVED AND CONSOLIDATED

  // Add this useEffect after Fabric.js canvas initialization
  // THIS IS NOW REMOVED AND CONSOLIDATED

  // Handle canvas size change
  const handleCanvasSizeChange = useCallback((newSize) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    setCanvasSize(newSize);
    // For carousel, maintain the current width (pageCount * PAGE_WIDTH) but update height
    const currentWidth = pageCount * PAGE_WIDTH;
    canvas.setDimensions({ width: currentWidth, height: newSize.height });
    
    // Removed zoom-related code
    
    canvas.renderAll();
    
    setSnackbar({ 
      open: true, 
      message: `Canvas resized to ${newSize.name}`, 
      severity: 'success' 
    });
  }, [pageCount]);

  // 2. Add functions to add profile and logo placeholders
  const addProfilePlaceholder = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const id = 'profile_placeholder_' + Date.now();
    const size = 120;
    // Circle placeholder for profile image
    const circle = new fabric.Circle({
      left: 0,
      top: 0,
      radius: size / 2,
      fill: '#F3F4F6',
      stroke: '#D1D5DB',
      strokeWidth: 2,
      originX: 'center',
      originY: 'center',
    });
    // Add placeholder text
    const text = new fabric.Text('Profile', {
      left: 0,
      top: 0,
      fontSize: 18,
      fontFamily: 'Inter',
      fontWeight: '500',
      fill: '#9CA3AF',
      textAlign: 'center',
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });
    // Group them
    const group = new fabric.Group([circle, text], {
      left: CANVAS_WIDTH / 2,
      top: CANVAS_HEIGHT / 2,
      originX: 'center',
      originY: 'center',
      id,
      placeholderType: 'image',
      placeholderLabel: 'Profile Image',
      selectable: true,
      hasControls: true,
    });
    canvas.add(group);
    canvas.setActiveObject(group);
    canvas.renderAll();
    setSnackbar({ open: true, message: 'Profile placeholder added!', severity: 'success' });
  }, []);

  const addLogoPlaceholder = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const id = 'logo_placeholder_' + Date.now();
    const width = 160;
    const height = 80;
    // Rectangle placeholder for logo
    const rect = new fabric.Rect({
      left: 0,
      top: 0,
      width,
      height,
      fill: '#F3F4F6',
      stroke: '#D1D5DB',
      strokeWidth: 2,
      rx: 12,
      ry: 12,
      originX: 'center',
      originY: 'center',
    });
    // Add placeholder text
    const text = new fabric.Text('Logo', {
      left: 0,
      top: 0,
      fontSize: 18,
      fontFamily: 'Inter',
      fontWeight: '500',
      fill: '#9CA3AF',
      textAlign: 'center',
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });
    // Group them
    const group = new fabric.Group([rect, text], {
      left: CANVAS_WIDTH / 2,
      top: CANVAS_HEIGHT / 2,
      originX: 'center',
      originY: 'center',
      id,
      placeholderType: 'image',
      placeholderLabel: 'Logo',
      selectable: true,
      hasControls: true,
    });
    canvas.add(group);
    canvas.setActiveObject(group);
    canvas.renderAll();
    setSnackbar({ open: true, message: 'Logo placeholder added!', severity: 'success' });
  }, []);

  const deletePage = useCallback((pageToDelete) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || pageCount <= 1) return; // Don't delete the last page
    
    console.log(`🗑️ Deleting page ${pageToDelete}`);
    
    // Get page bounds
    const pageBounds = getPageBounds(pageToDelete);
    
    // Remove all objects in this page
    const objectsToRemove = canvas.getObjects().filter(obj => {
      if (!obj.left) return false;
      return obj.left >= pageBounds.left && obj.left < pageBounds.right;
    });
    
    objectsToRemove.forEach(obj => canvas.remove(obj));
    
    // Remove page separator and border for this page
    const separatorToRemove = canvas.getObjects().find(obj => obj.id === `separator_${pageToDelete - 1}`);
    const borderToRemove = canvas.getObjects().find(obj => obj.id === `page_border_${pageToDelete}`);
    
    if (separatorToRemove) canvas.remove(separatorToRemove);
    if (borderToRemove) canvas.remove(borderToRemove);
    
    // Move objects from subsequent pages to the left
    if (pageToDelete < pageCount) {
      const objectsToMove = canvas.getObjects().filter(obj => {
        if (!obj.left) return false;
        return obj.left >= pageBounds.right;
      });
      
      objectsToMove.forEach(obj => {
        obj.set('left', obj.left - PAGE_WIDTH);
      });
    }
    
    // Update page count and canvas width
    const newPageCount = pageCount - 1;
    setPageCount(newPageCount);
    
    // Update separators array
    setPageSeparators(prev => prev.filter(sep => sep.id !== `separator_${pageToDelete - 1}`));
    
    canvas.renderAll();
    
    setSnackbar({ 
      open: true, 
      message: `Page ${pageToDelete} deleted successfully!`, 
      severity: 'success' 
    });
    
    // Save state for undo/redo
    setTimeout(() => saveState(), 100);
  }, [pageCount, getPageBounds, saveState]);

  // AI Design generation handler
  const handleGenerateAIDesign = useCallback(async () => {
    if (!designPrompt.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a design prompt',
        severity: 'warning'
      });
      return;
    }

    setIsGeneratingDesign(true);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/ai/generate-design`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: designPrompt,
          designType: 'carousel'
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate design');
      }
      
      if (data.success && data.design) {
        setGeneratedDesign(data.design);
        
        // Apply the generated design
        applyGeneratedDesign(data.design);
        
        setSnackbar({
          open: true,
          message: 'AI design generated successfully!',
          severity: 'success'
        });
        
        // Close the menu
        setAiMenuAnchorEl(null);
      } else {
        throw new Error('Invalid design data received');
      }
    } catch (error) {
      console.error('AI design generation error:', error);
      setSnackbar({
        open: true,
        message: `Failed to generate design: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsGeneratingDesign(false);
    }
  }, [designPrompt]);
  
  // Helper function to apply design content to canvas
  const applyDesignContent = useCallback((design, canvas) => {
    try {
      if (!canvas || !design || !design.slides) {
        console.warn('⚠️ Cannot apply design content: Missing required data');
        return;
      }
      
      console.log('🎨 Applying professional design layout to slides');
      
      // Apply content to each slide with enhanced visual design
      design.slides.forEach((slide, index) => {
        const pageIndex = index;
        const pageLeft = pageIndex * PAGE_WIDTH;
        const pageCenter = pageLeft + (PAGE_WIDTH / 2);
        
        // Create background shapes for visual interest
        const layoutType = index % 3; // Rotate between 3 different layout types
        
        // Add background decoration based on layout type
        if (layoutType === 0) {
          // Layout 1: Diagonal accent shape
          const bgShape = new fabric.Polygon(
            [
              { x: pageLeft, y: 0 },
              { x: pageLeft + 400, y: 0 },
              { x: pageLeft, y: 400 }
            ],
            {
              fill: design.colorScheme?.secondary || '#8B5CF6',
              opacity: 0.1,
              selectable: false,
              id: `ai_bg_shape_${pageIndex}_${Date.now()}`
            }
          );
          canvas.add(bgShape);
          
          // Add circle accent
          const circle = new fabric.Circle({
            left: pageLeft + PAGE_WIDTH - 200,
            top: PAGE_HEIGHT - 200,
            radius: 100,
            fill: design.colorScheme?.accent || '#10B981',
            opacity: 0.15,
            selectable: true,
            id: `ai_accent_${pageIndex}_${Date.now()}`
          });
          canvas.add(circle);
        } else if (layoutType === 1) {
          // Layout 2: Side accent bar
          const accentBar = new fabric.Rect({
            left: pageLeft + 50,
            top: 0,
            width: 10,
            height: PAGE_HEIGHT,
            fill: design.colorScheme?.primary || '#3B82F6',
            opacity: 0.8,
            selectable: true,
            id: `ai_accent_bar_${pageIndex}_${Date.now()}`
          });
          canvas.add(accentBar);
          
          // Add decorative rectangle
          const rect = new fabric.Rect({
            left: pageLeft + PAGE_WIDTH - 200,
            top: 50,
            width: 150,
            height: 150,
            rx: 20,
            ry: 20,
            fill: design.colorScheme?.accent || '#10B981',
            opacity: 0.15,
            selectable: true,
            id: `ai_rect_${pageIndex}_${Date.now()}`
          });
          canvas.add(rect);
        } else {
          // Layout 3: Bottom accent shape
          const bottomAccent = new fabric.Rect({
            left: pageLeft,
            top: PAGE_HEIGHT - 100,
            width: PAGE_WIDTH,
            height: 100,
            fill: design.colorScheme?.primary || '#3B82F6',
            opacity: 0.1,
            selectable: true,
            id: `ai_bottom_accent_${pageIndex}_${Date.now()}`
          });
          canvas.add(bottomAccent);
          
          // Add decorative circles
          const smallCircle = new fabric.Circle({
            left: pageLeft + 80,
            top: 80,
            radius: 40,
            fill: design.colorScheme?.secondary || '#8B5CF6',
            opacity: 0.2,
            selectable: true,
            id: `ai_circle_sm_${pageIndex}_${Date.now()}`
          });
          canvas.add(smallCircle);
        }
        
        // Add heading with improved styling
        if (slide.heading) {
                    // Create heading with better positioning and larger font
            const heading = new fabric.Textbox(slide.heading, {
              top: 80,
              width: 800,
              fontSize: 56, // Slightly smaller to avoid overflow
              fontFamily: design.typography?.headingFont || 'Poppins',
              fontWeight: 'bold',
              fill: design.colorScheme?.primary || '#3B82F6',
              textAlign: 'center', // Center aligned for better appearance
              id: `ai_heading_${pageIndex}_${Date.now()}`,
              originX: 'center',
              left: pageCenter, // Center horizontally
              lineHeight: 1.2
            });
          canvas.add(heading);
        }
        
        // Add content with better formatting
        if (slide.content) {
                     // Format the content based on its structure
           let formattedContent = slide.content;
           let contentTop = 280; // More space below heading to avoid overlap
           let contentAlign = 'left';
           
           // Check if content contains bullet points
           if (slide.content.includes('•') || slide.content.includes('-') || slide.content.includes('*')) {
             // This is likely a bullet list, keep left alignment
             contentAlign = 'left';
             contentTop = 300; // Even more space for bullet lists
           } else if (slide.content.length < 100) {
             // Short content can be centered
             contentAlign = 'center';
           }
          
                     const content = new fabric.Textbox(formattedContent, {
             width: 750,
             fontSize: 28, // Slightly smaller font for better fit
             fontFamily: design.typography?.bodyFont || 'Poppins',
             fontWeight: 'normal',
             fill: '#333333',
             textAlign: contentAlign,
             id: `ai_content_${pageIndex}_${Date.now()}`,
             originX: contentAlign === 'center' ? 'center' : 'left',
             left: contentAlign === 'center' ? pageCenter : pageLeft + 160,
             top: contentTop,
             lineHeight: 1.4, // Better line spacing
             charSpacing: 20 // Add some letter spacing for readability
           });
          canvas.add(content);
        }
        
        // Add visual elements based on recommendations with better positioning
        if (slide.visualElements && slide.visualElements.length > 0) {
          // Process each visual element recommendation
          slide.visualElements.forEach((elementDesc, elemIndex) => {
            const element = elementDesc.toLowerCase();
            
                         // Position elements strategically based on index
             // Spread elements across the page width to avoid overlapping
             const elemLeft = pageLeft + 200 + (elemIndex * 250);
             const elemTop = PAGE_HEIGHT - 200;
            
            if (element.includes('circle') || element.includes('round')) {
              const circle = new fabric.Circle({
                left: elemLeft,
                top: elemTop,
                radius: 80,
                fill: design.colorScheme?.accent || '#10B981',
                opacity: 0.7,
                stroke: design.colorScheme?.primary || '#3B82F6',
                strokeWidth: 2,
                id: `ai_element_circle_${pageIndex}_${elemIndex}_${Date.now()}`
              });
              canvas.add(circle);
            } else if (element.includes('rectangle') || element.includes('square')) {
              const rect = new fabric.Rect({
                left: elemLeft,
                top: elemTop,
                width: 160,
                height: 160,
                rx: 15, // Rounded corners
                ry: 15,
                fill: design.colorScheme?.secondary || '#8B5CF6',
                opacity: 0.7,
                stroke: design.colorScheme?.primary || '#3B82F6',
                strokeWidth: 2,
                id: `ai_element_rect_${pageIndex}_${elemIndex}_${Date.now()}`
              });
              canvas.add(rect);
            } else if (element.includes('icon') || element.includes('symbol')) {
              // Create a simple icon placeholder
              const iconSize = 100;
              const iconGroup = new fabric.Group([], {
                left: elemLeft,
                top: elemTop,
                width: iconSize,
                height: iconSize,
                id: `ai_icon_${pageIndex}_${elemIndex}_${Date.now()}`
              });
              
              // Add icon background
              const iconBg = new fabric.Circle({
                radius: iconSize/2,
                fill: design.colorScheme?.accent || '#10B981',
                opacity: 0.7,
                originX: 'center',
                originY: 'center'
              });
              
              // Add icon symbol (simple placeholder)
              const iconSymbol = new fabric.Text('★', {
                fontSize: iconSize * 0.6,
                fill: '#FFFFFF',
                fontFamily: 'Arial',
                originX: 'center',
                originY: 'center',
                left: 0,
                top: 0
              });
              
              iconGroup.addWithUpdate(iconBg);
              iconGroup.addWithUpdate(iconSymbol);
              canvas.add(iconGroup);
            }
          });
        }
      });
      
      // Force re-render and ensure canvas dimensions are correct
      const expectedWidth = Math.max(pageCount, design.slides.length) * PAGE_WIDTH;
      canvas.setDimensions({ width: expectedWidth, height: PAGE_HEIGHT });
      setCurrentCanvasWidth(expectedWidth);
      
      // Make sure background color is applied correctly
      if (design.colorScheme && design.colorScheme.background) {
        console.log(`🔄 Re-applying background color: ${design.colorScheme.background}`);
        canvas.setBackgroundColor(design.colorScheme.background, () => {
          canvas.renderAll();
        });
      }
      
      // Multiple render calls to ensure everything is properly displayed
      if (canvas.getContext()) {
        canvas.renderAll();
        canvas.calcOffset();
      }
      
      // Final render and state save after a delay
      setTimeout(() => {
        // Safety check to ensure canvas is still valid before rendering
        if (canvas && canvas.getContext()) {
          console.log('🔄 Final canvas refresh and state save');
          canvas.renderAll();
          saveState();
        } else {
          console.warn('⚠️ Canvas context not available for final render');
        }
      }, 300);
    } catch (error) {
      console.error('❌ Error applying design content:', error);
    }
  }, [pageCount, saveState, setCurrentCanvasWidth]);

  // Apply the generated design to the canvas
  const applyGeneratedDesign = useCallback((design) => {
    try {
      const canvas = fabricCanvasRef.current;
      if (!canvas || !design) {
        console.warn('⚠️ Cannot apply design: Canvas or design data is missing');
        return;
      }
      
      console.log('🎨 Applying AI design with', design.slides?.length || 0, 'slides');
      
      // Apply color scheme
      if (design.colorScheme && design.colorScheme.background) {
        // Update background color - make sure it's a valid color
        const newBgColor = design.colorScheme.background;
        console.log(`🎨 Setting background color to ${newBgColor}`);
        
        // Update both canvas and state immediately
        // We removed background from the canvas init dependencies, so this is safe
        setBackground(newBgColor);
        canvas.setBackgroundColor(newBgColor, () => {
          canvas.renderAll();
          console.log(`✅ Background color applied: ${newBgColor}`);
        });
      }
      
      // Apply design to each slide
      if (design.slides && design.slides.length > 0) {
        // Ensure we have enough pages
        const requiredPages = design.slides.length;
        if (pageCount < requiredPages) {
          // Add all needed pages at once
          const pagesToAdd = requiredPages - pageCount;
          console.log(`📄 Need to add ${pagesToAdd} pages for the design`);
          addMultiplePages(pagesToAdd);
          
          // Short delay to ensure pages are added before continuing
          setTimeout(() => {
            applyDesignContent(design, canvas);
          }, 300);
          return; // Exit early, the delayed function will handle content
        }
        
        // If we already have enough pages, apply content directly
        applyDesignContent(design, canvas);
      }
    } catch (error) {
      console.error('❌ Error applying AI design:', error);
    }
  }, [pageCount, addMultiplePages, applyDesignContent]);

  const duplicatePage = useCallback((pageToDuplicate) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    console.log(`📋 Duplicating page ${pageToDuplicate}`);
    
    // Get page bounds for the page to duplicate
    const pageBounds = getPageBounds(pageToDuplicate);
    
    // Find all objects in the page to duplicate (excluding separators and borders)
    const objectsToDuplicate = canvas.getObjects().filter(obj => {
      if (!obj.left || obj.excludeFromExport) return false;
      return obj.left >= pageBounds.left && obj.left < pageBounds.right;
    });
    
    // First, add a new page at the end
    const currentPageCount = pageCount;
    const newPageCount = pageCount + 1;
    const newPagePosition = currentPageCount * PAGE_WIDTH;
    
    // Add separator for the new page
    const separator = new fabric.Line([newPagePosition, 0, newPagePosition, PAGE_HEIGHT], {
      stroke: '#e2e8f0',
      strokeWidth: PAGE_SEPARATOR_WIDTH,
      strokeDashArray: [10, 5],
      selectable: false,
      evented: false,
      excludeFromExport: true,
      id: `separator_${currentPageCount}`
    });
    canvas.add(separator);
    
    // Add page border for the new page
    const pageBorder = new fabric.Rect({
      left: newPagePosition,
      top: 0,
      width: PAGE_WIDTH,
      height: PAGE_HEIGHT,
      fill: 'transparent',
      stroke: '#cbd5e1',
      strokeWidth: 1,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      excludeFromExport: true,
      id: `page_border_${newPageCount}`
    });
    canvas.add(pageBorder);
    
    // Clone and position the objects in the new page
    objectsToDuplicate.forEach(obj => {
      obj.clone((clonedObj) => {
        // Position the cloned object in the new page
        const offsetX = newPagePosition - pageBounds.left;
        clonedObj.set({
          left: obj.left + offsetX,
          top: obj.top
        });
        canvas.add(clonedObj);
      });
    });
    
    // Update page count
    setPageCount(newPageCount);
    setPageSeparators(prev => [...prev, separator]);
    
    // Update canvas background if needed
    canvas.setBackgroundColor(background, canvas.renderAll.bind(canvas));
    
    setSnackbar({ 
      open: true, 
      message: `Page ${pageToDuplicate} duplicated successfully!`, 
      severity: 'success' 
    });
    
    // Save state for undo/redo
    setTimeout(() => saveState(), 100);
  }, [pageCount, getPageBounds, background, saveState]);

  // Function to handle content generation
  const handleGenerateContent = useCallback(async () => {
    if (!contentPrompt.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a content prompt',
        severity: 'warning'
      });
      return;
    }

    setIsGeneratingContent(true);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/ai/generate-carousel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: contentPrompt,
          mode: contentMode
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate content');
      }
      
      if (data.success && data.slides && data.slides.length > 0) {
        // Apply the generated content to the carousel
        applyGeneratedContent(data.slides);
        
        setSnackbar({
          open: true,
          message: 'Content generated successfully!',
          severity: 'success'
        });
        
        // Close the menu
        setAiMenuAnchorEl(null);
      } else {
        throw new Error('Invalid content data received');
      }
    } catch (error) {
      console.error('Content generation error:', error);
      setSnackbar({
        open: true,
        message: `Failed to generate content: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsGeneratingContent(false);
    }
  }, [contentPrompt, contentMode]);

  // Function to apply generated content to carousel with professional design elements
  const applyGeneratedContent = useCallback(async (slides) => {
    // Always update page count to match the number of slides generated by AI
    setPageCount(slides.length);
    // Update canvas width to accommodate the slides
    setCurrentCanvasWidth(PAGE_WIDTH * slides.length);

    // Get the canvas
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Clear existing objects to start fresh
    canvas.clear();
    
    // Define a set of professional color schemes
    const colorSchemes = [
      { primary: '#4A90E2', secondary: '#F8F9FA', accent: '#2C3E50', text: '#333333', name: 'Professional Blue' }, 
      { primary: '#1E293B', secondary: '#FFFFFF', accent: '#3B82F6', text: '#1E293B', name: 'Corporate Dark' }, 
      { primary: '#8B5CF6', secondary: '#F3F4F6', accent: '#6D28D9', text: '#1F2937', name: 'Creative Purple' }, 
      { primary: '#10B981', secondary: '#ECFDF5', accent: '#059669', text: '#064E3B', name: 'Growth Green' }, 
      { primary: '#F59E0B', secondary: '#FFFBEB', accent: '#D97706', text: '#78350F', name: 'Amber Energy' },
      { primary: '#EF4444', secondary: '#FFFFFF', accent: '#B91C1C', text: '#1F2937', name: 'Bold Red' },
      { primary: '#0EA5E9', secondary: '#F0F9FF', accent: '#0284C7', text: '#0C4A6E', name: 'Sky Blue' },
      { primary: '#6366F1', secondary: '#EEF2FF', accent: '#4F46E5', text: '#312E81', name: 'Indigo' }
    ];
    
    // Choose a color scheme for consistency across all slides
    const colorScheme = colorSchemes[Math.floor(Math.random() * colorSchemes.length)];
    
    // Set background style based on color scheme
    const backgroundStyles = [
      // Gradient background
      () => {
        const gradientFill = new fabric.Gradient({
          type: 'linear',
          coords: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: PAGE_HEIGHT
          },
          colorStops: [
            { offset: 0, color: colorScheme.primary },
            { offset: 1, color: fabric.Color.fromHex(colorScheme.primary).darken(20).toRgba() }
          ]
        });
        canvas.setBackgroundColor(gradientFill, canvas.renderAll.bind(canvas));
      },
      // Radial gradient background
      () => {
        const gradientFill = new fabric.Gradient({
          type: 'radial',
          coords: {
            x1: PAGE_WIDTH/2,
            y1: PAGE_HEIGHT/2,
            r1: 0,
            x2: PAGE_WIDTH/2,
            y2: PAGE_HEIGHT/2,
            r2: PAGE_WIDTH
          },
          colorStops: [
            { offset: 0, color: fabric.Color.fromHex(colorScheme.primary).tint(20).toRgba() },
            { offset: 1, color: colorScheme.primary }
          ]
        });
        canvas.setBackgroundColor(gradientFill, canvas.renderAll.bind(canvas));
      },
      // Solid background with pattern overlay
      () => {
        canvas.setBackgroundColor(colorScheme.primary, canvas.renderAll.bind(canvas));
        
        // Create pattern overlay
        const patternSize = 20;
        for (let i = 0; i < slides.length; i++) {
          const slideX = PAGE_WIDTH * i;
          
          // Create grid pattern
          for (let x = 0; x < PAGE_WIDTH; x += patternSize * 4) {
            for (let y = 0; y < PAGE_HEIGHT; y += patternSize * 4) {
              const dot = new fabric.Circle({
                left: slideX + x,
                top: y,
                radius: 2,
                fill: colorScheme.secondary,
                opacity: 0.2,
                selectable: false,
                evented: false
              });
              canvas.add(dot);
            }
          }
        }
      }
    ];
    
    // Choose a background style
    const chosenBackgroundStyle = backgroundStyles[Math.floor(Math.random() * backgroundStyles.length)];
    chosenBackgroundStyle();
    
    // Fetch a relevant stock image from Pexels based on the content of the first slide
    let stockImage = null;
    try {
      const searchTerm = slides[0].title.replace(/<\/?p>/g, '').trim().split(' ').slice(0, 3).join(' ');
      const response = await fetch(`https://api.pexels.com/v1/search?query=${encodeURIComponent(searchTerm)}&per_page=1`, {
        headers: {
          'Authorization': 'Bearer 563492ad6f91700001000001f89041f47d0642368f390561db0d60ef' // Public Pexels API key
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.photos && data.photos.length > 0) {
          stockImage = data.photos[0].src.medium;
        }
      }
    } catch (error) {
      console.error('Error fetching Pexels image:', error);
      // Continue without stock image if there's an error
    }
    
    // Apply content to each slide with professional design layouts inspired by templates
    slides.forEach((slide, index) => {
      // Calculate slide position
      const slideX = PAGE_WIDTH * index;
      
      // Choose layout type based on slide index and content length for variety
      const layoutType = index % 5; // 0, 1, 2, 3, or 4 - more layout options
      
      // Clean up text content
      const titleText = slide.title.replace(/<\/?p>/g, '').trim();
      const contentText = slide.content.replace(/<\/?p>/g, '').replace(/<br\s*\/?>/g, '\n').trim();
      
      // Use the selected color scheme
      const colors = colorScheme;
      
      // Add branding/logo placeholder in top corner
      const brandingRect = new fabric.Rect({
        left: slideX + 50,
        top: 30,
        width: 40,
        height: 40,
        fill: colors.secondary,
        opacity: 0.8,
        rx: 8,
        ry: 8,
        slideIndex: index
      });
      canvas.add(brandingRect);
      
      // Add slide number indicator with better styling
      const slideNumber = new fabric.Text(`${index + 1}/${slides.length}`, {
        left: slideX + PAGE_WIDTH - 80,
        top: 40,
        fontSize: 18,
        fontFamily: 'Poppins, Arial',
        fontWeight: '500',
        fill: colors.secondary,
        opacity: 0.9,
        slideIndex: index
      });
      canvas.add(slideNumber);
      
      // Common background elements for all layouts
      // Add a large accent shape for visual interest
      const bgAccent = new fabric.Circle({
        left: slideX + PAGE_WIDTH - 200,
        top: PAGE_HEIGHT - 200,
        radius: 250,
        fill: colors.accent,
        opacity: 0.1,
        slideIndex: index,
        selectable: false,
        evented: false
      });
      canvas.add(bgAccent);
      
      // Apply different layouts based on type
      if (layoutType === 0) {
        // Layout 1: Modern Quote Style (inspired by quote-post template)
        
        // Add decorative elements
        const decorCircle1 = new fabric.Circle({
          left: slideX + 100,
          top: 100,
          radius: 60,
          fill: colors.accent,
          opacity: 0.1,
          slideIndex: index,
          selectable: false
        });
        
        const decorCircle2 = new fabric.Circle({
          left: slideX + PAGE_WIDTH - 150,
          top: PAGE_HEIGHT - 150,
          radius: 80,
          fill: colors.accent,
          opacity: 0.15,
          slideIndex: index,
          selectable: false
        });
        
        // Add stock image if available (for first slide only)
        if (stockImage && index === 0) {
          fabric.Image.fromURL(stockImage, (img) => {
            // Scale image to fit nicely
            const scale = 300 / Math.max(img.width, img.height);
            img.set({
              left: slideX + PAGE_WIDTH - 350,
              top: 100,
              scaleX: scale,
              scaleY: scale,
              opacity: 0.8,
              clipPath: new fabric.Circle({
                radius: 150 / scale,
                originX: 'center',
                originY: 'center'
              }),
              slideIndex: index
            });
            canvas.add(img);
            canvas.renderAll();
          }, { crossOrigin: 'anonymous' });
        }
        
        // Large quote mark
        const quoteMark = new fabric.Text('"', {
          left: slideX + 100,
          top: 120,
          fontSize: 150,
          fontFamily: 'Georgia, serif',
          fontWeight: 'bold',
          fill: colors.secondary,
          opacity: 0.2,
          slideIndex: index
        });
        
        // Add decorative line
        const decorLine = new fabric.Rect({
          left: slideX + PAGE_WIDTH/2 - 100,
          top: 300,
          width: 200,
          height: 3,
          fill: colors.accent,
          opacity: 0.8,
          originX: 'center',
          slideIndex: index
        });
        
        // Title with elegant styling
        const title = new fabric.Textbox(titleText, {
          left: slideX + PAGE_WIDTH/2,
          top: 180,
          width: PAGE_WIDTH - 200,
          fontSize: 48,
          fontWeight: 'bold',
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          textAlign: 'center',
          originX: 'center',
          lineHeight: 1.2,
          slideIndex: index,
          shadow: new fabric.Shadow({
            color: 'rgba(0,0,0,0.2)',
            blur: 5,
            offsetX: 2,
            offsetY: 2
          })
        });
        
        // Content with proper spacing
        const content = new fabric.Textbox(contentText, {
          left: slideX + PAGE_WIDTH/2,
          top: 350,
          width: PAGE_WIDTH - 250,
          fontSize: 28,
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          opacity: 0.9,
          textAlign: 'center',
          originX: 'center',
          lineHeight: 1.4,
          slideIndex: index
        });
        
        canvas.add(decorCircle1);
        canvas.add(decorCircle2);
        canvas.add(quoteMark);
        canvas.add(decorLine);
        canvas.add(title);
        canvas.add(content);
      } 
      else if (layoutType === 1) {
        // Layout 2: Tips & Tricks Style (inspired by tips-template)
        
        // Header accent rectangle with gradient
        const headerGradient = new fabric.Gradient({
          type: 'linear',
          coords: {
            x1: 0,
            y1: 0,
            x2: PAGE_WIDTH,
            y2: 0
          },
          colorStops: [
            { offset: 0, color: colors.accent },
            { offset: 1, color: fabric.Color.fromHex(colors.accent).tint(20).toRgba() }
          ]
        });
        
        const headerRect = new fabric.Rect({
          left: slideX,
          top: 0,
          width: PAGE_WIDTH,
          height: 180,
          fill: headerGradient,
          slideIndex: index,
          selectable: false,
          evented: false
        });
        canvas.add(headerRect);
        
        // Add decorative elements
        const decorElement1 = new fabric.Circle({
          left: slideX + 80,
          top: 90,
          radius: 30,
          fill: colors.secondary,
          opacity: 0.2,
          slideIndex: index,
          selectable: false
        });
        canvas.add(decorElement1);
        
        const decorElement2 = new fabric.Circle({
          left: slideX + PAGE_WIDTH - 80,
          top: 90,
          radius: 30,
          fill: colors.secondary,
          opacity: 0.2,
          slideIndex: index,
          selectable: false
        });
        canvas.add(decorElement2);
        
        // Add stock image if available (for second slide)
        if (stockImage && index === 1) {
          fabric.Image.fromURL(stockImage, (img) => {
            // Scale image to fit nicely
            const scale = 200 / Math.max(img.width, img.height);
            img.set({
              left: slideX + 100,
              top: 400,
              scaleX: scale,
              scaleY: scale,
              opacity: 0.9,
              slideIndex: index
            });
            canvas.add(img);
            canvas.renderAll();
          }, { crossOrigin: 'anonymous' });
        }
        
        // Title on the accent background with shadow
        const title = new fabric.Textbox(titleText.toUpperCase(), {
          left: slideX + PAGE_WIDTH/2,
          top: 90,
          width: PAGE_WIDTH - 200,
          fontSize: 42,
          fontWeight: 'bold',
          fontFamily: 'Montserrat, Arial',
          fill: colors.secondary,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index,
          shadow: new fabric.Shadow({
            color: 'rgba(0,0,0,0.3)',
            blur: 3,
            offsetX: 1,
            offsetY: 1
          })
        });
        
        // Add decorative icon based on content
        let iconText = '✓';
        if (contentText.toLowerCase().includes('tip')) iconText = '💡';
        if (contentText.toLowerCase().includes('step')) iconText = '👉';
        if (contentText.toLowerCase().includes('idea')) iconText = '💭';
        if (contentText.toLowerCase().includes('warning')) iconText = '⚠️';
        if (contentText.toLowerCase().includes('important')) iconText = '⭐';
        
        const icon = new fabric.Text(iconText, {
          left: slideX + PAGE_WIDTH/2 - 150,
          top: 240,
          fontSize: 36,
          fontFamily: 'Arial',
          slideIndex: index
        });
        canvas.add(icon);
        
        // Content with bullet points preserved
        const content = new fabric.Textbox(contentText, {
          left: slideX + PAGE_WIDTH/2,
          top: 350,
          width: PAGE_WIDTH - 300,
          fontSize: 30,
          fontFamily: 'Poppins, Arial',
          fill: colors.text,
          textAlign: 'left',
          originX: 'center',
          lineHeight: 1.8,
          slideIndex: index
        });
        
        // Add bottom accent
        const bottomAccent = new fabric.Rect({
          left: slideX + PAGE_WIDTH/2 - 100,
          top: PAGE_HEIGHT - 200,
          width: 200,
          height: 4,
          fill: colors.accent,
          opacity: 0.8,
          originX: 'center',
          slideIndex: index
        });
        canvas.add(bottomAccent);
        
        canvas.add(title);
        canvas.add(content);
      }
      else if (layoutType === 2) {
        // Layout 3: Carousel Cover Style (inspired by carousel-cover)
        
        // Create a more complex background pattern
        const patternSize = 40;
        for (let x = 0; x < PAGE_WIDTH; x += patternSize * 2) {
          for (let y = 0; y < PAGE_HEIGHT; y += patternSize * 2) {
            if ((x/patternSize + y/patternSize) % 3 === 0) {
              const dot = new fabric.Circle({
                left: slideX + x,
                top: y,
                radius: 3,
                fill: colors.secondary,
                opacity: 0.1,
                selectable: false,
                evented: false,
                slideIndex: index
              });
              canvas.add(dot);
            }
          }
        }
        
        // Large decorative circle with gradient
        const circleGradient = new fabric.Gradient({
          type: 'radial',
          coords: {
            x1: 0,
            y1: 0,
            r1: 0,
            x2: 0,
            y2: 0,
            r2: 350
          },
          colorStops: [
            { offset: 0, color: fabric.Color.fromHex(colors.accent).tint(30).toRgba() },
            { offset: 1, color: colors.accent }
          ]
        });
        
        const accentCircle = new fabric.Circle({
          left: slideX + PAGE_WIDTH/2,
          top: PAGE_HEIGHT/2,
          radius: 350,
          originX: 'center',
          originY: 'center',
          fill: circleGradient,
          opacity: 0.1,
          slideIndex: index,
          selectable: false,
          evented: false
        });
        canvas.add(accentCircle);
        
        // Add stock image if available (for third slide)
        if (stockImage && index === 2) {
          fabric.Image.fromURL(stockImage, (img) => {
            // Scale image to fit nicely
            const scale = 400 / Math.max(img.width, img.height);
            img.set({
              left: slideX + PAGE_WIDTH/2,
              top: PAGE_HEIGHT - 300,
              scaleX: scale,
              scaleY: scale,
              opacity: 0.6,
              originX: 'center',
              originY: 'center',
              slideIndex: index
            });
            
            // Add a filter to blend with background
            img.filters.push(new fabric.Image.filters.BlendColor({
              color: colors.primary,
              mode: 'tint',
              alpha: 0.3
            }));
            img.applyFilters();
            
            canvas.add(img);
            canvas.renderAll();
          }, { crossOrigin: 'anonymous' });
        }
        
        // Add decorative shapes
        const decorShape1 = new fabric.Rect({
          left: slideX + 100,
          top: 100,
          width: 80,
          height: 80,
          fill: colors.accent,
          opacity: 0.2,
          angle: 45,
          slideIndex: index
        });
        canvas.add(decorShape1);
        
        const decorShape2 = new fabric.Rect({
          left: slideX + PAGE_WIDTH - 150,
          top: 150,
          width: 60,
          height: 60,
          fill: colors.accent,
          opacity: 0.2,
          angle: 30,
          slideIndex: index
        });
        canvas.add(decorShape2);
        
        // Title with two-tone styling and shadow
        const titlePart1 = new fabric.Textbox(titleText.split(' ')[0] || titleText, {
          left: slideX + PAGE_WIDTH/2,
          top: 200,
          width: PAGE_WIDTH - 200,
          fontSize: 64,
          fontWeight: 'bold',
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index,
          shadow: new fabric.Shadow({
            color: 'rgba(0,0,0,0.2)',
            blur: 5,
            offsetX: 3,
            offsetY: 3
          })
        });
        
        // Second part of title in accent color
        const remainingWords = titleText.split(' ').slice(1).join(' ');
        if (remainingWords) {
          const titlePart2 = new fabric.Textbox(remainingWords, {
            left: slideX + PAGE_WIDTH/2,
            top: 280,
            width: PAGE_WIDTH - 200,
            fontSize: 52,
            fontWeight: 'bold',
            fontFamily: 'Poppins, Arial',
            fill: colors.accent,
            opacity: 0.9,
            textAlign: 'center',
            originX: 'center',
            slideIndex: index,
            shadow: new fabric.Shadow({
              color: 'rgba(0,0,0,0.1)',
              blur: 3,
              offsetX: 2,
              offsetY: 2
            })
          });
          canvas.add(titlePart2);
        }
        
        // Add "swipe" indicator
        const swipeText = new fabric.Text('Swipe →', {
          left: slideX + PAGE_WIDTH/2,
          top: PAGE_HEIGHT - 100,
          fontSize: 24,
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          opacity: 0.7,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index
        });
        canvas.add(swipeText);
        
        // Content below with better styling
        const content = new fabric.Textbox(contentText, {
          left: slideX + PAGE_WIDTH/2,
          top: 400,
          width: PAGE_WIDTH - 250,
          fontSize: 28,
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          opacity: 0.9,
          textAlign: 'center',
          originX: 'center',
          lineHeight: 1.4,
          slideIndex: index
        });
        
        canvas.add(titlePart1);
        canvas.add(content);
      }
      else if (layoutType === 3) {
        // Layout 4: Testimonial Style (inspired by testimonial-card)
        
        // Quote mark
        const quoteMark = new fabric.Text('"', {
          left: slideX + PAGE_WIDTH/2,
          top: 120,
          fontSize: 120,
          fontFamily: 'Georgia, serif',
          fontWeight: 'bold',
          fill: colors.accent,
          opacity: 0.4,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index
        });
        canvas.add(quoteMark);
        
        // Title as a highlighted statement
        const title = new fabric.Textbox(titleText, {
          left: slideX + PAGE_WIDTH/2,
          top: 220,
          width: PAGE_WIDTH - 200,
          fontSize: 42,
          fontWeight: 'bold',
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index
        });
        
        // Content as supporting text
        const content = new fabric.Textbox(contentText, {
          left: slideX + PAGE_WIDTH/2,
          top: 400,
          width: PAGE_WIDTH - 250,
          fontSize: 28,
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          opacity: 0.9,
          textAlign: 'center',
          originX: 'center',
          lineHeight: 1.6,
          slideIndex: index
        });
        
        // Add rating stars at bottom for visual interest
        const stars = new fabric.Text('⭐⭐⭐⭐⭐', {
          left: slideX + PAGE_WIDTH/2,
          top: PAGE_HEIGHT - 200,
          fontSize: 36,
          fontFamily: 'Arial',
          fill: '#FCD34D',
          textAlign: 'center',
          originX: 'center',
          slideIndex: index
        });
        
        canvas.add(title);
        canvas.add(content);
        canvas.add(stars);
      }
      else {
        // Layout 5: Statistic Highlight (inspired by stat-highlight)
        
        // Top accent line
        const topLine = new fabric.Rect({
          left: slideX + 100,
          top: 100,
          width: PAGE_WIDTH - 200,
          height: 4,
          fill: colors.accent,
          slideIndex: index
        });
        canvas.add(topLine);
        
        // Bottom accent line
        const bottomLine = new fabric.Rect({
          left: slideX + 100,
          top: PAGE_HEIGHT - 150,
          width: PAGE_WIDTH - 200,
          height: 4,
          fill: colors.accent,
          slideIndex: index
        });
        canvas.add(bottomLine);
        
        // Title as a large highlighted number/statement
        const title = new fabric.Textbox(titleText, {
          left: slideX + PAGE_WIDTH/2,
          top: 250,
          width: PAGE_WIDTH - 200,
          fontSize: 72,
          fontWeight: 'bold',
          fontFamily: 'Poppins, Arial',
          fill: colors.accent,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index
        });
        
        // Content as explanation
        const content = new fabric.Textbox(contentText, {
          left: slideX + PAGE_WIDTH/2,
          top: 400,
          width: PAGE_WIDTH - 300,
          fontSize: 28,
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          textAlign: 'center',
          originX: 'center',
          lineHeight: 1.4,
          slideIndex: index
        });
        
        canvas.add(title);
        canvas.add(content);
      }
      
      // Add CTA text for the last slide with better styling
      if (index === slides.length - 1) {
        // Add professional CTA section with gradient background
        const ctaGradient = new fabric.Gradient({
          type: 'linear',
          coords: {
            x1: 0,
            y1: 0,
            x2: PAGE_WIDTH - 200,
            y2: 0
          },
          colorStops: [
            { offset: 0, color: colors.accent },
            { offset: 1, color: fabric.Color.fromHex(colors.accent).tint(20).toRgba() }
          ]
        });
        
        // Main CTA box with gradient
        const ctaBox = new fabric.Rect({
          left: slideX + 100,
          top: PAGE_HEIGHT - 180,
          width: PAGE_WIDTH - 200,
          height: 120,
          rx: 15,
          ry: 15,
          fill: ctaGradient,
          opacity: 0.95,
          shadow: new fabric.Shadow({
            color: 'rgba(0,0,0,0.2)',
            blur: 10,
            offsetX: 0,
            offsetY: 5
          }),
          slideIndex: index
        });
        canvas.add(ctaBox);
        
        // Add decorative elements to CTA box
        const ctaDecor1 = new fabric.Circle({
          left: slideX + 130,
          top: PAGE_HEIGHT - 160,
          radius: 8,
          fill: colors.secondary,
          opacity: 0.5,
          slideIndex: index
        });
        canvas.add(ctaDecor1);
        
        const ctaDecor2 = new fabric.Circle({
          left: slideX + PAGE_WIDTH - 130,
          top: PAGE_HEIGHT - 100,
          radius: 12,
          fill: colors.secondary,
          opacity: 0.3,
          slideIndex: index
        });
        canvas.add(ctaDecor2);
        
        // "Thank you" text
        const thankYou = new fabric.Text('Thank you for reading!', {
          left: slideX + PAGE_WIDTH/2,
          top: PAGE_HEIGHT - 160,
          fontSize: 20,
          fontFamily: 'Poppins, Arial',
          fontWeight: '500',
          fill: colors.secondary,
          opacity: 0.9,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index
        });
        canvas.add(thankYou);
        
        // Main CTA with emoji and bold text
        const cta = new fabric.Textbox('👉 Follow for more insights!', {
          left: slideX + PAGE_WIDTH/2,
          top: PAGE_HEIGHT - 120,
          width: PAGE_WIDTH - 250,
          fontSize: 32,
          fontWeight: 'bold',
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index,
          shadow: new fabric.Shadow({
            color: 'rgba(0,0,0,0.15)',
            blur: 2,
            offsetX: 1,
            offsetY: 1
          })
        });
        canvas.add(cta);
        
        // Add social media handles or website
        const socialHandle = new fabric.Text('@YourHandle', {
          left: slideX + PAGE_WIDTH/2,
          top: PAGE_HEIGHT - 80,
          fontSize: 18,
          fontFamily: 'Poppins, Arial',
          fill: colors.secondary,
          opacity: 0.8,
          textAlign: 'center',
          originX: 'center',
          slideIndex: index
        });
        canvas.add(socialHandle);
      }
      
      // Add page separator lines with better styling
      if (index > 0) {
        const separator = new fabric.Line([
          slideX, 0, 
          slideX, PAGE_HEIGHT
        ], {
          stroke: colors.secondary,
          opacity: 0.3,
          strokeDashArray: [8, 8],
          selectable: false,
          evented: false
        });
        canvas.add(separator);
      }
    });
    
    // Save state and render
    canvas.renderAll();
    saveState();
  }, [pageCount, saveState, background, PAGE_WIDTH, PAGE_HEIGHT]);

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: 'background.default',
      overflow: 'hidden',
    }}>
      {/* Load all required fonts */}
      <FontLoader />
      
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8fafc',
        overflow: 'hidden'
      }}>
        {/* Enhanced Upper Toolbar */}
        <CarouselToolbar
          documentName={documentName}
          setDocumentName={setDocumentName}
          canvasSize={canvasSize}
          handleCanvasSizeChange={handleCanvasSizeChange}
          historyIndex={historyIndex}
          history={history}
          undo={undo}
          redo={redo}
          selectedObject={selectedObject}
          fabricCanvasRef={fabricCanvasRef}
          duplicateSelected={duplicateSelected}
          groupSelected={groupSelected}
          ungroupSelected={ungroupSelected}
          deleteSelected={deleteSelected}
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          zoom={zoom}
          setZoom={setZoom}
          zoomLocked={zoomLocked}
          toggleZoomLock={toggleZoomLock}
          downloadImage={downloadImage}
          exportToPDF={exportToPDF}
          handleBackToTemplates={handleBackToTemplates}
          setShowShortcuts={setShowShortcuts}
          onSaveTemplate={() => setShowSaveDialog(true)}
          setAiMenuAnchorEl={setAiMenuAnchorEl}
        />
        
        <Box sx={{ 
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#f8fafc',
        }}>
          {/* Left Sidebar Component */}
          <CarouselLeftSidebar
            activeSection={activeSection}
            handleSectionClick={handleSectionClick}
            addHeading={addHeading}
            addParagraph={addParagraph}
            addImage={addImage}
            addProfilePlaceholder={addProfilePlaceholder}
            addLogoPlaceholder={addLogoPlaceholder}
            addGroupedElement={addGroupedElement}
            addShape={addShape}
            addEmoji={addEmoji}
            activeElementTab={activeElementTab}
            setActiveElementTab={setActiveElementTab}
            setShowPexels={setShowPexels}
            removeBackground={removeBackground}
            removeBackgroundAI={removeBackgroundAI}
            isRemovingBackground={isRemovingBackground}
            activeBackgroundTab={activeBackgroundTab}
            setActiveBackgroundTab={setActiveBackgroundTab}
            background={background}
            setBackground={setBackground}
            fabricCanvasRef={fabricCanvasRef}
            applyGradient={applyGradient}
            selectedBrandAccent={selectedBrandAccent}
            applyBrandAccent={applyBrandAccent}
            canvasObjects={canvasObjects}
            setSelectedObject={setSelectedObject}
            updateObjectsList={updateObjectsList}
            toggleObjectLock={toggleObjectLock}
            toggleObjectVisibility={toggleObjectVisibility}
            currentPageIndex={getCurrentPageIndex()}
            pages={Array.from({length: pageCount}, (_, i) => ({ id: i, name: `Slide ${i + 1}` }))}
            setSnackbar={setSnackbar}
            canvasSize={canvasSize}
          />

          {/* Canvas Area - Centered with Auto-Zoom */}
          <Box sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#f1f5f9',
            backgroundImage: showGrid ? 'radial-gradient(circle, #e2e8f0 1px, transparent 1px)' : 'none',
            backgroundSize: showGrid ? '20px 20px' : 'auto',
            overflow: 'auto', // Allow horizontal scrolling
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 0, // Allow flex shrinking
          }}>
            


            {/* Canvas Container */}
            <Box sx={{
              flex: 1,
              width: '100%',
              overflow: 'auto', // Main scrollable container
              px: 4, // Horizontal padding for scroll margins
              py: 2, // Vertical padding
              position: 'relative', // Enable positioning for floating buttons
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100%',
                minWidth: `${currentCanvasWidth * zoom + 400}px`, // Ensure container is wider for scroll
                position: 'relative', // Enable positioning context
              }}>
                <Box
                  sx={{
                    transform: `scale(${zoom})`,
                    transformOrigin: 'center center',
                boxShadow: '0 10px 25px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                    overflow: 'visible',
                    minWidth: `${currentCanvasWidth}px`,
                    width: 'fit-content',
                    mx: 'auto', // Center horizontally
                position: 'relative', // Enable positioning for floating buttons
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  display: 'block',
                      maxWidth: 'none',
                      width: 'auto',
                      height: 'auto'
                }}
              />
                </Box>
                
                {/* Floating Page Action Buttons - positioned relative to canvas */}
                {Array.from({ length: pageCount }, (_, i) => {
                  const pageNum = i + 1;
                  const scaledPageWidth = PAGE_WIDTH * zoom;
                  const pageStartX = i * scaledPageWidth;
                  const pageCenterX = pageStartX + (scaledPageWidth / 2);
                  const canvasHalfWidth = (currentCanvasWidth * zoom / 2);
                  const leftPosition = `calc(50% + ${pageCenterX - canvasHalfWidth - 110}px)`;
                  
                  // Calculate position above canvas (canvas is vertically centered)
                  const scaledCanvasHeight = PAGE_HEIGHT * zoom;
                  const topPosition = `calc(50% - ${scaledCanvasHeight / 2}px - 50px)`;
                  
                  return (
                    <Box
                      key={`page-actions-${pageNum}`}
                      sx={{
                        position: 'absolute',
                        top: topPosition,
                        left: leftPosition,
                        display: 'flex',
                        gap: 1,
                        zIndex: 1000,
                        pointerEvents: 'all'
                      }}
                    >
                      {/* Page Label */}
                      <Chip
                        label={`Page ${pageNum}`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                          color: '#374151',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      />
                      
                      {/* Add Page Button */}
                      <Tooltip title="Add new page" arrow>
                        <IconButton
                          size="small"
                          onClick={addNewPage}
                          sx={{
                            bgcolor: 'rgba(72, 144, 254, 0.95)',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(72, 144, 254, 0.3)',
                            backdropFilter: 'blur(8px)',
                            '&:hover': {
                              bgcolor: 'rgba(72, 144, 254, 1)',
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            width: 32,
                            height: 32
                          }}
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      
                      {/* Duplicate Page Button */}
                      <Tooltip title="Duplicate this page" arrow>
                        <IconButton
                          size="small"
                          onClick={() => duplicatePage(pageNum)}
                          sx={{
                            bgcolor: 'rgba(16, 185, 129, 0.95)',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                            backdropFilter: 'blur(8px)',
                            '&:hover': {
                              bgcolor: 'rgba(16, 185, 129, 1)',
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            width: 32,
                            height: 32
                          }}
                        >
                          <DuplicateIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      
                      {/* Delete Page Button - only show if more than 1 page */}
                      {pageCount > 1 && (
                        <Tooltip title="Delete this page" arrow>
                          <IconButton
                            size="small"
                            onClick={() => deletePage(pageNum)}
                            sx={{
                              bgcolor: 'rgba(239, 68, 68, 0.95)',
                              color: 'white',
                              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                              backdropFilter: 'blur(8px)',
                              '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 1)',
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.2s ease-in-out',
                              width: 32,
                              height: 32
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Right Properties Panel Component */}
          {selectedObject && (
            <CarouselRightProperties
              selectedObject={selectedObject}
              fabricCanvasRef={fabricCanvasRef}
              setSelectedObject={setSelectedObject}
              handleImageUpload={handleImageUpload}
              updateHistory={updateHistory}
              removeBackground={removeBackground}
              removeBackgroundAI={removeBackgroundAI}
              isRemovingBackground={isRemovingBackground}
              setSnackbar={setSnackbar}
            />
          )}
        </Box>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Keyboard Shortcuts Dialog */}
        <Dialog
          open={showShortcuts}
          onClose={() => setShowShortcuts(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            pb: 1
          }}>
            <KeyboardIcon sx={{ color: '#4890fe' }} />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogContent>
            <Box sx={{ py: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                Edit Actions
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Undo</Typography>
                    <Chip label="Ctrl+Z" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Redo</Typography>
                    <Chip label="Ctrl+Y" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Duplicate</Typography>
                    <Chip label="Ctrl+D" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Group</Typography>
                    <Chip label="Ctrl+G" size="small" variant="outlined" />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Delete</Typography>
                    <Chip label="Del" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Deselect</Typography>
                    <Chip label="Esc" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Select All</Typography>
                    <Chip label="Ctrl+A" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Ungroup</Typography>
                    <Chip label="Ctrl+Shift+G" size="small" variant="outlined" />
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                Quick Tools
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Add Text</Typography>
                    <Chip label="T" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Add Rectangle</Typography>
                    <Chip label="R" size="small" variant="outlined" />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Add Circle</Typography>
                    <Chip label="C" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Export PNG</Typography>
                    <Chip label="Ctrl+S" size="small" variant="outlined" />
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                Help
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Show Shortcuts</Typography>
                <Chip label="?" size="small" variant="outlined" />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setShowShortcuts(false)}
              sx={{ textTransform: 'none' }}
            >
              Got it!
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* AI Design Dropdown Menu */}
        <Menu
          anchorEl={aiMenuAnchorEl}
          open={Boolean(aiMenuAnchorEl)}
          onClose={() => !isGeneratingDesign && !isGeneratingContent && setAiMenuAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 2,
              width: 450,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
        >
          <Box sx={{ mb: 2 }}>
            {/* Tabs for Design and Content */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={aiMenuTab} 
                onChange={(e, newValue) => setAiMenuTab(newValue)}
                variant="fullWidth"
              >
                <Tab 
                  label="Design" 
                  value="design" 
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: aiMenuTab === 'design' ? 600 : 400
                  }} 
                />
                <Tab 
                  label="Content" 
                  value="content"
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: aiMenuTab === 'content' ? 600 : 400
                  }}  
                />
              </Tabs>
            </Box>
            
            {/* Design Tab Content */}
            {aiMenuTab === 'design' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AutoFixHigh sx={{ color: '#4890fe', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    AI Design Generator
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                  Describe the design you want to create. Include details about style, colors, content, and layout.
                </Typography>
                
                        <TextField
          label="Design Prompt"
          placeholder="E.g., Create a professional carousel about digital marketing trends with blue color scheme and modern style"
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={designPrompt}
          onChange={(e) => setDesignPrompt(e.target.value)}
          disabled={isGeneratingDesign}
          size="small"
          sx={{ mb: 2 }}
        />
        
        <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
          The AI will automatically determine the optimal number of slides based on your design prompt.
        </Typography>
        
        {isGeneratingDesign ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                    <CircularProgress size={32} sx={{ mb: 2, color: '#4890fe' }} />
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Generating your design...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button 
                      onClick={() => setAiMenuAnchorEl(null)}
                      disabled={isGeneratingDesign}
                      sx={{ 
                        textTransform: 'none',
                        color: '#64748b'
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleGenerateAIDesign}
                      disabled={isGeneratingDesign || !designPrompt.trim()}
                      variant="contained"
                      startIcon={<AutoFixHigh />}
                      sx={{ 
                        textTransform: 'none',
                        bgcolor: '#4890fe',
                        '&:hover': {
                          bgcolor: '#3b82f6'
                        }
                      }}
                    >
                      Generate Design
                    </Button>
                  </Box>
                )}
              </>
            )}
            
            {/* Content Tab */}
            {aiMenuTab === 'content' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AutoFixHigh sx={{ color: '#8b5cf6', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    AI Content Generator
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                  Generate professional carousel content with engaging titles, body text, and CTAs.
                </Typography>
                
                {/* Content Mode Selection */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Generation Mode
                  </Typography>
                  <ButtonGroup variant="outlined" fullWidth>
                    <Button 
                      onClick={() => setContentMode('topic')}
                      variant={contentMode === 'topic' ? 'contained' : 'outlined'}
                      sx={{ 
                        textTransform: 'none',
                        bgcolor: contentMode === 'topic' ? '#8b5cf6' : 'transparent',
                        color: contentMode === 'topic' ? 'white' : '#8b5cf6',
                        borderColor: '#8b5cf6',
                        '&:hover': {
                          bgcolor: contentMode === 'topic' ? '#7c3aed' : 'rgba(139, 92, 246, 0.04)',
                          borderColor: '#7c3aed'
                        }
                      }}
                    >
                      Topic
                    </Button>
                    <Button 
                      onClick={() => setContentMode('text')}
                      variant={contentMode === 'text' ? 'contained' : 'outlined'}
                      sx={{ 
                        textTransform: 'none',
                        bgcolor: contentMode === 'text' ? '#8b5cf6' : 'transparent',
                        color: contentMode === 'text' ? 'white' : '#8b5cf6',
                        borderColor: '#8b5cf6',
                        '&:hover': {
                          bgcolor: contentMode === 'text' ? '#7c3aed' : 'rgba(139, 92, 246, 0.04)',
                          borderColor: '#7c3aed'
                        }
                      }}
                    >
                      Existing Text
                    </Button>
                  </ButtonGroup>
                </Box>
                
                <TextField
                  label={contentMode === 'topic' ? "Topic" : "Text Content"}
                  placeholder={
                    contentMode === 'topic' 
                      ? "E.g., 5 ways to improve your LinkedIn engagement" 
                      : "Paste your existing text here to convert into carousel format"
                  }
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={contentPrompt}
                  onChange={(e) => setContentPrompt(e.target.value)}
                  disabled={isGeneratingContent}
                  size="small"
                  sx={{ mb: 2 }}
                />
                
                {isGeneratingContent ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                    <CircularProgress size={32} sx={{ mb: 2, color: '#8b5cf6' }} />
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Generating your content...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button 
                      onClick={() => setAiMenuAnchorEl(null)}
                      disabled={isGeneratingContent}
                      sx={{ 
                        textTransform: 'none',
                        color: '#64748b'
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleGenerateContent}
                      disabled={isGeneratingContent || !contentPrompt.trim()}
                      variant="contained"
                      startIcon={<AutoFixHigh />}
                      sx={{ 
                        textTransform: 'none',
                        bgcolor: '#8b5cf6',
                        '&:hover': {
                          bgcolor: '#7c3aed'
                        }
                      }}
                    >
                      Generate Content
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Menu>

        {/* Pexels Search Dialog */}
        <PexelsSearch
          open={showPexels}
          onClose={() => setShowPexels(false)}
          onSelectImage={handlePexelsImage}
        />
        
        {/* Context Menu */}
        <Menu
          open={Boolean(contextMenu)}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu
              ? { top: contextMenu.y, left: contextMenu.x }
              : undefined
          }
        >
          <MenuItem 
            onClick={() => handleContextMenuAction('duplicate')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <DuplicateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('delete')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
          
          <Divider />
          
          <MenuItem 
            onClick={() => handleContextMenuAction('copy')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <CopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('paste')}
            disabled={!copiedObject}
          >
            <ListItemIcon>
              <PasteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Paste</ListItemText>
          </MenuItem>
          
          <Divider />
          
          {/* Alignment options in nested menu */}
          <MenuItem 
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <AlignHorizontalCenter fontSize="small" />
            </ListItemIcon>
            <ListItemText>Align</ListItemText>
            <ArrowForwardIcon fontSize="small" sx={{ ml: 1 }} />
            
            <Menu
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(selectedObject)}
              onClose={handleCloseContextMenu}
              sx={{ pointerEvents: 'none' }}
            >
              <MenuItem 
                onClick={() => handleContextMenuAction('alignLeft')}
                sx={{ pointerEvents: 'auto' }}
              >
                <ListItemIcon>
                  <AlignHorizontalLeft fontSize="small" />
                </ListItemIcon>
                <ListItemText>Align Left</ListItemText>
              </MenuItem>
              
              <MenuItem 
                onClick={() => handleContextMenuAction('alignCenter')}
                sx={{ pointerEvents: 'auto' }}
              >
                <ListItemIcon>
                  <AlignHorizontalCenter fontSize="small" />
                </ListItemIcon>
                <ListItemText>Align Center</ListItemText>
              </MenuItem>
              
              <MenuItem 
                onClick={() => handleContextMenuAction('alignRight')}
                sx={{ pointerEvents: 'auto' }}
              >
                <ListItemIcon>
                  <AlignHorizontalRight fontSize="small" />
                </ListItemIcon>
                <ListItemText>Align Right</ListItemText>
              </MenuItem>
              
              <Divider />
              
              <MenuItem 
                onClick={() => handleContextMenuAction('alignTop')}
                sx={{ pointerEvents: 'auto' }}
              >
                <ListItemIcon>
                  <AlignVerticalTop fontSize="small" />
                </ListItemIcon>
                <ListItemText>Align Top</ListItemText>
              </MenuItem>
              
              <MenuItem 
                onClick={() => handleContextMenuAction('alignMiddle')}
                sx={{ pointerEvents: 'auto' }}
              >
                <ListItemIcon>
                  <AlignVerticalCenter fontSize="small" />
                </ListItemIcon>
                <ListItemText>Align Middle</ListItemText>
              </MenuItem>
              
              <MenuItem 
                onClick={() => handleContextMenuAction('alignBottom')}
                sx={{ pointerEvents: 'auto' }}
              >
                <ListItemIcon>
                  <AlignVerticalBottom fontSize="small" />
                </ListItemIcon>
                <ListItemText>Align Bottom</ListItemText>
              </MenuItem>
            </Menu>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('bringToFront')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <BringToFrontIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bring to Front</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('sendToBack')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <SendToBackIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send to Back</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('bringForward')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <BringForwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bring Forward</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('sendBackward')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <SendBackwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send Backward</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('copy')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <CopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('paste')}
            disabled={!copiedObject}
          >
            <ListItemIcon>
              <PasteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Paste</ListItemText>
          </MenuItem>
          
          <Divider />
          
          <MenuItem 
            onClick={() => handleContextMenuAction('lock')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Lock</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('unlock')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <UnlockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Unlock</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={() => handleContextMenuAction('toggleVisibility')}
            disabled={!selectedObject}
          >
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>Toggle Visibility</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default FabricCarouselDesigner;